/* OVERGUARD — the shared behaviour layer.

   This file began as a copy of reference/assets/js/main.js, which
   reference/build/partials/scripts.html generates. It is no longer that copy:
   the reference is a static site and this is a theme, and the two have diverged
   on the branch that built the home page. Expect these categories of difference,
   and do not treat the reference version as authoritative for any of them:

     - a config round-trip. An asset cannot contain a Liquid tag, so everything
       the behaviour layer needs from the store arrives as inert JSON in
       #og-config and is read once into CFG. Thresholds, currency marks, routes
       and the colourway palette all come from there rather than from literals.
     - destination and palette helpers. The reference linked flat .html files and
       wrote hex values inline; productUrl, collectionUrl and swatchHex resolve
       those against the store's real routes, the shop's own colour list and
       the theme's settings — swatchHex's own comment carries which tier wins.
     - a binding helper and chrome guards. Layouts render different chrome and
       password.liquid renders none, so bindings go through on() and the
       overlay entry points return early rather than throwing — this file is one
       IIFE, and an unguarded dereference used to take everything below it down.
     - section mount and unmount. The theme editor re-renders one section at a
       time, so anything bound per element has to be re-established and anything
       retained has to be released.

   docs/shopify-mapping.md records which setting feeds which of these.

   One difference is about the reference build rather than about the theme. The
   reference's built main.js opens with the pre-paint language script, because
   export_site.py sweeps every <script> out of the assembled document and appends
   it here. That defeats the script's entire purpose: it has to run before first
   paint, and this file loads at the foot of the body. It lives inline in
   layout/theme.liquid <head> instead, which is where the partial always put it. */

(function(){
  var d=document, root=d.documentElement;
  var touch = !!(window.matchMedia && window.matchMedia('(hover: none)').matches);

  /* ---- config ----
     Everything this file needs from Liquid. An asset cannot contain a Liquid
     tag, so snippets/og-config.liquid renders it as inert JSON and we read it
     here. Defaults mirror the schema, so a missing or malformed blob degrades
     to the old hardcoded behaviour instead of throwing -- and a throw in this
     file takes the language toggle and the menu down with it. */
  var CFG = (function(){
    var base = {
      routes:{root:'/', all:'/collections/all', collections:'/collections',
              search:'/search', cart:'/cart', cartAdd:'/cart/add',
              cartChange:'/cart/change', predictive:'/search/suggest'},
      palette:{},
      money:{cents:100, en:'EGP', ar:'\u062c.\u0645', locale:'en-US'},
      cart:{freeOver:2500, delivery:70},
      swatchFallback:'',
      soldout:{en:'Sold out', ar:'\u0646\u0641\u062f\u062a \u0627\u0644\u0643\u0645\u064a\u0629'},
      stock:{low:2, productLow:3, cap:99},
      help:{sizeGuide:null, returns:null},
      template:'', designMode:false
    };
    try{
      var n = d.getElementById('og-config');
      if(!n) return base;
      var j = JSON.parse(n.textContent) || {};
      for(var k in j){ if(j[k] && typeof j[k]==='object' && base[k]){
        for(var k2 in j[k]) base[k][k2] = j[k][k2];
      } else if(j[k]!=null){ base[k] = j[k]; } }
      return base;
    }catch(e){ return base; }
  })();

  /* ---- stock wording ----
     One state, one pair of words, written once in Settings. card.liquid renders
     them server side for every card Liquid builds; this is how the ones this
     file builds -- the collection grid, the quick view -- say the same thing.
     The literals stay as the fallback for a page whose blob failed to parse. */
  function soldOut(){
    var s = CFG.soldout || {};
    return [s.en || 'Sold out', s.ar || '\u0646\u0641\u062f\u062a \u0627\u0644\u0643\u0645\u064a\u0629'];
  }

  /* ---- destinations ----
     The reference linked flat .html files and built its own query strings. Those
     paths 404 on a storefront, and three of them were being written over good
     Liquid hrefs at runtime: the category CTA at boot, a card's link on every
     swatch click, and the quick view's way out. Everything routes through here
     now, and a catalogue row's own url wins when it has one. */
  function productUrl(sku, colour, fallback){
    var row = null, i;
    /* The fallback matters more than it looks. This is called to OVERWRITE an
       href Liquid already wrote correctly, so a sku the catalogue does not know
       would silently downgrade a real product URL to the catalogue page. Callers
       that are replacing something pass what they are replacing. */
    /* Both lists, the way productFor() reads them. A line whose product is
       outside the catalogue's fifty-row window, or was unpublished while it sat
       in a bag, is an ADOPTED row -- and og-cart.liquid published that line's
       real url precisely so the row it renders can still be clicked. Searching
       PRODUCTS alone sent every one of them to the catalogue index. */
    var lists = [PRODUCTS, ADOPTED], li, L;
    for(li=0; li<lists.length && !row; li++){
      L = lists[li] || [];
      for(i=0;i<L.length;i++){
        if(L[i].sku===sku && (!colour || L[i].colour===colour)){ row=L[i]; break; }
      }
    }
    if(row && row.url) return row.url;
    return fallback || CFG.routes.all;
  }
  /* The rail's chips resolve their colour in Liquid; every chip the script
     builds resolves it the same way through here, so one colourway cannot
     show two colours on one page. Three tiers, most specific first:

       1. the hex the catalogue row shipped with -- the swatch on the entry in
          the shop's colour list this product resolved to, whether the merchant
          picked it or its name matched, and the only tier they can extend
          without a developer. og-catalog.liquid does that resolution once for
          every row, so every row of one colourway carries the same hex and this
          tier cannot disagree with itself;
       2. the Colourway swatches theme setting of the same name, which is where
          the palette lived before the colour list existed and still covers a
          colourway with no entry;
       3. the "Unknown colour" setting.

     THE FIRST TWO USED TO BE THE OTHER WAY ROUND, and swapping them is the
     point. Seeded from the palette, all fourteen agree, so the new order is
     inert today -- measured against the store, not assumed. It stops being inert
     the moment someone edits a colour, and then the tier that must win is the
     list we tell them to edit, not the theme setting we tell them to ignore.

     THE ACCEPTED COST IS EVERY HARDCODED HEX IN THIS FILE, and there are two
     sources of them, not one. The `fallback` argument wins now, so wherever a
     caller passes a reference literal rather than a catalogue row, that literal
     outranks a palette the merchant has customised:

       - the invented PRODUCTS array, reached only when the blob is malformed.
         A fallback inside a fallback, on a page that has already lost its
         catalogue;
       - the product page's own PRODUCT.colours, which is NOT a fallback. Its
         hexes are unconditional reference literals and buildColours() hands
         them straight to this function, so on that page a merchant who edits
         colour_blood would see #5E1D1F and not their own value.

     The second is unreachable today -- templates/product.liquid is the stub, so
     #pdFrames is absent and that IIFE returns before it builds anything. It
     stops being unreachable the day main-product.liquid lands, and the fix then
     is to feed PRODUCT from the catalogue rather than to unpick this order:
     a row that carries a hex is a row whose colourway is in the merchant's own
     list, which is the tier that must win. */

  /* ---- the product card, shared -----------------------------------
     Hoisted out of the collection IIFE. It lived beside the grid, and that
     IIFE returns at its fourth statement on any page without #grid -- so on
     the home, product, cart and search pages card() was declared and
     immediately collected. The drawer opens on all of those, so a cross-sell
     that wanted the real card could not reach it. Exporting from there cannot
     work either: the export statement never runs. A second copy is how
     CATALOG and PRODUCTS drifted, and every symptom of that was silent.
     So it lives here, and the collection aliases it off the OG surface.

     stockLine takes the selected sizes as an ARGUMENT now. It used to read
     the collection's live filter state directly, which has no meaning on any
     other page; the grid passes S.sizes, everyone else passes nothing. */
  function T(en,ar){ return isAr() ? ar : en; }
  /* Shopify's own answer where there is one. Deriving it from the size map
     alone marks the sizeless product permanently sold out, because it has no
     sizes to have stock in. The fixture catalogue carries no avail, so the
     size-map reading stays as the fallback. */
  function inStock(p){
    if(typeof p.avail === 'boolean') return p.avail;
    return ALL_SIZES.some(function(s){ return p.stock[s] > 0; });
  }
  function totalStock(p){
    var t = 0, k;
    for(k in (p.stock || {})) t += (p.stock[k] | 0);
    /* The sizeless product keeps its quantity in stock1, because an empty-string
       key in the map would put a blank chip in the size facet. Summing the map
       alone answered 0 for it, so the card read "Low stock" at any quantity --
       the same omission inStock() above was written to avoid. Only a catalogue
       row carries a sizes array; the invented rows below answer unchanged. */
    if(p.sizes && !p.sizes.length) t += p.stock1 | 0;
    return t;
  }

  /* ---- the source: a collection, or a query --------------------- */
  function stockLine(p, picked){
    var sel = picked || [];
    if(sel.length){
      /* Counted across the sizes actually asked for, and named, because
         "Only 2 left" is a different promise from "only 2 left in a size you
         can wear". With several selected the label lists them rather than
         inventing a word for the set. */
      var label = sel.join(', ');
      var n = sel.reduce(function(a,z){ return a + (p.stock[z] || 0); }, 0);
      if(!n)      return ['gone', label + ' ' + T('sold out','نفد')];
      if(n <= CFG.stock.low)  return ['low',  T('Only','باقي') + ' ' + n + ' ' + T('left in','مقاس') + ' ' + label];
      return ['', label + ' ' + T('in stock','متوفر')];
    }
    var out = soldOut();
    if(!inStock(p))       return ['gone', T(out[0], out[1])];
    if(totalStock(p) <= 6)return ['low',  T('Low stock','كمية محدودة')];
    return ['',''];
  }
  /* ---- a card and its colours --------------------------------------
     A garment is every row sharing a sku. A LISTING card is one row: since
     16 Oct 2026 the collection grid, the search page and the search overlay
     draw every colour as a card of its own, with no swatch row -- the colours
     already stand side by side, so a row of dots under each would only repeat
     them. The cards that still stand for a whole garment -- the home band, the
     home rail, "Also in the drop" and the drawer's cross-sell -- open on one
     colourway (the LEAD) and their swatches switch it in place. pcSiblings()
     is that swatch row, one per colour: two products that claim one colour (a
     duplicated product nobody re-coloured -- the store had four on 4 Oct 2026)
     show one swatch, not two identical ones, and the second stays reachable
     from its own page. reference/build/partials/scripts.html carries the same
     functions under the same names. */
  /* Sorted by the merchant's colour order, like every other list of
     colourways: catalogFrom() does it for the quick view and the product page,
     and a card that ordered its swatches differently from the sheet that opens
     off it would read as two lists. Stable and Infinity-for-unset, same rule. */
  function pcSiblings(sku){
    var seen = Object.create(null);
    return PRODUCTS.filter(function(x){ return x.sku === sku; }).sort(function(a, b){
      return ((typeof a.sort === 'number') ? a.sort : Infinity)
           - ((typeof b.sort === 'number') ? b.sort : Infinity);
    }).filter(function(x){
      if(seen[x.colour]) return false;
      seen[x.colour] = 1; return true;
    });
  }
  function pcRow(sku, colour){
    var hit = null;
    PRODUCTS.some(function(x){ if(x.sku === sku && x.colour === colour){ hit = x; return true; } });
    return hit;
  }
  /* A colour with no name of its own takes the garment's, card.liquid's rule
     for the same chip. Names and ids are a merchant's, so they are escaped. */
  function pcSwatches(p, sibs){
    if(sibs.length < 2) return '';
    return '<div class="pc-swatch" role="group" aria-label="' + esc(T('Colour','اللون')) +
      '" data-al-en="Colour" data-al-ar="اللون">' +
      sibs.map(function(x){
        var en = x.cEn || x.en, ar = x.cAr || x.ar || en;
        return '<button type="button" data-colour="' + esc(x.colour) + '" style="--c:' +
          esc(swatchHex(x.colour, x.hex)) + '" aria-pressed="' + (x.colour === p.colour) +
          '" aria-label="' + esc(T(en, ar)) + '" data-al-en="' + esc(en) + '" data-al-ar="' + esc(ar) + '"></button>';
      }).join('') + '</div>';
  }
  /* Shopify answers image_url's width as a query parameter, so one URL is
     every size. EVERY PHOTOGRAPH CARRIES A srcset FROM THIS LADDER -- the rule
     since 5 Oct 2026, when an audit found every card requested at one fixed
     width (520, 680) and a retina screen handed half the pixels its slot
     needs. snippets/srcset.liquid is the same ladder on the server.
     The CDN never upscales: a rung above the source returns the source, so the
     ladder is not filtered here, and theme-hd.spec.js names any upload smaller
     than its slot for the client to replace. Anything not shaped like a CDN URL
     -- the parity fixtures' shipped files -- gets no srcset, so those lanes are
     untouched by construction. */
  var LADDER = [360, 540, 720, 900, 1080, 1440, 1800];
  var LADDER_WIDE = LADDER.concat([2400, 3000]);
  /* The slots' real widths, so the browser picks the smallest sharp file.
     .col-grid is 2 / 3 / 4 columns; the drawer's cross-sell is two ~150px
     cards; the quick view's frame is a 430px column beside the details. */
  var SZ_GRID = '(min-width:1200px) 24vw, (min-width:820px) 31vw, 46vw';
  var SZ_DRAWER = '160px';
  var SZ_QUICK = '(min-width:820px) 430px, 100vw';
  function cdnWidth(url, w){
    return /[?&]width=\d+/.test(url || '') ? url.replace(/([?&]width=)\d+/, '$1' + w) : '';
  }
  function cdnSet(url, ladder){
    if(!cdnWidth(url, 1)) return '';
    return (ladder || LADDER).map(function(w){ return cdnWidth(url, w) + ' ' + w + 'w'; }).join(', ');
  }
  /* The two attributes, as a string for markup built as HTML. Where an <img>
     is set property by property instead, sizes and srcset go BEFORE src: set
     src first and the browser starts that fetch before it learns of the set. */
  function imgSet(url, sizes, ladder){
    var set = cdnSet(url, ladder);
    return set ? ' sizes="' + esc(sizes) + '" srcset="' + esc(set) + '"' : '';
  }
  /* swatches:false is a listing card -- one colour, no row of the others. */
  function card(p, i, opt){
    var o = opt || {};
    var a = d.createElement('article');
    a.className = 'pc ch line' + (inStock(p) ? '' : ' out');
    a.style.setProperty('--i', i % 12);
    a.dataset.sku = p.sku; a.dataset.colour = p.colour;
    a.pcSizes = o.sizes || [];
    var sl = stockLine(p, o.sizes);
    var siblings = o.swatches === false ? [] : pcSiblings(p.sku);
    a.innerHTML =
      '<div class="pc-media">' +
        '<img' + imgSet(p.img, o.imgSizes || SZ_GRID) + ' src="' + p.img + '" alt="" loading="lazy">' +
        (o.add === false ? '' :
        '<button class="pc-add ch-sm" aria-label="' + T('Quick view','عرض سريع') + '">' +
          '<svg class="ic" aria-hidden="true"><use href="#ph-plus"/></svg>' +
          '<span class="pc-add-label">' + T('Quick view','عرض سريع') + '</span></button>') +
      '</div>' +
      '<div class="pc-body">' +
        /* the row's own page first, as pcPick links: productUrl() answers the
           FIRST product of a sku and colour, so of two products claiming one
           colour the second's card would open the first's page */
        '<h3><a class="card-link" href="' + esc(p.url || productUrl(p.sku, p.colour)) + '"></a></h3>' +
        '<p></p>' +
        '<span class="price">' + (p.was ? '<s>' + money(p.was) + '</s>' : '') + money(p.price) +
          ' <em style="font-style:normal;color:var(--dust)">' + CFG.money.en + '</em></span>' +
        pcSwatches(p, siblings) +
        (sl[1] ? '<p class="pc-stock ' + sl[0] + '"></p>' : '') +
      '</div>';
    /* The title, the colourway and the stock line are a merchant's words and a
       merchant's size names. They go in as text, after the block of markup they
       sit in -- the same rule editorial() follows. */
    a.querySelector('.card-link').textContent = T(p.en,p.ar);
    a.querySelectorAll('.pc-body > p')[0].textContent = T(p.cEn,p.cAr);
    if(sl[1]) a.querySelector('.pc-stock').textContent = sl[1];
    if(o.swipe === false) a.ogNoSwipe = true;
    else swipeSync(a);
    return a;
  }

  function swatchHex(id, fallback){
    var pal = CFG.palette || {};
    return fallback || (id && pal[id]) || CFG.swatchFallback;
  }

  /* ---- picking a colour on a .pc -----------------------------------
     The card becomes that colourway where it stands: photo, colour line,
     price, sold-out state, stock line and link. It works on every .pc -- the
     grid's, which card() draws, and the home band's and the search page's,
     which Liquid draws and fillCards() gives a swatch row -- so it reads the
     card rather than assuming either's shape: the colour line and the stock
     line are created when the lead had none, and a placeholder SVG where a
     product has no photo is left alone.

     The photo is decoded before it is shown, so the old one stays up until the
     new one is ready, and a token makes a quick A-then-B end on B whichever
     decode finishes last. A rejected decode still swaps: a picture that will
     not decode in advance can still load. */
  function pcPick(c, colour){
    var p = pcRow(c.dataset.sku, colour);
    if(!p) return;
    c.dataset.colour = colour;
    Array.prototype.forEach.call(c.querySelectorAll('.pc-swatch button'), function(b){
      b.setAttribute('aria-pressed', String(b.dataset.colour === colour));
    });
    c.classList.toggle('out', !inStock(p));
    var img = c.querySelector('.pc-media img');
    if(img && p.img && p.img !== BLANK_PX && img.getAttribute('src') !== p.img){
      var tok = c.pcTok = (c.pcTok || 0) + 1;
      /* The slot keeps its sizes; the set follows the colour. The pre-decode
         asks with the same sizes and set, so it warms the file the card is
         about to show rather than the 520 base. */
      var set = img.sizes ? cdnSet(p.img) : '';
      var pre = new Image();
      var put = function(){
        if(c.pcTok !== tok) return;
        if(set) img.srcset = set; else img.removeAttribute('srcset');
        img.src = p.img;
        /* the search page's photo is named; a named photo follows its colour */
        if(img.getAttribute('alt')) img.setAttribute('alt', T(p.en, p.ar) + (p.cEn ? ', ' + T(p.cEn, p.cAr) : ''));
      };
      if(set){ pre.sizes = img.sizes; pre.srcset = set; }
      pre.src = p.img;
      if(pre.decode) pre.decode().then(put, put); else put();
    }
    var body = c.querySelector('.pc-body');
    var line = body.querySelector('p:not(.pc-stock)');
    if(!line){
      line = d.createElement('p');
      body.insertBefore(line, body.querySelector('.price'));
    }
    setBi(line, p.cEn, p.cAr);
    var price = body.querySelector('.price');
    if(price){
      var em = price.querySelector('em');
      price.innerHTML = (p.was ? '<s>' + money(p.was) + '</s>' : '') + money(p.price) + ' ' +
        (em ? em.outerHTML : '<em style="font-style:normal;color:var(--dust)">' + esc(CFG.money.en) + '</em>');
    }
    var sl = stockLine(p, c.pcSizes), st = body.querySelector('.pc-stock');
    if(sl[1]){
      if(!st){ st = d.createElement('p'); body.appendChild(st); }
      st.className = 'pc-stock ' + sl[0]; st.textContent = sl[1];
    } else if(st) st.parentNode.removeChild(st);
    var link = c.querySelector('.card-link');
    if(link) link.setAttribute('href', p.url || productUrl(p.sku, p.colour, link.getAttribute('href')));
    swipeSync(c);
    c.dispatchEvent(new CustomEvent('og:pick', {bubbles:true, detail:{sku:p.sku, colour:colour}}));
  }
  /* One listener for every .pc on the page, so a card built after load -- the
     grid redraws on every filter -- needs no binding of its own. */
  d.addEventListener('click', function(e){
    var b = e.target.closest && e.target.closest('.pc-swatch button');
    var c = b && b.closest('.pc');
    if(!c || b.getAttribute('aria-pressed') === 'true') return;
    pcPick(c, b.dataset.colour);
  });
  /* Warm the photo before the click lands. pointerover and focusin bubble;
     pointerenter and focus do not, and this is one listener for every card. */
  function pcWarm(e){
    var b = e.target.closest && e.target.closest('.pc-swatch button');
    var c = b && b.closest('.pc');
    var p = c && pcRow(c.dataset.sku, b.dataset.colour);
    if(p && p.img && !p.pcWarm){
      p.pcWarm = 1;
      /* Warm what pcPick will ask for: the same sizes and set, not the base. */
      var im = c.querySelector('.pc-media img'), pre = new Image();
      var set = im && im.sizes ? cdnSet(p.img) : '';
      if(set){ pre.sizes = im.sizes; pre.srcset = set; }
      pre.src = p.img;
    }
  }
  d.addEventListener('pointerover', pcWarm);
  d.addEventListener('focusin', pcWarm);

  /* ---- a phone card pages through its photographs -------------------
     Hashim, 5 Oct 2026, on a phone: "in product cards, you should be able to
     swipe through the product images". A card held one photograph -- a .card
     held two and swapped them on a tap -- while the product behind it was
     photographed three to six times, and a finger has no hover to reach any
     of them.

     So on a touch screen a card whose catalogue row lists two or more
     photographs (og-catalog's imgs) gets a scroll-snap strip of all of them
     over its picture, with a dot per frame. A sideways swipe pages; a
     vertical one still scrolls the page; a tap opens the product, through the
     card's own link. The strip sits above the stretched link and under the
     quick-view button and the tag.

     LIVE CARDS ONLY, BY CONSTRUCTION. The parity fixture publishes no imgs and
     the reference never builds a strip, so every graded card -- frozen or
     drawn by card() off the fixture -- is untouched. The drawer's cross-sell
     opts out: a swipe inside a card inside a sideways carousel would fight
     the carousel for the same finger.

     Rebuilt when the card changes colour -- pcPick and cardPick call this --
     because each colour is its own product with its own photographs. Built
     once per colour: data-for names the row it was built from. */
  function swipeSync(c){
    if(!touch || !c || c.ogNoSwipe) return;
    var media = c.querySelector('.pc-media, .card-media');
    if(!media) return;
    var old = media.querySelector('.og-swipe'), oldDots = media.querySelector('.og-dots');
    var p = pcRow(c.dataset.sku, c.dataset.colour);
    var imgs = p && p.imgs;
    if(!imgs || imgs.length < 2){
      c.swTok = (c.swTok || 0) + 1;
      if(old) media.removeChild(old);
      if(oldDots) media.removeChild(oldDots);
      c.classList.remove('has-swipe');
      return;
    }
    if(old && old.dataset.for === p.id) return;
    /* The slot's own sizes, off the photograph the card already shows, so the
       first frame is the same file -- a cache hit, not a second download. */
    var base = media.querySelector('img');
    var sz = (base && base.getAttribute('sizes')) || SZ_GRID;
    var strip = d.createElement('div');
    strip.className = 'og-swipe';
    strip.dataset.for = p.id;
    /* The photographs repeat the card; the link beside them names it. */
    strip.setAttribute('aria-hidden', 'true');
    /* Every frame lazy, the first included: the base photograph under the
       strip is lazy, and an eager first frame fetched every card's picture at
       once -- off-screen cards and the home band's hidden tabs with them. */
    strip.tabIndex = -1;
    strip.innerHTML = imgs.map(function(u){
      return '<img' + imgSet(u, sz) + ' src="' + esc(u) + '" alt="" draggable="false" loading="lazy" decoding="async">';
    }).join('');
    var dots = d.createElement('div');
    dots.className = 'og-dots';
    dots.setAttribute('aria-hidden', 'true');
    dots.innerHTML = imgs.map(function(u, i){ return '<i' + (i ? '' : ' class="on"') + '></i>'; }).join('');
    /* RTL reports scrollLeft negative, hence Math.abs, as tabFade does. */
    strip.addEventListener('scroll', function(){
      var at = Math.round(Math.abs(strip.scrollLeft) / (strip.clientWidth || 1));
      Array.prototype.forEach.call(dots.children, function(o, k){ o.classList.toggle('on', k === at); });
    }, {passive:true});
    /* A tap is the card's link. Stopped here so a .card's media does not also
       take it as its old tap-to-swap. */
    strip.addEventListener('click', function(e){
      e.stopPropagation();
      var a = c.querySelector('.card-link');
      if(a) a.click();
    });
    var put = function(){
      if(c.swTok !== tok) return;
      var o = media.querySelector('.og-swipe'), od = media.querySelector('.og-dots');
      if(o) media.removeChild(o);
      if(od) media.removeChild(od);
      media.appendChild(strip);
      media.appendChild(dots);
      c.classList.add('has-swipe');
    };
    var tok = c.swTok = (c.swTok || 0) + 1;
    if(!old){ put(); return; }
    /* A colour switch keeps the old strip up until the new colour's first
       photograph is decoded, the way pcPick keeps the old picture; a token
       makes a quick A-then-B end on B. */
    var pre = new Image(), set = cdnSet(imgs[0]);
    if(set){ pre.sizes = sz; pre.srcset = set; }
    pre.src = imgs[0];
    if(pre.decode) pre.decode().then(put, put); else put();
  }

  /* ---- live cards are filled from the catalogue ---------------------
     Liquid cannot list a garment's colours inside a card without walking the
     shop inside every card, so the live templates -- card.liquid,
     home-categories.liquid and main-search.liquid -- render the product they
     were given and mark the card data-og-fill, and this gives it the rest from
     CATALOG's rows.

     THE MARKER IS WHAT KEEPS THE PARITY LANES SAFE. The frozen copies of the
     reference's cards (main-home.liquid, og-product-fixture.liquid) carry no
     marker and are never touched, so the cards both pixel trees photograph
     stay exactly as written. No graded page renders a live template, so the
     attribute is never compared either.

     The row is found by the link, not by data-sku: both the card's href and the
     row's url are written from product.url, and the home band used to derive
     its sku and colour by a different rule from the catalogue's. A card whose
     product is not in the blob (it lists the first fifty) is left as Liquid
     drew it.

     data-og-fill="own" is a LISTING card -- the search page's, one colour each
     since 16 Oct 2026 -- and is filled from its own row alone: the colour line
     in both languages, money()'s price, the stock line and the swipe strip,
     but no swatch row and no lead, so a sold-out colour stays itself rather
     than turning into a sibling that has its own card beside it. */
  function pathOf(u){
    try{ return decodeURIComponent(new URL(u, location.href).pathname).replace(/\/$/, ''); }
    catch(e){ return ''; }
  }
  function fillRow(c){
    var a = c.querySelector('.card-link'), path = a && pathOf(a.getAttribute('href'));
    var hit = null;
    if(path) PRODUCTS.some(function(x){
      if(x.url && pathOf(x.url) === path){ hit = x; return true; }
    });
    return hit;
  }
  /* The first colour that can be bought, so a garment only looks sold out when
     every colour is -- the grid's rule, where results() orders in-stock first. */
  function fillLead(row, sibs){
    if(inStock(row)) return row;
    for(var i = 0; i < sibs.length; i++) if(inStock(sibs[i])) return sibs[i];
    return row;
  }
  function fillCards(root){
    Array.prototype.forEach.call((root || d).querySelectorAll('[data-og-fill]'), function(c){
      if(c.ogFilled) return;
      var row = fillRow(c);
      if(!row) return;
      c.ogFilled = true;
      c.dataset.sku = row.sku; c.dataset.colour = row.colour;
      var sibs = c.getAttribute('data-og-fill') === 'own' ? [row] : pcSiblings(row.sku);
      if(c.classList.contains('pc')) fillPc(c, row, sibs);
      else if(c.classList.contains('card')) fillCard(c, row, sibs);
      swipeSync(c);
    });
  }
  /* A .pc gets card()'s swatch row after its price, then goes through pcPick
     once -- even on its own colour -- so every filled card is in the state the
     grid would have drawn: the colour line in both languages, the price in
     money()'s format, the stock line, an in-stock lead. */
  function fillPc(c, row, sibs){
    c.pcSizes = [];
    var price = c.querySelector('.pc-body .price');
    if(price && sibs.length > 1) price.insertAdjacentHTML('afterend', pcSwatches(row, sibs));
    pcPick(c, fillLead(row, sibs).colour);
  }
  /* A .card gets one chip per colour, each carrying every attribute
     cardPick() reads, present even when empty -- a missing data-price means
     "leave the price alone" to that handler, not "no price". data-cw is the
     chip's POSITION in the merchant's colour order, so the server's images,
     drawn for the one colour it knew, are renumbered to that colour's place.
     Every other colour's photos are mounted now, lazily: they sit at opacity 0,
     so they load with the card and a switch never waits on the network, which
     is the rail's own rule. */
  function fillCard(c, row, sibs){
    if(sibs.length < 2) return;
    var at = 0;
    sibs.forEach(function(x, i){ if(x.colour === row.colour) at = i; });
    var media = c.querySelector('.card-media');
    Array.prototype.forEach.call(media.querySelectorAll('img[data-cw="0"]'), function(im){
      im.dataset.cw = String(at);
    });
    var after = media.querySelectorAll('img');
    after = after[after.length - 1];
    /* Every colour's photographs take the slot's own sizes, from the lead the
       server drew, so a colour switched to is as sharp as the one it replaced. */
    var sz = (after && after.getAttribute('sizes')) || SZ_GRID;
    var out = soldOut(), union = [];
    sibs.forEach(function(x){
      (x.sizes || []).forEach(function(z){ if(union.indexOf(z) === -1) union.push(z); });
    });
    union.sort(function(a, b){
      var ia = ALL_SIZES.indexOf(a), ib = ALL_SIZES.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    var chips = sibs.map(function(x, i){
      var en = x.cEn || x.en, ar = x.cAr || x.ar || en, sold = !inStock(x);
      var live = (x.sizes || []).filter(function(z){ return x.stock[z] > 0; });
      if(i !== at){
        var html = '<img class="front" data-cw="' + i + '"' + imgSet(x.img, sz) +
          ' src="' + esc(cdnWidth(x.img, 680) || x.img) + '" alt="" loading="lazy">' +
          (x.img2 ? '<img class="back" data-cw="' + i + '"' + imgSet(x.img2, sz) +
          ' src="' + esc(cdnWidth(x.img2, 680) || x.img2) + '" alt="" loading="lazy">' : '');
        if(after) after.insertAdjacentHTML('afterend', html);
        after = media.querySelectorAll('img');
        after = after[after.length - 1];
      }
      return '<button type="button" data-cw="' + i + '" data-colour="' + esc(x.colour) +
        '" style="--c:' + esc(swatchHex(x.colour, x.hex)) + '" aria-pressed="' + (i === at) +
        '" data-name-en="' + esc(en) + '" data-name-ar="' + esc(ar) +
        '" aria-label="' + esc(T(en, ar)) + '" data-al-en="' + esc(en) + '" data-al-ar="' + esc(ar) +
        '" data-price="' + esc(money(x.price)) + '" data-was="' + (x.was ? esc(money(x.was)) : '') +
        '" data-tag-en="' + (sold ? esc(out[0]) : '') + '" data-tag-ar="' + (sold ? esc(out[1]) : '') +
        '" data-tag-cls="' + (sold ? 'out' : '') + '" data-sizes="' + esc(live.join(',')) + '"></button>';
    });
    var g = c.querySelector('.card-swatch');
    if(g) g.innerHTML = chips.join('');
    var szBox = c.querySelector('.sizes');
    if(szBox && union.length){
      var here = (row.sizes || []).filter(function(z){ return row.stock[z] > 0; });
      szBox.innerHTML = union.map(function(z){
        return '<button' + (here.indexOf(z) > -1 ? '' : ' class="gone" disabled') + '>' + esc(z) + '</button>';
      }).join('');
    }
    var lead = fillLead(row, sibs);
    if(lead !== row && g){
      var chip = g.querySelector('button[data-colour="' + lead.colour.replace(/"/g, '') + '"]');
      if(chip) cardPick(chip);
    }
  }
  function collectionUrl(handle){
    if(!handle) return CFG.routes.all;
    return CFG.routes.collections.replace(/\/$/,'') + '/' + handle;
  }
  function searchUrl(q){
    return CFG.routes.search + '?q=' + encodeURIComponent(q) + '&type=product';
  }

  /* ---- storage ----
     One place that knows storage can be missing. Reading window.localStorage
     itself throws when a browser is set to block site data -- not just the
     call -- so the try has to wrap the property access, not sit around a
     lookup done once. localStorage for what should outlive the tab. */
  function store(box){
    return {
      get: function(k){ try{ return window[box].getItem(k); }catch(e){ return null; } },
      set: function(k,v){ try{ window[box].setItem(k,v); }catch(e){} },
      del: function(k){ try{ window[box].removeItem(k); }catch(e){} }
    };
  }
  var mem = store('localStorage'), ses = store('sessionStorage');

  /* ---- resize ----
     Nine handlers were bound straight to the event, several of them measuring
     live rects: measureMorph alone writes three inline styles, reads a rect and
     restores them, which is three forced reflows per event. iOS fires resize
     continuously while the URL bar collapses on scroll -- exactly while someone
     is scrolling. The ribbon's scroll handler was already rAF-batched; this is
     the same idea, shared. */
  var rzJobs = [], rzTick = null;
  function onResize(fn){
    rzJobs.push(fn);
    if(rzJobs.length > 1) return;
    window.addEventListener('resize', function(){
      if(rzTick) return;
      rzTick = requestAnimationFrame(function(){
        rzTick = null;
        for(var i=0;i<rzJobs.length;i++){ try{ rzJobs[i](); }catch(e){} }
      });
    });
  }

  /* ---- language: real RTL, not just translated strings ----
     Applied before anything measures or builds, so the menu roll-up is built
     from Arabic once instead of Latin first and Arabic 20ms later, the header
     fold is sized against the right type stack, and no listener needs an
     og:dir at init to correct itself. The choice is a property of the reader,
     not of the view, so it lives in storage rather than in the URL.
     partials/head.html reads the same key inline, before first paint. */
  var LANG_KEY = 'og:lang';
  var ar = false;
  function applyLang(toAr, announce, scope){
    ar = !!toAr;
    /* A scoped call translates one re-rendered section and must not touch the
       document's own lang/dir -- only a real toggle does that. */
    var box = scope || d;
    if(!scope){
      root.lang = ar ? 'ar' : 'en';
      root.dir  = ar ? 'rtl' : 'ltr';
    }
    Array.prototype.forEach.call(box.querySelectorAll('[data-en]'), function(el){
      var v = ar ? el.dataset.ar : el.dataset.en;
      if(v!=null){ if(v.indexOf('<')>-1) el.innerHTML=v; else el.textContent=v; }
    });
    /* Placeholders are the one string textContent cannot carry. They used to be
       three hand-written exceptions in three functions; now they are an
       attribute pair like everything else. */
    Array.prototype.forEach.call(box.querySelectorAll('[data-ph-en]'), function(el){
      el.placeholder = ar ? el.dataset.phAr : el.dataset.phEn;
    });
    /* aria-label is not a text node, so the swap never reached it and every
       accessible name on the site stayed English in Arabic. */
    Array.prototype.forEach.call(box.querySelectorAll('[data-al-en]'), function(el){
      el.setAttribute('aria-label', ar ? el.dataset.alAr : el.dataset.alEn);
    });
    /* Alt text is the same case as aria-label: a sentence a reader hears, and
       the spec sheet carries an Arabic one for each of the three photographs a
       merchant writes. Without this pair they were fields the admin offered and
       no page could ever show. The gallery composes its own alt at runtime from
       the colourway and needs no pair. */
    Array.prototype.forEach.call(box.querySelectorAll('[data-alt-en]'), function(el){
      el.setAttribute('alt', ar ? el.dataset.altAr : el.dataset.altEn);
    });
    if(announce){
      window.dispatchEvent(new Event('og:dir'));
      setTimeout(sizeFold, 30);
      remeasureOnFonts();
    }
  }
  /* English is already what the markup says, so only Arabic needs the walk. */
  if(mem.get(LANG_KEY) === 'ar') applyLang(true, false);
  function toggleLang(){
    applyLang(!ar, true);
    mem.set(LANG_KEY, ar ? 'ar' : 'en');
  }
  /* Arabic swaps the whole type stack to Tajawal and zeroes letter-spacing, and
     both the collapsed fold width and the menu morph are baked pixel values. A
     face is only fetched once text needing it renders, so this has to run after
     the swap rather than before it, or fonts.ready resolves on a promise that
     never asked for Tajawal. */
  function remeasureOnFonts(){
    if(!d.fonts || !d.fonts.ready) return;
    d.fonts.ready.then(function(){ sizeFold(); measureMorph(); });
  }

  /* Drag to scroll a horizontal strip. Touch is left to the browser so it
     keeps momentum and snapping; a mouse gets nothing by default, which is
     what makes these strips feel stuck in a narrow desktop window. A drag
     also swallows the click that follows it, so you cannot change tab or
     open a card just by dragging past one. */
  /* Not every layout renders every piece of chrome -- password.liquid renders
     none of it -- and this file is one IIFE, so a single addEventListener on a
     missing header aborted everything below it, including the language toggle.
     Binding goes through here so an absent element is a no-op rather than a
     throw, and the same file works on a layout that has no chrome at all. */
  function on(el, ev, fn, opt){ if(el) el.addEventListener(ev, fn, opt); return el; }

  var DRAG_MIN = 6;
  function dragScroll(el, onEnd){
    if(!el) return;
    var down=false, held=false, pid=null, startX=0, startLeft=0, moved=0;
    function move(e){
      if(!down) return;
      var dx=e.clientX-startX;
      if(Math.abs(dx)>moved) moved=Math.abs(dx);
      /* Capture only once it is a drag. Capturing on pointerdown retargets
         pointerup to this element, which destroys the click on whatever was
         actually pressed, so a plain tap would select nothing. */
      if(!held && moved>DRAG_MIN){
        held=true; el.classList.add('dragging');
        try{ el.setPointerCapture(pid); }catch(err){}
      }
      if(held) el.scrollLeft=startLeft-dx;
    }
    function up(){
      if(!down) return;
      var wasHeld=held;
      down=false; held=false; el.classList.remove('dragging');
      /* A drag that leaves the strip has to keep tracking the pointer, which
         is what these two are for -- but only until it ends. Bound for the
         life of the strip they outlive it: the theme editor re-renders a
         section on every edit, and each render used to leave a pair behind
         holding a detached row alive and running on every pointermove. */
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if(wasHeld && onEnd) onEnd();
    }
    el.addEventListener('pointerdown', function(e){
      if(e.pointerType==='touch') return;
      down=true; held=false; moved=0; pid=e.pointerId;
      startX=e.clientX; startLeft=el.scrollLeft;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    });
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('click', function(e){
      if(moved>DRAG_MIN){ e.preventDefault(); e.stopPropagation(); }
      moved=0;
    }, true);
    /* A card is a link wrapping an image and both are natively draggable, so a
       mouse drag that starts on one begins an HTML5 link/image drag instead:
       the browser takes the pointer stream, the moves above stop arriving, and
       the strip never scrolls. Measured on the drawer's cross-sell rail --
       pointerdown lands, exactly one pointermove lands, then nothing, and
       scrollLeft stays 0. Every strip this function binds holds a link or an
       image, so all five of them have been undraggable with a mouse.
       It reproduces only under real input: synthetic PointerEvents never start
       a native drag, so a test that dispatches its own events sees a working
       carousel. That is why this survived a pixel lane and a behaviour lane.
       Refused outright rather than gated on `held`, which is set one move too
       late to help -- and nothing this function binds wants drag and drop. */
    el.addEventListener('dragstart', function(e){ e.preventDefault(); });
  }

  /* ---- loader ---- */
  function drop(){ var l=d.getElementById('loader'); if(l) l.classList.add('done'); }
  window.addEventListener('load', function(){ setTimeout(drop, 1500); });
  setTimeout(drop, 3200); // hard fallback so the page never gets stuck

  /* ---- announcement ribbon ----
     Two jobs. The ribbon is in the document flow, so it scrolls away by
     itself; the first job is telling the fixed header where its bottom edge
     currently is, so the bar rides down while the ribbon is on screen and
     returns to its resting offset once it has gone. Reading a rect per frame
     is cheaper here than an observer, because the value is wanted every frame
     anyway. The second is the marquee, below. */
  /* Re-mountable, because the ribbon is a section now: editing it in the theme
     editor replaces the whole subtree, and a handler bound to the element that
     used to be #ribbon is left pointing at a node no longer in the document.
     What binds ONCE is the window-level scroll/resize pair -- those survive a
     section swap and re-binding them per mount would stack duplicates. */
  var ribbon=null, ribTrack=null, ribMsgs=[], ribLoop=null, ribW=0;
  function hdrFollow(){
    root.style.setProperty('--hdr-top',
      Math.round(ribbon ? Math.max(0, ribbon.getBoundingClientRect().bottom) : 0) + 'px');
  }
  var rTick=null;
  function onRib(){
    if(rTick) return;
    rTick=requestAnimationFrame(function(){ rTick=null; hdrFollow(); });
  }
  window.addEventListener('scroll', onRib, {passive:true});
  onResize(onRib);

  /* ---- the marquee ----
     THE SERVER RENDERS THE STATIC RIBBON AND THIS TURNS IT INTO A LOOP, out of
     the very nodes the server sent. Nothing about the markup changes, which is
     what keeps tools/dom_parity.py (raw server HTML) and every pixel lane (all
     reduced motion) untouched by construction -- and a reduced-motion visitor
     simply never gets past the first return below.

     THE LOOP HAS TO BE WIDER THAN IT LOOKS. Two copies sliding by -50% close
     only when each copy is at least as wide as the ribbon; two short messages
     are about 600px, so on a 1440px screen a naive two-copy loop opens a gap
     every cycle. The group is padded with repeats until it fills the track, and
     only then doubled.

     Clones are aria-hidden and inert, with no .on and no editor attributes. The ORIGINALS stay first and live, so a screen reader
     reads each message once and a link a merchant puts in one stays reachable.

     No visible jump on load: #loader covers the page at z-index 200 over the
     ribbon's 100 on every load, and this runs before it drops. */
  var ribStill = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : {matches:false};
  function ribHide(el){
    el.classList.remove('on');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('inert', '');
    [el].concat([].slice.call(el.querySelectorAll('*'))).forEach(function(n){
      n.classList.remove('on');
      n.removeAttribute('data-shopify-editor-block');
      n.removeAttribute('id');
    });
    return el;
  }
  /* Put the server's nodes back where it rendered them and drop the loop. */
  function ribTear(){
    if(ribLoop){
      ribMsgs.forEach(function(m){ ribTrack.appendChild(m); });
      if(ribLoop.parentNode) ribLoop.parentNode.removeChild(ribLoop);
    }
    ribLoop = null;
    if(ribbon) ribbon.classList.remove('is-marq');
  }
  function ribBuild(){
    ribTear();
    if(!ribbon || !ribTrack || !ribMsgs.length) return;
    if(ribStill.matches || ribbon.classList.contains('gone')) return;
    ribbon.classList.add('is-marq');
    var loop = d.createElement('div'), grp = d.createElement('div');
    loop.className = 'rib-loop';
    grp.className = 'rib-grp';
    ribMsgs.forEach(function(m){ grp.appendChild(m); });
    loop.appendChild(grp);
    ribTrack.appendChild(loop);
    ribLoop = loop;
    ribW = ribTrack.clientWidth;
    /* Bounded, so a track that measures 0 (a hidden ancestor) cannot spin. */
    for(var g=0; grp.getBoundingClientRect().width < ribW && g < 24; g++){
      ribMsgs.forEach(function(m){ grp.appendChild(ribHide(m.cloneNode(true))); });
    }
    loop.appendChild(ribHide(grp.cloneNode(true)));
    var pxs = parseFloat(getComputedStyle(ribbon).getPropertyValue('--rib-pxs')) || 40;
    loop.style.setProperty('--rib-dur', (grp.getBoundingClientRect().width / pxs).toFixed(2) + 's');
  }
  /* REGISTERED ONCE, here, never inside mountRibbon: rzJobs has no removal path,
     so a job bound per theme-editor swap would stack one stale writer per edit.
     Each reads the module's current handles by name. A resize only rebuilds
     when the track actually changed width -- a rebuild restarts the scroll. */
  onResize(function(){ if(ribLoop && ribTrack && ribTrack.clientWidth !== ribW) ribBuild(); });
  /* Web fonts change every width the loop was measured with, and a language
     switch rewrites every message's text through applyLang. */
  if(d.fonts && d.fonts.ready) d.fonts.ready.then(function(){ if(ribLoop) ribBuild(); });
  window.addEventListener('og:dir', function(){
    ribBuild();
    if(d.fonts && d.fonts.ready) d.fonts.ready.then(function(){ if(ribLoop) ribBuild(); });
  });
  /* Follows the OS setting live: switching reduced motion on mid-visit tears
     the loop down, switching it off builds one. */
  if(ribStill.addEventListener) ribStill.addEventListener('change', ribBuild);
  else if(ribStill.addListener) ribStill.addListener(ribBuild);

  /* Re-mountable, because the ribbon is a section: editing it in the theme
     editor replaces the whole subtree, and handles pointing at the old one are
     pointing at nodes no longer in the document. The old loop went with the old
     subtree, so it is dropped rather than torn down. */
  function mountRibbon(){
    ribLoop = null;
    ribbon = d.getElementById('ribbon');
    ribTrack = ribbon ? ribbon.querySelector('.ribbon-track') : null;
    ribMsgs = ribTrack ? [].slice.call(ribTrack.querySelectorAll('.ribbon-msg')) : [];
    /* With no ribbon -- every message deleted -- this resolves to 0 and the
       header sits at the top of the viewport, which is where the section's
       own --rib-h:0px has already put the hero. */
    hdrFollow();
    if(!ribbon) return;
    ribBuild();

    /* A fresh element every mount, so this cannot double-bind. */
    var rx=d.getElementById('ribbonX');
    if(rx) rx.addEventListener('click', function(){
      root.classList.add('rib-out');            /* only the dismissal animates */
      ribbon.classList.add('gone');             /* .gone also pauses the loop */
      ribbon.setAttribute('aria-hidden','true');
      root.style.setProperty('--rib-h','0px');   /* the hero grows into the gap */
      /* the bar has to stay welded to the ribbon's edge for the whole
         collapse, not jump to the end of it */
      var t0=Date.now();
      (function step(){
        hdrFollow();
        if(Date.now() - t0 < 560) requestAnimationFrame(step);
        else root.classList.remove('rib-out');
      })();
    });
  }
  mountRibbon();

  /* ---- wordmark: one, centred, held still ---- */
  /* Hoisted so a re-rendered marquee section can be refilled. The band is
     100% JS-generated content, which makes it the most visible symptom of a
     missing re-mount: edit the section and the wordmark never comes back.

     The band has a preset, so a merchant can add a second one and both carry
     the same id. querySelectorAll matches every element carrying it, which is
     how the second band gets filled on first paint rather than only after the
     merchant happens to edit it. */
  var WORDMARK = "<svg class=\"mark\" viewBox=\"0 0 1400 544\" xmlns=\"http://www.w3.org/2000/svg\" aria-hidden=\"true\"><path fill-rule=\"evenodd\" d=\"M1351.0 519.3C1352.4 518.4 1358.8 513.2 1365.2 507.8L1377.0 497.9L1377.1 455.2C1377.2 431.7 1377.3 375.8 1377.4 331.0L1377.5 249.5L1369.9 233.5C1365.7 224.7 1360.2 212.9 1357.5 207.3L1352.8 197.2L1342.1 191.5C1306.2 172.2 1261.1 149.0 1259.5 149.0C1258.2 149.0 1256.3 201.1 1257.5 203.0C1257.9 203.6 1260.6 205.2 1263.6 206.5C1267.2 208.1 1269.0 209.5 1269.0 210.7C1269.1 215.5 1268.7 346.0 1268.5 378.8C1268.4 399.3 1268.0 416.4 1267.6 416.7C1267.2 417.1 1264.6 416.2 1261.8 414.6C1257.7 412.3 1256.6 412.0 1255.9 413.1C1255.4 413.9 1255.0 426.9 1255.0 442.1L1255.0 469.8L1263.2 474.0C1267.8 476.4 1274.9 480.1 1279.0 482.3C1288.0 487.1 1335.7 514.3 1341.9 518.2C1347.1 521.5 1347.8 521.6 1351.0 519.3Z M71.5 505.5C79.8 501.0 92.8 493.8 100.5 489.7L114.5 482.1L127.4 456.9L140.3 431.7L139.6 385.6C139.3 360.2 138.7 311.8 138.4 277.9C138.0 244.0 137.7 214.3 137.6 211.9C137.5 209.5 137.3 203.1 137.2 197.8L137.0 188.0L123.9 173.3C113.3 161.4 110.3 158.5 108.3 158.7C107.0 158.8 93.5 165.3 78.2 173.2L50.5 187.5L43.3 201.5C39.3 209.2 33.1 222.0 29.4 230.0L22.7 244.5L23.4 333.0C23.7 381.7 24.3 437.0 24.6 456.0L25.2 490.5L27.9 493.0C42.1 506.0 53.2 514.9 54.8 514.3C55.7 514.0 63.2 510.0 71.5 505.5Z M1240.1 432.3C1240.1 418.1 1240.0 405.8 1240.0 404.9C1240.0 404.0 1237.6 402.2 1234.0 400.5C1229.1 398.2 1228.0 397.1 1227.5 394.6C1227.1 392.9 1222.8 375.4 1217.9 355.7C1213.0 336.0 1209.0 318.8 1209.0 317.5C1209.0 316.0 1212.9 311.0 1220.9 302.5L1232.7 289.7L1233.4 276.1C1233.7 268.6 1234.0 241.6 1234.0 216.0L1233.9 169.5L1222.8 148.0C1216.7 136.2 1211.1 125.6 1210.4 124.6C1208.9 122.5 1189.9 114.3 1156.3 101.4C1146.3 97.6 1107.0 84.0 1105.9 84.0C1105.4 84.0 1105.0 95.0 1105.0 111.0C1105.0 140.9 1104.5 139.0 1111.9 140.4C1118.5 141.6 1118.0 133.5 1118.0 246.1C1118.0 309.8 1117.6 349.1 1117.1 349.5C1116.5 349.8 1113.6 349.2 1110.6 348.1C1107.6 347.1 1104.9 346.5 1104.6 346.8C1103.3 348.0 1104.0 401.7 1105.2 402.8C1105.9 403.3 1109.4 404.7 1113.0 405.8C1116.6 407.0 1130.7 412.4 1144.5 417.9C1158.2 423.4 1169.8 427.6 1170.2 427.2C1170.7 426.7 1170.9 414.3 1170.8 399.5L1170.5 372.5L1164.9 369.9C1158.3 366.7 1158.0 366.0 1158.5 354.0C1158.7 349.3 1158.9 335.3 1159.0 322.8L1159.0 300.0L1161.9 300.0C1166.0 300.0 1166.2 300.4 1172.0 323.0C1174.9 334.3 1179.0 350.2 1181.2 358.5C1183.8 368.8 1184.9 374.6 1184.5 377.0C1183.7 381.4 1183.0 429.8 1183.7 432.6C1184.0 434.1 1186.6 435.9 1192.3 438.5C1196.8 440.7 1207.9 446.0 1217.0 450.3C1235.1 459.0 1238.1 460.1 1239.4 458.9C1239.9 458.5 1240.2 446.5 1240.1 432.3Z M1335.9 456.2C1333.4 454.7 1326.1 450.4 1319.7 446.7C1312.4 442.4 1308.0 439.2 1308.0 438.2C1307.5 422.3 1308.3 233.3 1308.8 232.8C1309.5 232.1 1317.2 235.6 1326.5 240.9C1328.7 242.2 1332.3 244.2 1334.5 245.3C1342.8 249.7 1342.0 237.6 1342.0 354.7C1342.0 412.0 1341.7 459.0 1341.2 459.0C1340.8 458.9 1338.4 457.7 1335.9 456.2Z M63.6 447.9C63.3 446.2 63.0 427.4 62.9 406.1C62.9 384.9 62.5 339.6 62.0 305.5C61.4 256.2 61.5 243.1 62.5 241.5C63.7 239.6 95.3 223.0 97.6 223.0C98.4 223.0 98.9 236.6 99.4 268.8C100.0 315.8 100.6 348.2 101.6 400.6C102.0 424.7 101.9 430.9 100.8 431.7C100.1 432.2 92.3 436.4 83.5 441.0C74.7 445.5 67.1 449.6 66.5 450.1C64.9 451.6 64.3 451.1 63.6 447.9Z M219.2 433.4C223.7 431.5 233.8 427.4 241.5 424.3C258.2 417.7 256.2 419.3 257.1 411.7C258.1 403.5 260.6 382.4 262.5 367.5C263.4 360.9 264.5 351.9 265.0 347.5C265.5 343.1 266.6 334.8 267.5 329.0C268.3 323.2 269.2 316.9 269.5 315.0C270.6 306.8 274.9 271.9 277.5 249.0C280.4 224.3 283.1 202.6 285.0 188.5C286.1 180.8 287.9 166.4 289.0 156.5C291.3 136.8 291.2 137.0 298.5 134.3L303.5 132.4L303.8 105.7C304.0 82.4 303.8 78.9 302.5 78.4C300.6 77.6 237.1 98.6 235.0 100.6C234.2 101.4 234.4 154.0 235.2 155.3C235.7 156.1 236.9 155.9 239.5 154.7C246.1 151.6 248.3 152.3 247.6 157.2C247.3 159.6 246.6 165.6 246.1 170.5C245.5 175.4 242.4 203.8 239.1 233.5C235.8 263.2 232.8 291.1 232.4 295.5C231.1 310.6 229.5 307.6 226.5 285.0C225.6 278.7 224.5 270.4 224.0 266.5C222.4 254.0 221.0 244.8 217.9 226.5C216.2 216.6 214.2 203.6 213.5 197.5C212.7 191.4 211.6 183.6 211.0 180.0C209.3 168.7 209.6 166.1 212.8 164.9C214.3 164.3 217.0 163.2 218.8 162.4L222.0 161.0L222.0 134.5C222.0 110.9 221.8 107.9 220.4 107.3C219.0 106.8 202.7 112.8 188.5 119.1C186.3 120.1 177.8 123.7 169.5 127.3L154.5 133.7L154.2 160.6C154.1 175.4 154.2 188.0 154.4 188.7C154.8 189.6 156.6 189.2 160.9 187.5C164.2 186.1 167.1 185.0 167.3 185.0C167.8 185.0 168.4 188.3 170.5 202.5C171.3 208.0 172.7 216.1 173.5 220.5C175.8 232.9 181.9 269.9 182.5 275.5C183.2 281.0 188.0 312.6 191.0 331.5C192.0 338.1 194.5 353.9 196.5 366.5C198.5 379.1 201.2 396.2 202.5 404.5C203.8 412.8 205.4 423.1 206.0 427.5C207.7 438.9 207.0 438.6 219.2 433.4Z M331.6 393.6C342.4 389.7 387.2 376.7 397.0 374.5C400.0 373.8 407.7 371.9 414.0 370.2C420.3 368.6 430.9 366.0 437.5 364.5C444.1 363.0 450.3 361.3 451.2 360.8C453.9 359.5 454.0 259.3 451.4 258.3C449.6 257.6 443.2 258.7 424.5 263.0L412.5 265.7L412.2 288.6C412.1 301.1 411.6 311.8 411.2 312.2C410.8 312.6 403.5 314.7 395.0 317.0C386.5 319.2 378.5 321.3 377.3 321.6L375.0 322.2L375.0 282.1C375.0 260.1 375.4 241.0 375.9 239.6C376.9 237.2 383.1 235.3 406.1 230.5C410.2 229.6 414.3 228.7 415.2 228.4C417.2 227.8 418.0 175.8 416.1 174.6C415.2 174.0 409.0 175.2 389.6 180.0C382.5 181.7 376.5 182.9 376.2 182.6C376.0 182.3 375.7 166.4 375.6 147.3L375.5 112.5L385.0 110.3C390.2 109.1 397.4 107.4 401.0 106.5C412.9 103.7 412.0 102.3 412.0 122.4C412.0 132.0 412.4 140.1 412.9 140.5C413.7 140.9 446.3 135.0 451.2 133.4C452.9 132.9 453.0 129.8 453.0 86.9L453.0 41.0L450.0 41.0C439.3 41.0 360.0 59.6 327.5 69.7L321.5 71.6L321.5 98.5L321.5 125.5L324.5 125.4C326.1 125.3 328.7 125.1 330.3 124.9L333.1 124.5L333.2 228.6C333.3 285.8 333.0 333.1 332.7 333.7C332.3 334.4 329.5 335.6 326.6 336.4C322.5 337.6 321.1 338.5 320.7 340.2C319.9 343.2 319.9 392.6 320.6 394.5C321.4 396.6 323.6 396.4 331.6 393.6Z M1083.8 368.2L1083.5 340.5L1077.7 338.7C1070.5 336.4 1070.6 336.8 1068.0 315.0C1066.8 305.4 1065.2 292.6 1064.4 286.5C1063.6 280.4 1062.3 270.3 1061.5 264.0C1060.7 257.7 1059.4 247.3 1058.5 241.0C1057.7 234.7 1056.5 225.9 1056.0 221.5C1055.4 217.1 1054.4 208.8 1053.6 203.0C1052.8 197.2 1051.2 185.5 1050.1 177.0C1049.0 168.5 1047.6 158.1 1047.0 154.0C1045.6 143.5 1043.3 125.7 1041.5 111.0C1040.7 104.1 1039.5 95.6 1039.0 92.0C1038.4 88.4 1037.6 81.5 1037.1 76.6C1036.1 66.3 1036.5 66.8 1024.5 64.0C1019.5 62.8 1009.9 60.5 1003.2 58.9C996.4 57.2 990.6 56.1 990.3 56.3C990.1 56.6 989.3 60.5 988.5 65.1C987.7 69.7 985.7 81.4 984.0 91.0C982.3 100.6 979.9 115.2 978.5 123.5C977.2 131.8 974.2 149.3 972.0 162.5C969.7 175.7 966.8 193.0 965.5 201.0C964.2 209.0 962.3 220.7 961.2 227.0C960.1 233.3 958.5 243.0 957.6 248.5C953.6 274.3 948.9 302.6 948.6 303.6C948.3 304.4 946.6 304.4 942.5 303.7C938.5 302.9 936.6 303.0 936.2 303.7C935.8 304.3 935.6 316.6 935.8 330.9L936.1 357.1L938.5 358.0C939.8 358.5 944.6 359.6 949.2 360.4C958.0 362.1 962.3 363.0 984.7 368.3C994.5 370.6 999.2 371.4 1000.0 370.6C1001.6 369.0 1001.4 318.5 999.7 317.2C999.0 316.5 996.0 315.6 993.2 314.9C988.8 314.0 988.0 313.5 988.0 311.6C988.0 308.0 992.1 286.6 992.9 285.7C994.0 284.6 1022.9 291.4 1024.6 293.2C1025.8 294.4 1027.4 303.3 1028.7 316.9L1029.3 323.3L1025.4 322.6C1023.3 322.3 1020.0 321.7 1018.2 321.4L1015.0 320.8L1015.2 348.6L1015.5 376.4L1034.5 381.6C1045.0 384.5 1059.4 388.8 1066.5 391.3C1073.7 393.8 1080.5 395.8 1081.8 395.9L1084.0 396.0L1083.8 368.2Z M498.5 353.0C508.4 351.4 520.3 349.6 525.0 349.0C537.8 347.2 540.7 346.3 541.4 343.7C542.5 339.5 542.1 293.2 540.9 292.4C540.3 292.1 537.1 291.8 533.9 291.9L528.0 292.0L528.0 257.6L528.0 223.1L531.6 222.4C533.5 222.1 535.6 222.2 536.1 222.7C537.4 224.0 554.3 274.6 557.4 286.4C557.5 287.0 556.8 288.2 555.8 289.2C554.1 290.9 554.0 293.0 554.2 317.3L554.5 343.5L560.5 343.4C565.7 343.4 594.5 341.0 609.7 339.4C612.6 339.1 615.2 338.4 615.6 337.9C616.5 336.5 616.4 285.5 615.5 284.1C615.0 283.2 612.8 282.9 608.8 283.2L602.8 283.5L601.5 280.0C600.7 278.1 598.7 272.0 597.0 266.5C595.3 261.0 590.9 247.5 587.3 236.4L580.8 216.3L593.2 198.1L605.5 179.9L605.5 120.2L605.5 60.5L595.0 46.0C589.2 38.0 583.5 30.1 582.2 28.5L579.9 25.6L555.2 27.9C526.8 30.5 519.5 31.2 497.5 34.1C488.7 35.3 479.6 36.4 477.3 36.7L473.2 37.2L472.8 63.9C472.6 78.5 472.6 91.1 472.8 91.8C473.3 93.3 477.9 93.4 481.6 92.0C483.2 91.3 484.7 91.3 485.2 91.8C486.1 92.7 486.4 296.5 485.5 298.0C485.2 298.4 482.9 299.1 480.3 299.5C471.7 300.8 472.3 298.6 472.1 328.6C472.1 343.3 472.3 355.7 472.6 356.2C472.9 356.7 474.8 356.8 476.8 356.5C478.9 356.2 488.6 354.6 498.5 353.0Z M888.5 342.1C896.0 334.0 908.2 320.0 910.8 316.6L913.0 313.7L913.0 205.0C913.0 85.9 912.6 93.8 918.7 96.1C920.2 96.6 922.5 96.9 923.7 96.5L926.0 96.0L926.0 69.5L926.0 43.0L923.6 42.0C919.1 40.3 862.5 32.6 861.0 33.5C859.4 34.5 859.6 86.9 861.2 87.5C861.9 87.7 864.6 88.1 867.2 88.5L872.0 89.1L872.0 191.1C872.0 247.1 871.7 293.0 871.3 293.0C870.4 293.0 843.4 290.1 838.2 289.4L834.0 288.9L834.2 187.2L834.5 85.5L840.8 85.8C844.2 85.9 847.0 85.7 847.1 85.3C847.5 77.7 847.9 35.8 847.5 33.9C846.9 31.4 846.5 31.2 837.2 30.1C820.0 28.1 791.2 25.9 786.3 26.2L781.5 26.5L781.5 53.0L781.5 79.5L787.4 80.2C790.6 80.6 793.6 81.2 793.9 81.6C794.2 81.9 794.3 131.9 794.1 192.6L793.7 303.0L803.6 316.7C809.1 324.3 815.2 333.1 817.4 336.2L821.2 341.9L827.6 342.5C831.1 342.9 843.8 344.5 855.7 346.0C881.9 349.3 882.0 349.3 888.5 342.1Z M724.2 335.0L729.8 335.0L740.4 321.8C746.3 314.5 752.6 306.5 754.5 304.1L758.0 299.7L758.0 230.3C758.0 175.7 757.7 160.9 756.8 160.5C750.8 158.6 698.1 158.2 697.0 160.1C696.1 161.5 695.8 202.3 696.6 205.2C697.1 206.7 698.4 207.0 705.5 207.0C710.1 207.0 715.0 207.4 716.5 208.0L719.1 209.0L718.9 241.2C718.9 259.0 718.6 275.0 718.3 276.8L717.8 280.0L698.6 279.8L679.5 279.5L679.5 185.5C679.5 133.8 679.7 88.3 680.0 84.5L680.5 77.5L695.5 77.2C703.8 77.0 712.4 77.2 714.8 77.5L719.0 78.1L719.0 99.5C719.0 111.5 719.4 121.1 719.9 121.5C721.3 122.3 756.8 124.5 757.6 123.7C758.0 123.4 758.2 109.0 758.1 91.8L757.9 60.5L750.7 50.0C740.0 34.2 735.4 27.7 733.2 25.4C731.4 23.3 730.2 23.2 699.8 22.5C677.0 22.0 667.7 22.1 666.2 22.9C665.0 23.5 658.4 31.9 651.5 41.6L639.0 59.2L638.9 117.4C638.8 149.3 638.6 200.9 638.5 232.0C638.2 304.7 636.8 295.9 651.9 316.0C668.8 338.7 663.8 336.3 693.5 335.6C707.2 335.3 721.0 335.1 724.2 335.0Z M1180.9 250.9C1174.1 248.2 1166.4 245.0 1163.8 243.8L1159.0 241.7L1159.0 201.3C1159.0 169.7 1159.3 160.8 1160.3 160.5C1161.6 160.0 1176.2 165.4 1188.2 170.9L1195.0 173.9L1195.0 214.4C1195.0 236.7 1194.6 255.1 1194.2 255.4C1193.7 255.7 1187.8 253.7 1180.9 250.9Z M1009.0 234.2C1000.8 232.1 1000.9 233.3 1005.5 203.8C1010.7 170.3 1010.5 170.7 1012.6 189.7C1014.9 210.9 1017.0 231.4 1017.0 233.8C1017.0 236.4 1017.4 236.3 1009.0 234.2Z M528.7 166.3C527.6 165.3 527.9 87.2 528.9 86.5C530.4 85.6 561.9 82.7 563.5 83.4C564.8 83.9 565.0 89.4 565.0 123.0L565.0 162.0L562.8 163.0C559.0 164.5 529.7 167.4 528.7 166.3Z\"/></svg>";
  Array.prototype.forEach.call(d.querySelectorAll('#marqMark'), function(mk){
    if(!mk.firstChild) mk.innerHTML = WORDMARK;
  });

  /* ---- header and menu ----
     Re-mountable, because the header is a section now. Editing it in the theme
     editor replaces the whole subtree, and this block used to capture five
     element handles at parse time and bind eight listeners to them -- so a swap
     left the menu unopenable, the language toggle dead, the fold un-collapsed
     and the morph opening from the hardcoded 260x50 fallback. All of it failed
     silently: mountSection's try/catch only speaks in design mode.

     The fix is to REASSIGN rather than re-capture. Every handle below is a
     module-scope variable that mountHeader() re-points at the new DOM, and
     every function reads them by name. That is what keeps the two onResize jobs
     and the onBag subscriber valid across a swap without needing a removal path
     -- rzJobs has none, so a job bound per mount would stack one stale writer
     per edit, forever. */
  var menu=null, menuBtn=null, hdr=null, hdrInner=null, fold=null, panel=null;
  var menuTimer=null, menuCountEl=null;

  /* the inner row keeps its natural width even while collapsed, so it stays
     measurable; re-measure on resize and after a language swap */
  function foldWidth(){
    return fold && fold.firstElementChild ? fold.firstElementChild.offsetWidth : 0;
  }
  function sizeFold(){
    if(!hdrInner || !fold || !fold.firstElementChild) return;
    if(!hdrInner.classList.contains('open')) fold.style.width=foldWidth()+'px';
  }
  function menuOpen(){ return !!menu && menu.classList.contains('on') && !menu.classList.contains('closing'); }
  function openMenu(){
    if(!menu || !hdr || !hdrInner || !fold || !menuBtn) return;
    clearTimeout(menuTimer);
    /* A panel that reopens where you left it is disorienting: the menu is a
       fixed list, not a document, and its first row is the point. Reset before
       the panel is visible, so the jump is never seen. */
    var ms = menu.querySelector('.menu-scroll');
    if(ms) ms.scrollTop = 0;
    menu.classList.remove('closing');
    menu.classList.add('on'); menu.setAttribute('aria-hidden','false');
    hdr.classList.add('raise'); hdrInner.classList.add('open');
    fold.style.width='0px'; fold.setAttribute('inert','');
    menuBtn.setAttribute('aria-expanded','true');
    d.body.classList.add('locked');
    trap(menu);
  }
  function shutMenu(){
    if(!menuOpen()) return false;
    clearTimeout(menuTimer);
    menu.classList.add('closing'); menu.setAttribute('aria-hidden','true');
    hdrInner.classList.remove('open');
    fold.removeAttribute('inert'); fold.style.width=foldWidth()+'px';
    menuBtn.setAttribute('aria-expanded','false');
    d.body.classList.remove('locked');
    untrap();
    /* keep the panel mounted until the exit finishes, and hold the raised
       z-index until then or the bar drops behind the overlay mid-transition */
    var closing = menu, closingHdr = hdr;
    menuTimer=setTimeout(function(){
      closing.classList.remove('on','closing');
      closingHdr.classList.remove('raise');
    }, 660);
  }

  /* index every staggered child once: --i counts down the panel, --o back up.
     Re-run per mount, because a merchant can add or remove nav rows and the
     count is whatever the section rendered. */
  function indexMi(){
    if(!menu) return;
    var mItems = [].concat(
      [].slice.call(menu.querySelectorAll('.menu-list li')),
      [].slice.call(menu.querySelectorAll('.menu-side > *')),
      [].slice.call(menu.querySelectorAll('.menu-foot'))
    );
    mItems.forEach(function(el,i){
      el.classList.add('mi');
      el.style.setProperty('--i', i);
      el.style.setProperty('--o', mItems.length - 1 - i);
    });
  }

  /* ---- menu link roll-up ---- */
  function buildRoll(sp){
    var txt = sp.textContent;
    sp.classList.add('roll');
    sp.innerHTML = '';
    /* Arabic joins, so it rolls as one piece rather than per letter */
    var units = root.dir === 'rtl' ? [txt] : txt.split('');
    units.forEach(function(ch, i){
      var box = d.createElement('span');
      box.className = 'roll-ch';
      box.style.setProperty('--n', i);
      var col = d.createElement('span');
      col.className = 'roll-in';
      [0,1].forEach(function(){
        var g = d.createElement('i');
        g.textContent = ch === ' ' ? '\u00A0' : ch;
        col.appendChild(g);
      });
      box.appendChild(col);
      sp.appendChild(box);
    });
  }
  function rollAll(){
    Array.prototype.forEach.call(d.querySelectorAll('.menu-list a > span[data-en]'), buildRoll);
  }
  /* the language swap rewrites textContent, so rebuild after it */
  window.addEventListener('og:dir', function(){ setTimeout(rollAll, 20); });

  /* The morph interpolates between two live rects, so it stays correct across
     breakpoints, safe-area insets and the wider Arabic bar. It reads the BAR
     and writes the PANEL, which is why the header and the menu have to be one
     section: split them and one holds a stale reference to the other. */
  function measureMorph(){
    if(!panel || !menu || !hdrInner) return;
    var bar = hdrInner.getBoundingClientRect();
    var mStyle = getComputedStyle(menu);
    var padT = parseFloat(mStyle.paddingTop) || 0;
    /* the panel's full size is whatever the overlay's content box allows,
       capped by the panel's own max-width */
    var availH = menu.clientHeight - padT - (parseFloat(mStyle.paddingBottom) || 0);
    /* read the panel's natural box with the caps lifted, rather than
       recomputing the layout by hand and drifting from it */
    var prevW = panel.style.maxWidth, prevH = panel.style.maxHeight, prevM = panel.style.marginTop;
    panel.style.maxWidth='none'; panel.style.maxHeight='none'; panel.style.marginTop='0';
    var natural = panel.getBoundingClientRect().width;
    panel.style.maxWidth=prevW; panel.style.maxHeight=prevH; panel.style.marginTop=prevM;
    panel.style.setProperty('--panel-w', Math.round(natural) + 'px');
    panel.style.setProperty('--panel-h', Math.round(availH) + 'px');
    panel.style.setProperty('--pw', Math.round(bar.width) + 'px');
    panel.style.setProperty('--ph', Math.round(bar.height) + 'px');
    panel.style.setProperty('--pt', Math.max(0, Math.round(bar.top - padT)) + 'px');
  }
  /* Bound ONCE, against functions that read the module variables above, so a
     section swap needs no unsubscribe. */
  onResize(sizeFold);
  onResize(measureMorph);
  window.addEventListener('og:dir', function(){ setTimeout(measureMorph, 40); });

  /* --pw/--ph are the BAR's box, and the bar is not settled when the old code
     last measured it. remeasureOnFonts() hangs the re-measure off
     document.fonts.ready, which resolves IMMEDIATELY when the faces are cached
     -- so on a warm load --pw was written at t=0 against a 295.59px bar, the
     bar settled to 291.39px a few hundred ms later, and nothing ever looked
     again. The menu's closed box was ~5px wider than the bar it morphs out of,
     on every warm load. Nothing caught it because freeze.js dispatches a
     synthetic resize before every capture, which re-ran measureMorph and hid it
     from the one suite that could have seen it.

     Watching the element instead of guessing when it settles covers cached
     fonts, the language toggle, the safe-area inset and anything later.

     The guard is not optional: .hdr-inner's padding animates through the whole
     morph, so an unguarded observer would fire every frame and rewrite --pw
     from a bar that is mid-collapse -- baking the closed-state box at whatever
     width it happened to catch. */
  var barRO = null;
  if(window.ResizeObserver){
    barRO = new ResizeObserver(function(){
      if(!hdrInner || hdrInner.classList.contains('open')) return;
      if(menu && menu.classList.contains('closing')) return;
      measureMorph();
    });
  }


  function mountHeader(){
    /* A swap while the menu was open leaves body.locked set with nothing able
       to clear it -- Lenis stays stopped through the MutationObserver, and the
       focus trap keeps intercepting Tab into a detached tree. Release both
       before the old handles go out of scope. */
    if(menuOpen()){
      d.body.classList.remove('locked');
      untrap();
    }
    clearTimeout(menuTimer);

    menu     = d.getElementById('menu');
    menuBtn  = d.getElementById('menuBtn');
    hdr      = d.querySelector('.hdr');
    hdrInner = d.querySelector('.hdr-inner');
    fold     = d.querySelector('.hdr-fold');
    panel    = menu ? menu.querySelector('.menu-panel') : null;
    if(!menu || !hdr) return;

    indexMi();
    rollAll();
    sizeFold();
    measureMorph();
    if(barRO){ barRO.disconnect(); if(hdrInner) barRO.observe(hdrInner); }

    /* Every element here is freshly rendered, so binding cannot double up. */
    on(menu, 'click', function(e){ if(e.target===menu) shutMenu(); });
    on(menuBtn, 'click', function(){ menuOpen() ? shutMenu() : openMenu(); });
    /* while the padding is still animating the bar is a few px larger than
       the button, so the bar itself has to answer clicks too */
    on(hdrInner, 'click', function(e){
      if(hdrInner.classList.contains('open') && !e.target.closest('#menuBtn')) shutMenu();
    });
    /* The two search entry points live here rather than in the search block:
       both buttons are header markup, so they are replaced by the same swap.
       srchOpen and shutMenu are function declarations and hoist, so calling
       them from here is safe however early this runs. */
    on(d.getElementById('searchBtn'), 'click', function(){ srchOpen(); });
    on(d.getElementById('menuSearchBtn'), 'click', function(){
      shutMenu(); setTimeout(srchOpen, 260);
    });
    /* The language toggle used to bind 1100 lines below, against elements this
       swap replaces -- so editing the header silently killed the whole
       bilingual system. */
    on(d.getElementById('langBtn'), 'click', toggleLang);
    on(d.getElementById('langBtnMenu'), 'click', toggleLang);

    /* The bag badge in the menu foot is re-rendered too. Re-point the handle
       and repaint from the live bag rather than adding a second onBag
       subscriber, which has no unsubscribe and would leak one per edit. */
    menuCountEl = d.getElementById('menuCartCount');
    /* Not at boot. `bag` is declared 1200 lines below this call, so at boot it
       is hoisted-but-unassigned and bagCount() throws on undefined.reduce --
       at file scope, outside any try/catch, which would abort the rest of the
       bundle. Boot does not need it anyway: onBag() paints the badge the moment
       it subscribes. This repaint is for the re-mount case, where the
       subscriber fired long ago and the fresh element would otherwise sit at
       the 0 its Liquid rendered. */
    if(menuCountEl && Array.isArray(bag)) menuCountEl.textContent = bagCount();
  }
  mountHeader();

  /* ---- cart drawer ---- */
  /* Module-scope handles, re-pointed by mountDrawer(). The drawer is a SECTION
     now, so the theme editor replaces this whole subtree on every edit -- and
     these were captured once at parse time. Before the mount existed, editing
     the drawer's copy left it unopenable and the scrim dead, silently, because
     mountSection's try/catch only speaks in design mode. Every function below
     reads them by name; nothing re-captures. */
  var drawer=null, scrim=null, dEmpty=null, dList=null, dFoot=null,
      dRail=null, dRailTrack=null;
  /* The rail's ONE setting, declared HERE for the same reason as drawerOnAdd
     below: a second `var railOn = true` further down is an assignment that runs
     after the mount and puts the merchant's OFF back to ON. The default is the
     reference's behaviour, the right thing to fail to when the blob is missing.

     EVERYTHING ELSE HERE IS A CONSTANT AND MUST STAY ONE. They are a height
     budget, not a preference: .drawer-scroll is the only shrinkable child in the
     panel, so a merchant who raised one of these would not get more suggestions,
     they would get their shopper's own cart compressed to make room.

     The budget, and the arithmetic so it can be re-checked rather than guessed:
     282px of fixed panel chrome + 114px per cart row + ~310px for a 4:5 card at
     this width. One row needs 706px of viewport, two need 820px, three would
     need 934px -- which no phone has, and is why railOver is 2. A row was 113
     while the drawer was read-only; with its stepper and remove it is 114 at
     the tallest measured on 5 Oct 2026, once the drawer's column gap came
     down to 5px (see .drawer-scroll .cart-end). The CSS carries
     the one-row floor, because a media query cannot see how many rows are in the
     bag; railRoom() below carries the rest from these same three numbers. */
  var railOn = true, railBound = false;
  var railCap = 6, railOver = 2;
  var railChromeH = 282, railRowH = 114, railCardH = 310;
  /* Whether an add slides the drawer open. A merchant setting on the
     cart-drawer section, so it cannot live in og-config (that snippet reads
     settings.*, and a static section's own store is one only it can see).
     mountDrawer() assigns it from #og-drawer; true until it does, which is the
     reference's behaviour and therefore the right thing to fail to. Declared
     HERE, beside the handles that mount owns, and nowhere else: a second
     `var drawerOnAdd = true` further down this IIFE is an assignment that runs
     after the mount and puts the merchant's OFF back to ON -- the trap
     dEmpty/dList/dFoot are commented for, which this shipped a second time. */
  var drawerOnAdd = true;
  /* The drawer is a dialog and was the only one of the six never treated as
     one: no scroll lock, no focus trap, no role. Every caller reaches it by
     way of qaShut() or shutMenu(), both of which drop body.locked on the way
     through, so Lenis restarted and the page scrolled behind an open drawer
     while Tab walked out into it. */
  function openCart(){
    if(!drawer || drawer.classList.contains('on')) return;
    /* The drawer confirms an add. On the cart page the add is already visible in
       the list, so sliding a confirmation over the thing being confirmed is
       noise -- and undoing a removal there did exactly that. */
    if(d.getElementById('cartList')) return;
    drawer.classList.add('on'); scrim.classList.add('on');
    drawer.setAttribute('aria-hidden','false');
    d.body.classList.add('locked');
    trap(drawer);
  }
  function shutCart(){
    if(!drawer || !drawer.classList.contains('on')) return false;
    drawer.classList.remove('on'); scrim.classList.remove('on');
    drawer.setAttribute('aria-hidden','true');
    d.body.classList.remove('locked');
    untrap();
  }
  /* The dock's bag button is in chrome-bottom-end, a snippet, so it is never
     replaced and is bound once. It calls openCart(), which reads `drawer` by
     name -- which is exactly what reassigning rather than re-capturing buys. */
  on(d.getElementById('cartBtn'), 'click', openCart);

  function mountDrawer(){
    /* Release before the old handles go out of scope. A swap while the drawer
       is OPEN would otherwise leave body.locked set and the trap pointing at a
       detached node -- Lenis stopped, the page frozen, and nothing left on the
       screen able to undo it. mountHeader() learned this first. */
    if(drawer && drawer.classList.contains('on')){
      d.body.classList.remove('locked');
      untrap();
    }
    drawer = d.getElementById('drawer');
    scrim  = d.getElementById('scrim');
    dEmpty = d.getElementById('drawerEmpty');
    dList  = d.getElementById('drawerList');
    dFoot  = d.getElementById('drawerFoot');
    dRail  = d.getElementById('drawerRail');
    dRailTrack = d.getElementById('drawerRailTrack');
    railBound = false;   /* a swap brings a new node; rebind to that one */

    /* Read inside the mount, not at boot: an edit to the checkbox arrives as a
       new subtree carrying a new blob, and a value read once at boot would go
       on describing the setting as it was when the page loaded. */
    try{
      var raw = d.getElementById('og-drawer');
      if(raw){
        var got = JSON.parse(raw.textContent);
        if(typeof got.openOnAdd === 'boolean') drawerOnAdd = got.openOnAdd;
        if(typeof got.rail === 'boolean') railOn = got.rail;
      }
    }catch(e){}

    on(d.getElementById('drawerClose'), 'click', shutCart);
    on(d.getElementById('drawerKeep'), 'click', shutCart);
    on(scrim, 'click', shutCart);
    /* Bound by its class, not a new id: tools/dom_parity.py compares every id
       against the reference, and .drawer-go is already on both. A fresh node
       arrives with every section swap, so binding here cannot stack. */
    on(drawer && drawer.querySelector('.drawer-go'), 'click', drawerGo);
    /* The steppers and removes, delegated from the list. A section swap brings
       a new list node, so binding per mount cannot stack. */
    on(dList, 'click', drawerEdit);

    /* Not at boot. `bag` is declared some 1200 lines below and is
       hoisted-but-unassigned here, so renderDrawer would throw on
       undefined.length -- at file scope, outside any try/catch, taking the rest
       of the bundle with it. The onBag subscriber renders it once anyway. */
    if(Array.isArray(bag)) renderDrawer();
  }
  mountDrawer();

  /* ---- checkout ----
     ONE WAY OUT, USED BY BOTH BUTTONS. The bag page's button had this to
     itself until the drawer's "View bag" became "Checkout" on 4 Oct 2026, and
     the two live in different IIFEs -- bindGo() far below cannot be reached from
     here -- so the logic moved up and both call it.

     THE WAIT IS THE POINT. bagAdd() opens the drawer BEFORE it queues
     /cart/add.js, so the drawer's button is on screen while the add is still in
     flight. queue() chains onto cartQ synchronously, so by the time a finger
     reaches the button the pending add is already in the promise this waits on.
     Leave without it and checkout renders the cart as it was one write ago --
     the line just added, missing. Capped at 2.5s: cartFetch has no timeout, and
     one write that never settles must not leave the button dead for the visit.
     Every arm leads to the same place on purpose; Shopify's cart, not this
     page's copy of it, is what checkout renders.

     The root, not a bare '/checkout': root_url carries the locale prefix on a
     second market and has no trailing slash there, so it is joined the way
     collectionUrl() joins. Returns false when it did nothing -- an empty bag, or
     a press while already leaving -- so a caller lights its own busy state only
     for a press that counted. */
  var checkoutGoing = false;
  function goCheckout(){
    if(!bag || !bag.length) return false;
    if(checkoutGoing) return false;
    checkoutGoing = true;
    var went = false;
    var go = function(){
      if(went) return;
      went = true;
      window.location.href = String(CFG.routes.root || '/').replace(/\/$/,'') + '/checkout';
    };
    var pending = cartQ;
    if(!(pending && pending.then)){ go(); return true; }
    var t = setTimeout(go, 2500);
    var done = function(){ clearTimeout(t); go(); };
    pending.then(done, done);
    return true;
  }
  /* The drawer's link. A plain primary click goes through goCheckout(); a
     modified one (new tab, new window) is left to the browser, which follows
     the real href. An anchor cannot be disabled, so a press that counted marks
     it aria-disabled -- and a second press while leaving is swallowed rather
     than starting a second navigation. */
  function drawerGo(e){
    if(e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if(goCheckout()) e.currentTarget.setAttribute('aria-disabled', 'true');
  }
  /* Back from checkout restores the page from the back/forward cache exactly as
     it left: the flag still set, so neither button would ever leave again. */
  window.addEventListener('pageshow', function(e){
    if(!e.persisted) return;
    checkoutGoing = false;
    var g = drawer && drawer.querySelector('.drawer-go');
    if(g) g.removeAttribute('aria-disabled');
  });
  /* Topmost first, and only one closes per press: Escape used to run every
     shut function on the site at once, so two overlays could unwind together
     and the second untrap() would restore focus to nothing. */
  d.addEventListener('keydown', function(e){
    if(e.key!=='Escape') return;
    var order=[srchShut, qaShut, shutCart, shutMenu];
    for(var i=0;i<order.length;i++){ if(order[i]() !== false) return; }
  });

  /* ---- quick add ---- */
  /* The bag lives further down, after PRODUCTS: a line is looked up against the
     catalogue and revalidated against stock at init, so it cannot be built
     before the catalogue exists. Function declarations hoist, so the add paths
     above can still call bagAdd. */
  /* Shop the look no longer carries its own size picker and add button. It
     listed one product per slide and duplicated state the quick view already
     owns; now every product in a look opens that one sheet instead. */


  /* ---- shop the look carousel ----
     Nav is index based rather than scrollBy so RTL is handled by the browser.
     The centred slide is whichever one is nearest the track's midpoint. */
  /* One id per section, and the section has a preset, so a merchant can add a
     second "Shop the look". Everything below therefore resolves from the track's
     own section -- filterCats already does this -- and the sync/flip handles
     hang off the track rather than sitting in module slots a second section
     would overwrite. querySelectorAll matches every element carrying the id,
     which is how both sections get mounted from one call. */
  function mountLook(scope){
    Array.prototype.forEach.call((scope||d).querySelectorAll('#lookTrack'), mountOneLook);
  }
  function eachLook(fn){
    Array.prototype.forEach.call(d.querySelectorAll('#lookTrack'), function(t){
      if(t.ogLookSync) fn(t);
    });
  }
  function mountOneLook(track){
    if(!track || track.ogLookBound) return;
    track.ogLookBound = true;
    var box=track.closest('.shopify-section') || d;
    var slides=[].slice.call(track.querySelectorAll('.look-slide')),
        bar=box.querySelector('#lookBar') || d.getElementById('lookBar'),
        prev=box.querySelector('#lookPrev') || d.getElementById('lookPrev'),
        next=box.querySelector('#lookNext') || d.getElementById('lookNext'),
        active=-1, tick=null;

    function tryPlay(v){
      if(!v) return;
      var pr=v.play();
      if(pr && pr.catch) pr.catch(function(){});
    }

    /* Only play what is actually on screen, and retry once the file can decode.
       A single play() call at init loses the race against the video loading. */
    slides.forEach(function(s){
      var v=s.querySelector('video');
      if(v) v.addEventListener('canplay', function(){
        if(s.classList.contains('live') && inView(s)) tryPlay(v);
      });
    });
    function inView(el){
      var r=el.getBoundingClientRect();
      return r.bottom > 0 && r.top < (window.innerHeight||0);
    }
    if('IntersectionObserver' in window){
      var vio=new IntersectionObserver(function(es){
        es.forEach(function(en){
          var v=en.target.querySelector('video'); if(!v) return;
          (en.isIntersecting && en.target.classList.contains('live')) ? tryPlay(v) : v.pause();
        });
      },{threshold:.35});
      slides.forEach(function(s){ if(s.querySelector('video')) vio.observe(s); });
    }

    function centreOf(el){ var r=el.getBoundingClientRect(); return r.left + r.width/2; }

    function sync(){
      var tr=track.getBoundingClientRect(), mid=tr.left + tr.width/2, best=0, dist=1e9;
      all.forEach(function(s,i){
        var dd=Math.abs(centreOf(s)-mid); if(dd<dist){ dist=dd; best=i; }
      });
      if(best!==active){
        active=best;
        all.forEach(function(s,i){
          var live = i===active;
          s.classList.toggle('live', live);
          var v=s.querySelector('video');
          if(v){ live ? tryPlay(v) : v.pause(); }
          if(!live){ var c=s.querySelector('.look-card'); if(c){ c.classList.remove('open');
            var rw=c.querySelector('.look-bar'); if(rw) rw.setAttribute('aria-expanded','false'); } }
        });
        bar.style.width = ((active+1)/all.length*100)+'%';
      }
      ends(steering ? pos : active);
    }

    var settle=null;
    track.addEventListener('scroll', function(){
      if(!tick) tick=requestAnimationFrame(function(){ tick=null; sync(); });
      clearTimeout(settle);
      settle=setTimeout(settled, 140);    /* only once the scroll has stopped */
    });
    /* ---- stepping ------------------------------------------------------
       NO LOOP, by Hashim's call on 16 Oct 2026: the carousel runs from the
       first look to the last and stops there, and the arrow with nowhere to
       go is marked off. It looped from the first build, by copying the looks
       either side, and the copies are where that night's faults lived -- Next
       sliding backwards on two looks, a run of swipes reaching the end of the
       copies, a copied video look showing an empty frame. Without copies there
       is nothing to hop between and nothing to run out of.

       THE ARROWS STEP; THEY DO NOT AIM. `pos` is the look the carousel is on
       or already heading to, and a press is pos plus or minus one, so quick
       presses each count -- reading the slide centred mid-animation instead
       lost them. Once the shopper has moved the strip -- a swipe, a drag --
       the next press starts from where it is on screen. */
    var all = slides;
    var pos = 0, steering = false;
    function step(dir){
      if(!steering){ sync(); pos = active; }
      var to = Math.max(0, Math.min(all.length - 1, pos + dir));
      if(to === pos) return;
      steering = true;
      pos = to;
      ends(pos);
      centreOn(all[pos], true);
    }
    /* aria-disabled rather than disabled: a disabled button drops the focus
       it holds, so a keyboard user pressing Next onto the last look would be
       thrown back to the top of the page. The pair stays in place. */
    function ends(at){
      prev.setAttribute('aria-disabled', String(at <= 0));
      next.setAttribute('aria-disabled', String(at >= all.length - 1));
    }
    /* the strip has stopped: whatever moved it, that is where it is */
    function settled(){
      steering = false;
      sync();
      pos = active;
    }
    function grab(){ steering = false; }
    track.addEventListener('touchstart', grab, {passive:true});
    track.addEventListener('pointerdown', grab, {passive:true});

    prev.addEventListener('click', function(){ step(-1); });
    next.addEventListener('click', function(){ step(1); });

    /* mouse drag; settle on the nearest slide when it ends */
    dragScroll(track, function(){ sync(); pos = active; centreOn(all[pos], true); });

    /* keyboard */
    track.setAttribute('tabindex','0');
    track.setAttribute('role','region');
    track.setAttribute('aria-label','Shop the look');
    track.addEventListener('keydown', function(e){
      if(e.key==='ArrowRight'){ e.preventDefault(); step(root.dir==='rtl' ? -1 : 1); }
      if(e.key==='ArrowLeft'){  e.preventDefault(); step(root.dir==='rtl' ?  1 : -1); }
    });

    /* ---- the list ----
       The bar toggles it; the list rises over the picture inside the same
       card. Only one look is ever open, or scrolling back to a slide you
       left open shows a panel you did not ask for. */
    function shutLooks(except){
      Array.prototype.forEach.call(track.querySelectorAll('.look-card.open'), function(c){
        if(c===except) return;
        c.classList.remove('open');
        var b=c.querySelector('.look-bar'); if(b) b.setAttribute('aria-expanded','false');
      });
    }
    Array.prototype.forEach.call(track.querySelectorAll('.look-bar'), function(bar){
      bar.addEventListener('click', function(){
        var card=bar.closest('.look-card'), open=!card.classList.contains('open');
        shutLooks(card);
        card.classList.toggle('open', open);
        bar.setAttribute('aria-expanded', String(open));
      });
    });

    /* the dimmed picture is the obvious way back out, and on a phone it is a
       far bigger target than the chevron */
    Array.prototype.forEach.call(track.querySelectorAll('.look-media'), function(m){
      m.addEventListener('click', function(e){
        if(e.target.closest('.look-mute')) return;
        var card=m.closest('.look-card');
        if(!card.classList.contains('open')) return;
        card.classList.remove('open');
        var b=card.querySelector('.look-bar'); if(b) b.setAttribute('aria-expanded','false');
      });
    });

    /* every product in a look goes through the one quick view the grids use.
       A row's data-sku is the catalogue's family key, so CATALOG knows every
       product the blob lists and qaOpen opens on the row's colourway. A product
       the blob does not list (it stops at fifty) would make qaOpen return
       early and do nothing, so that row goes to its product page instead. */
    Array.prototype.forEach.call(track.querySelectorAll('.look-item'), function(it){
      it.addEventListener('click', function(e){
        e.preventDefault(); e.stopPropagation();
        var sku = it.dataset.sku;
        if(sku && CATALOG[sku]) { qaOpen(sku, it.dataset.colour); return; }
        if(it.dataset.url) window.location.href = it.dataset.url;
      });
    });

    /* sound */
    Array.prototype.forEach.call(track.querySelectorAll('.look-mute'), function(btn){
      btn.addEventListener('click', function(){
        var v=btn.parentNode.querySelector('video'); if(!v) return;
        v.muted=!v.muted; btn.setAttribute('aria-pressed', String(!v.muted));
      });
    });

    function centreOn(el, smooth){
      var tr=track.getBoundingClientRect(), er=el.getBoundingClientRect();
      var delta=(er.left + er.width/2) - (tr.left + tr.width/2);
      track.scrollTo({left:track.scrollLeft + delta, behavior:smooth?'smooth':'auto'});
    }
    sync();
    track.ogLookSync = sync;
    track.ogLookFlip = function(){ active=-1; sync(); pos = active; };
  }
  mountLook(d);
  onResize(function(){ eachLook(function(t){ t.ogLookSync(); }); });
  /* the track re-centres when direction flips, so re-sync after a language swap */
  window.addEventListener('og:dir', function(){
    setTimeout(function(){ eachLook(function(t){ t.ogLookFlip(); }); }, 60);
  });

  /* ---- scroll reveals ----
     These sections are clipped to zero height until they reveal, which makes
     their intersection rect empty: IntersectionObserver reports ratio 0, so a
     section could never trigger its own reveal. Layout geometry is unaffected
     by the clip, so the check reads bounding rects instead. Three elements,
     so the cost is nil. */
  var rvs=[].slice.call(d.querySelectorAll('.rv'));
  function revealCheck(){
    var h=window.innerHeight||0;
    for(var i=rvs.length-1;i>=0;i--){
      var el=rvs[i];
      /* A detached node measures all zeros, so it can never satisfy the test
         below and would sit in this array being re-measured on every scroll
         for the life of the page. The product gallery replaces its figures on
         every colourway change, so that array only grew. */
      if(!el.isConnected){ rvs.splice(i,1); continue; }
      var r=el.getBoundingClientRect();
      if(r.top < h*0.88 && r.bottom > 0){ el.classList.add('in'); rvs.splice(i,1); }
    }
  }
  revealCheck();
  /* batched, like the ribbon's scroll handler already is */
  var rvTick=null;
  function revealSoon(){
    if(rvTick) return;
    rvTick=requestAnimationFrame(function(){ rvTick=null; revealCheck(); });
  }
  window.addEventListener('scroll', revealSoon, {passive:true});
  onResize(revealCheck);
  window.addEventListener('load', revealCheck);

  /* Built from PRODUCTS further down, not written out again. Two hand-kept
     copies had already drifted: the quick view called the sweatpant colourway
     'wash' where the grid called it 'washed', so the Washed card opened Black;
     it had no 'short' at all, so the Training Short's Quick view button was a
     labelled, focusable control that did nothing; and Violet and Cobalt fell
     through to Blood, taking Blood's photography, Blood's strikethrough price
     and Blood's size availability with them -- offering a sold-out S and
     refusing an XL that was in stock. One list, grouped on the way in. */
  var CATALOG = null;

  var qa=d.getElementById('qa'), qaScrim=d.getElementById('qaScrim'),
      qaFrames=d.getElementById('qaFrames'), qaDots=d.getElementById('qaDots'),
      qaName=d.getElementById('qaName'), qaDesc=d.getElementById('qaDesc'),
      qaPrice=d.getElementById('qaPrice'), qaColours=d.getElementById('qaColours'),
      qaColourName=d.getElementById('qaColourName'), qaSizes=d.getElementById('qaSizes'),
      qaAdd=d.getElementById('qaAdd'), qaAddLabel=d.getElementById('qaAddLabel'),
      qaFull=d.getElementById('qaFull'), qaGuide=d.getElementById('qaGuide');
  var qaSku=null, qaCol=0, qaSize=null;
  /* The reference hardcoded four sizes because its invented catalogue had four.
     This derives them from the catalogue instead, ordered smallest first, with
     anything unrecognised kept in its own order at the end. Today's literal
     catalogue carries only S/M/L/XL, so the result is the same four and the
     change is inert -- it is the seam for the catalogue milestone, when a
     merchant's 2XL has to reach the chips rather than be silently dropped.
     The collection grid and the product page still hold their own literal
     lists; they follow when their milestones land. */
  var ALL_SIZES=['S','M','L','XL'];
  var SIZE_ORDER=['XXS','XS','S','M','L','XL','2XL','XXL','3XL','XXXL','4XL'];
  function sizesFromCatalogue(list){
    /* Keyed by the merchant's own Size option values, so no prototype: on a
       plain literal a size named for an Object member reads back seen on first
       sight and never reaches ALL_SIZES, the size facet or its counts. */
    var seen=Object.create(null), out=[];
    (list||[]).forEach(function(x){
      for(var k in (x.stock||{})){ if(!seen[k]){ seen[k]=1; out.push(k); } }
    });
    if(!out.length) return ALL_SIZES;
    out.sort(function(a,b){
      var ia=SIZE_ORDER.indexOf(a), ib=SIZE_ORDER.indexOf(b);
      if(ia<0 && ib<0) return 0;
      if(ia<0) return 1;
      if(ib<0) return -1;
      return ia-ib;
    });
    return out;
  }
  function isAr(){ return root.dir==='rtl'; }
  /* The JS half of snippets/bi.liquid's contract, enforced the same way it is.

     applyLang() walks every [data-en] and writes innerHTML when the value has a
     '<' in it -- deliberately, so the ribbon and the hero can carry an <em>
     from an inline_richtext setting, which bi.liquid opts into with html: true.
     Its plain branch strip_html's the value FIRST, and that is the enforcement
     rather than tidiness: it makes "a plain value cannot carry a tag" true by
     construction. setBi is the runtime half of the same channel and had no such
     rule, so a product title reading `Scar Compression <img src=x onerror=...>`
     painted as text and then ran on the first press of the language toggle --
     through the cart row, the drawer, the dock and the quick view alike. Strip
     here, at the one function all of them pass through, rather than at the
     twenty-four call sites. */
  function biText(v){ return String(v == null ? '' : v).replace(/<[^>]*>/g, ''); }
  function setBi(el,en,ar){
    en = biText(en); ar = biText(ar);
    el.dataset.en=en; el.dataset.ar=ar; el.textContent = isAr()?ar:en;
  }

  /* Whether this colourway can be bought at all, which for the one product with
     no Size option is a question its empty size list cannot answer. */
  function qaBuyable(c){
    return c.sizeless ? (c.stock1 | 0) > 0 : c.sizes.length > 0;
  }
  function qaRenderSizes(){
    var c=CATALOG[qaSku].colours[qaCol];
    qaSizes.innerHTML='';
    /* No size row for a product that has no sizes. Four disabled chips would
       say "sold out in every size" about a product that is sold in one. */
    if(c.sizeless) return;
    ALL_SIZES.forEach(function(s){
      var b=d.createElement('button');
      b.textContent=s; b.setAttribute('aria-pressed', String(qaSize===s));
      if(c.sizes.indexOf(s)===-1) b.disabled=true;
      else b.addEventListener('click', function(){ qaSize=s; qaRenderSizes(); qaSyncAdd(); });
      qaSizes.appendChild(b);
    });
  }
  function qaSyncAdd(){
    var c=CATALOG[qaSku].colours[qaCol];
    var out=soldOut();
    if(c.sizeless){
      var live=qaBuyable(c);
      qaAdd.disabled=!live;
      setBi(qaAddLabel, live?'Add to bag':out[0],
                        live?'\u0623\u0636\u0641 \u0625\u0644\u0649 \u0627\u0644\u0633\u0644\u0629'
                            :out[1]);
      return;
    }
    if(!c.sizes.length){ qaAdd.disabled=true; setBi(qaAddLabel,out[0],out[1]); return; }
    if(!qaSize){ qaAdd.disabled=true; setBi(qaAddLabel,'Select a size','\u0627\u062e\u062a\u0631 \u0627\u0644\u0645\u0642\u0627\u0633'); return; }
    qaAdd.disabled=false; setBi(qaAddLabel,'Add to bag','\u0623\u0636\u0641 \u0625\u0644\u0649 \u0627\u0644\u0633\u0644\u0629');
  }
  /* gallery ------------------------------------------------------ */
  var qaFrame=0;
  function qaDot(){
    var n=qaDots.children.length;
    Array.prototype.forEach.call(qaDots.children, function(dt,i){
      dt.classList.toggle('on', i===qaFrame);
    });
    /* a class, not an inline display: an inline value outranks the media
       query that hides these arrows below 820px, so resizing a sheet that was
       opened wide left them on screen where the CSS wanted them gone */
    qa.classList.toggle('one-frame', n<=1);
  }
  function buildGallery(p, c){
    var imgs = c.gallery && c.gallery.length ? c.gallery : [c.img];
    qaFrames.innerHTML=''; qaDots.innerHTML='';
    imgs.forEach(function(src, i){
      var im=d.createElement('img');
      var set = cdnSet(src);
      if(set){ im.sizes = SZ_QUICK; im.srcset = set; }
      /* A garment with no colourway name read "Box Cut Wide Leg Sweatpants in
         , view 1"; the colour is said only when there is one to say. */
      im.src=src; im.alt=p.en+(c.en ? ' in '+c.en : '')+', view '+(i+1);
      im.loading = i ? 'lazy' : 'eager';
      qaFrames.appendChild(im);
      qaDots.appendChild(d.createElement('i'));
    });
    qaFrame=0; qaFrames.scrollLeft=0; qaDot();
  }
  function qaGo(step){
    var n=qaFrames.children.length; if(!n) return;
    qaFrame=((qaFrame+step)%n+n)%n;                 /* the gallery loops too */
    /* Both galleries snap on scroll-snap-align:center, and both used to scroll to
       a start-aligned pixel offset (index * clientWidth). The frames are a few px
       narrower than the scroller, so that offset is not a snap point and mandatory
       snapping pulled the scroll back: the arrows moved nothing, in either
       direction, on either page. The look carousel already had the answer --
       measure the two rects and scroll by the difference. Relative, so it needs no
       RTL sign, and it lands exactly on the centre the snap wants. */
    var frame = qaFrames.children[qaFrame];
    if(frame){
      var fr = qaFrames.getBoundingClientRect(), er = frame.getBoundingClientRect();
      qaFrames.scrollTo({
        left: qaFrames.scrollLeft + (er.left + er.width/2) - (fr.left + fr.width/2),
        behavior:'smooth'
      });
    }
    qaDot();
  }
  on(d.getElementById('qaPrev'), 'click', function(){ qaGo(-1); });
  on(d.getElementById('qaNext'), 'click', function(){ qaGo(1); });
  on(qaFrames, 'scroll', function(){
    var w=qaFrames.clientWidth; if(!w) return;
    var i=Math.round(Math.abs(qaFrames.scrollLeft)/w);
    if(i!==qaFrame && i<qaFrames.children.length){ qaFrame=i; qaDot(); }
  }, {passive:true});
  dragScroll(qaFrames);

  /* price lives on the colourway as well as the SKU: the tee is 1,480 from
     1,850 in Blood and Graphite and 1,480 flat in Violet and Cobalt, so a
     SKU-level price would strike through a discount two colourways never had. */
  /* THE SIZE GUIDE LINK FOLLOWS THE PRODUCT, and hides when there is nothing to
     follow. Liquid writes a fallback href on this link and nothing used to
     replace it, so a shopper who opened quick view and tapped "Size guide"
     landed on the shop-all grid -- the only one of the four size-guide surfaces
     that ignored the data. The header, the footer and the search overlay all
     read settings.size_guide_page and mute themselves when it is empty.

     THE COLOURWAY ANSWERS, not the family. og-catalog.liquid resolves a spec
     sheet per product the way main-product.liquid does -- the picked sheet
     first, the family-named one second -- so the flag is on the row the link
     is about, and the two surfaces cannot disagree about whether a #sizing
     band is there to jump to.

     The hash is rebuilt rather than appended: productUrl() falls back to the
     href it is replacing when the catalogue does not know the sku, and that
     href already ends in #sizing by the second call. */
  function qaSyncGuide(c){
    if(!qaGuide) return;
    qaGuide.hidden = !c.chart;
    if(!c.chart) return;
    var url = productUrl(qaSku, c.id, qaGuide.getAttribute('href') || '');
    qaGuide.href = String(url).split('#')[0] + '#sizing';
  }

  function qaPriceHTML(x){
    return (x.was ? '<s>'+x.was+'</s>' : '') + x.price +
           ' <em style="font-style:normal;color:var(--dust)">' + CFG.money.en + '</em>';
  }
  function qaPickColour(i){
    qaCol=i; qaSize=null;
    var p=CATALOG[qaSku], c=p.colours[i];
    buildGallery(p, c);
    qaPrice.innerHTML=qaPriceHTML(c);
    /* the way out of the sheet and into the page, on the colourway being viewed */
    if(qaFull) qaFull.href = productUrl(qaSku, c.id, qaFull.href);
    qaSyncGuide(c);
    setBi(qaColourName, c.en, c.ar);
    Array.prototype.forEach.call(qaColours.children, function(b,n){
      b.setAttribute('aria-pressed', String(n===i));
    });
    qaRenderSizes(); qaSyncAdd();
  }
  /* href is the card's own link, when the caller has one. A colour id can name
     two products -- a duplicate nobody re-coloured -- and since 16 Oct 2026
     each of them is its own card on a listing, so the sheet opens on the
     product the card links to, and on the colour id only when no link says. */
  function qaOpen(sku, colourId, href){
    if(!CATALOG[sku]) return;
    qaSku=sku; qaSize=null;
    var p=CATALOG[sku];
    setBi(qaName, p.en, p.ar);
    setBi(qaDesc, p.descEn||'', p.descAr||'');
    qaPrice.innerHTML=qaPriceHTML(p);
    qaColours.innerHTML='';
    p.colours.forEach(function(c,i){
      var b=d.createElement('button');
      b.style.setProperty('--c', c.hex);
      b.setAttribute('aria-label', c.en);
      b.setAttribute('aria-pressed','false');
      if(!qaBuyable(c)) b.className='gone';
      b.addEventListener('click', function(){ qaPickColour(i); });
      qaColours.appendChild(b);
    });
    var start=0, exact=-1, want=href ? pathOf(href) : '';
    p.colours.forEach(function(c,i){
      if(c.id===colourId) start=i;
      if(want && c.url && pathOf(c.url)===want) exact=i;
    });
    qaPickColour(exact > -1 ? exact : start);
    var qs = qa.querySelector('.qa-scroll');
    if(qs) qs.scrollTop = 0;              /* every sheet opens at its top */
    qa.classList.add('on'); qaScrim.classList.add('on');
    qa.setAttribute('aria-hidden','false'); d.body.classList.add('locked');
    trap(qa);
  }
  function qaShut(){
    if(!qa || !qa.classList.contains('on')) return false;
    qa.classList.remove('on'); qaScrim.classList.remove('on');
    qa.setAttribute('aria-hidden','true'); d.body.classList.remove('locked');
    untrap();
  }
  on(qaScrim, 'click', qaShut);
  /* the overlay fills the viewport around the panel, so a press that
     lands on it rather than on a child is a press outside */
  on(qa, 'pointerdown', function(e){ if(e.target===qa) qaShut(); });
  on(d.getElementById('qaClose'), 'click', qaShut);
  on(qaAdd, 'click', function(){
    if(qaAdd.disabled) return;
    /* captured before qaShut, which is free to reset the sheet's state */
    var c = CATALOG[qaSku] && CATALOG[qaSku].colours[qaCol];
    var sku=qaSku, colour=c && c.id, size=qaSize;
    qaShut();
    bagAdd(sku, colour, size, 1);
  });

  /* Size chips on the drop cards are real controls, so a configuration can be
     completed without opening anything. */
  var wide = function(){ return window.matchMedia('(min-width:820px) and (hover:hover)').matches; };
  function cardBtnState(card){
    var btn=card.querySelector('.pc-add'); if(!btn) return;
    var label=btn.querySelector('.pc-add-label');
    var sizes=card.querySelector('.sizes');
    if(!sizes || !wide()){                       /* no options on the card: quick view */
      btn.disabled=false;
      if(label) setBi(label,'Quick view','\u0639\u0631\u0636 \u0633\u0631\u064a\u0639');
      return;
    }
    var picked=sizes.querySelector('button[aria-pressed="true"]');
    btn.disabled=!picked;
    if(label) picked ? setBi(label,'Quick add','\u0623\u0636\u0641 \u0644\u0644\u0633\u0644\u0629')
                     : setBi(label,'Select a size','\u0627\u062e\u062a\u0631 \u0627\u0644\u0645\u0642\u0627\u0633');
  }
  /* Size chips and quick-add are delegated from the document rather than bound
     per element. The theme editor re-renders one section at a time, and a
     re-rendered card would arrive with no listeners at all -- delegation makes
     that impossible rather than something a mount step has to remember. */
  d.addEventListener('click', function(e){
    var b = e.target.closest && e.target.closest('.card .sizes button');
    if(!b || b.disabled) return;
    e.preventDefault(); e.stopPropagation();
    var g = b.closest('.sizes');
    var on = b.getAttribute('aria-pressed')==='true';
    Array.prototype.forEach.call(g.querySelectorAll('button'), function(o){
      o.setAttribute('aria-pressed','false'); });
    b.setAttribute('aria-pressed', String(!on));
    cardBtnState(g.closest('.card'));
  });

  /* every quick-add entry point goes through the sheet */
  d.addEventListener('click', function(e){
    var btn = e.target.closest && e.target.closest('.pc-add');
    if(!btn) return;
    e.preventDefault(); e.stopPropagation();
    var host=btn.closest('[data-sku]'); if(!host) return;
    if(!CATALOG[host.dataset.sku]){
      var away=host.querySelector('.card-link');
      if(away && away.href) window.location.href = away.href;
      return;
    }
    var colour=host.dataset.colour;
    var sw=host.querySelector('.card-swatch button[aria-pressed="true"]');
    if(sw && sw.dataset.colour) colour=sw.dataset.colour;
    /* the card already carries a complete configuration, so skip the modal */
    var sizes=host.querySelector('.sizes');
    var picked=sizes && sizes.querySelector('button[aria-pressed="true"]');
    if(wide() && picked){
      bagAdd(host.dataset.sku, colour, picked.textContent.trim(), 1);
      return;
    }
    var own=host.querySelector('.card-link');
    qaOpen(host.dataset.sku, colour, own && own.getAttribute('href'));
  });

  /* Recompute only: the quick-add label depends on the breakpoint, so it has to
     be re-derived whenever the viewport changes. It reads state, never clears it. */
  function cardStates(root){
    Array.prototype.forEach.call((root || d).querySelectorAll('.card, .pc'), cardBtnState);
  }
  /* What delegation cannot do: put a card into its starting state. Idempotent,
     so a re-render can call it again on just the new subtree.

     Kept separate from cardStates because this one CLEARS the size a shopper
     picked. Wired to resize it undid the selection on every rotate and on every
     mobile URL-bar collapse, which is not a re-render. */
  function mountCards(root){
    root = root || d;
    Array.prototype.forEach.call(root.querySelectorAll('.card .sizes button'), function(b){
      b.setAttribute('aria-pressed','false');
    });
    /* A card whose colourway has no price shows a restock line the section
       rendered from its own settings. Swatching to a priced colourway and back
       destroys it, so the element is kept here -- at load and again after an
       editor re-render, which is when the merchant's text is on the page. */
    Array.prototype.forEach.call(root.querySelectorAll('.card'), function(card){
      var line = card.querySelector('.price [data-en]');
      if(line) card.ogRestock = line.cloneNode(true);
    });
    cardStates(root);
  }
  mountCards(d);
  window.addEventListener('resize', function(){ cardStates(d); });

  /* The gap card fills the remainder of its row. The column count comes from
     the resolved grid, so it follows the breakpoint rather than duplicating it. */
  function fillGap(){
    Array.prototype.forEach.call(d.querySelectorAll('.rail'), function(rail){
      var gapCard=rail.querySelector('.card-all');
      if(!gapCard) return;
      var cols=getComputedStyle(rail).gridTemplateColumns.split(' ').filter(Boolean).length;
      var items=rail.querySelectorAll('.card:not(.card-all)').length;
      var used=items % cols;
      /* Fill the leftover of the row the products end on -- that is the whole
         point of this cell. When they end exactly on a column boundary there is
         no leftover and the cell starts a fresh row. On a three- or four-column
         grid spanning that whole row turned it into a full-width empty band
         under the rail, so there it is just another card. On two columns it
         fills the row (Hashim, 6 Oct 2026, on a phone: "this should fill the
         row on mobile") -- one card's width beside an empty one read as a gap,
         not as a destination. */
      gapCard.style.gridColumn = 'span ' + (used===0 ? (cols <= 2 ? cols : 1) : cols-used);
    });
  }
  fillGap();
  onResize(fillGap);

  /* ---- category filter ----
     One set of cards, shown or hidden by the categories they carry.
     The button label follows the tab so it always names a real place. */
  /* Scoped to the tab's own section, not the document.
     These were snapshot arrays of every .cat-tab and every .pc on the page, which
     meant one section's tabs hid another section's cards, and a re-render left
     both arrays pointing at detached nodes. Querying per call fixes both. */
  function catScope(tab){ return tab.closest('.shopify-section') || d; }
  /* Which real collection each tab means, published by home-categories.liquid.
     Read from the tab's OWN section and read on every call: that section ships
     a preset, so a merchant can add a second one and both emit
     id="og-categories" -- a document-wide lookup answers with the first, and
     the second section's Tops button filters its own cards and then navigates
     to the other section's collection. Everything else here already resolves
     through catScope for that reason. Reading per call also means a theme
     editor edit is picked up: the section arrives as a new subtree carrying a
     new blob, and a map read once at boot would describe the old setting. */
  function catUrls(scope){
    try{
      var el = (scope || d).querySelector('#og-categories');
      var raw = el ? JSON.parse(el.textContent) : null, out = Object.create(null), k;
      for(k in raw) out[k] = raw[k];
      return out;
    }catch(e){ return Object.create(null); }
  }
  function filterCats(tab){
    var scope=catScope(tab), key=tab.dataset.cat, shown=0;
    Array.prototype.forEach.call(scope.querySelectorAll('.cat-tab'), function(o){
      o.setAttribute('aria-selected', String(o===tab)); });
    Array.prototype.forEach.call(scope.querySelectorAll('.pc'), function(p){
      /* a card with no data-cat used to throw here and abort the rest of this
         file -- the language toggle and the menu went with it */
      var hit = (p.dataset.cat||'').split(' ').indexOf(key) !== -1;
      p.classList.toggle('hide', !hit);
      if(hit){
        p.style.setProperty('--i', shown++);
        p.classList.remove('anim'); void p.offsetWidth; p.classList.add('anim');
      }
    });
    var catCta=scope.querySelector('#catCta') || d.getElementById('catCta');
    if(!catCta) return;
    setBi(catCta, tab.dataset.ctaEn, tab.dataset.ctaAr);
    /* the label already followed the tab; the href did not, so the button
       named a collection and went nowhere */
    /* The tab's own destination, then the section's published map, then a
       handle built from the filter word -- which is the one that 404s, because
       'new', 'tops' and 'seamless' are filter words and not collections on
       this store. */
    var ctaLink = catCta.closest('a');
    if(ctaLink) ctaLink.href = catUrls(scope)[key] || collectionUrl(key);
  }
  d.addEventListener('click', function(e){
    /* The home band's tabs filter in place: the live band and the frozen home
       fixture both render them as buttons, as the reference does (Hashim, 5 Oct
       2026). button.cat-tab rather than .cat-tab so a category written as a
       LINK anywhere else stays a link -- the menu and the footer send a
       category to the shop page, and this must never fire mid-navigation. */
    var tab = e.target.closest && e.target.closest('button.cat-tab');
    if(!tab) return;
    filterCats(tab);
    /* keep a tapped tab in view, but only ever inside its own row */
    var row=tab.parentNode, tr=row.getBoundingClientRect(), br=tab.getBoundingClientRect();
    if(br.left<tr.left+8) row.scrollBy({left:br.left-tr.left-16,behavior:'smooth'});
    else if(br.right>tr.right-8) row.scrollBy({left:br.right-tr.right+16,behavior:'smooth'});
  });

  /* edge fades follow the scroll position; RTL reports scrollLeft as negative */
  function tabFade(row){
    if(!row) return;
    var max=row.scrollWidth-row.clientWidth, x=Math.abs(row.scrollLeft);
    var none=max<=2, atStart=x<=2, atEnd=x>=max-2;
    row.classList.toggle('fade-end',  !none && atStart);
    row.classList.toggle('fade-start',!none && atEnd);
    row.classList.toggle('fade-both', !none && !atStart && !atEnd);
  }
  function mountTabRows(scope){
    Array.prototype.forEach.call((scope||d).querySelectorAll('.cat-tabs'), function(row){
      if(!row.ogTabsBound){
        row.ogTabsBound = true;
        dragScroll(row, function(){ tabFade(row); });
        row.addEventListener('scroll', function(){ tabFade(row); }, {passive:true});
      }
      tabFade(row);
    });
  }
  mountTabRows(d);
  onResize(function(){ mountTabRows(d); });
  /* Select the first tab in each tab row, per section. Idempotent, so a
     re-rendered section can be brought back to its starting state. */
  function mountCats(root){
    root = root || d;
    Array.prototype.forEach.call(root.querySelectorAll('.cat-tabs'), function(row){
      var first = row.querySelector('.cat-tab');
      if(first) filterCats(first);
    });
  }
  mountCats(d);

  /* ---- colourway switching ----
     Swapping the swatch swaps the mounted image pair and rewrites the
     colour label, including its translations so a later language flip
     still shows the right name. */
  /* One swatch is not a choice: CSS already stops it answering a pointer, but
     it stayed in the tab order, so a keyboard user landed on a control that
     could not be activated. Idempotent, so a re-render can call it again. */
  function mountSwatches(root){
    root = root || d;
    Array.prototype.forEach.call(root.querySelectorAll('.card-swatch'), function(g){
      if(g.children.length === 1) g.children[0].tabIndex = -1;
    });
  }
  mountSwatches(d);

  d.addEventListener('click', function(e){
    var b = e.target.closest && e.target.closest('.card-swatch button');
    if(b) cardPick(b);
  });
  /* Named so fillCards() can move a card off a sold-out colour through the
     same code a shopper's click runs, rather than by faking the click. */
  function cardPick(b){
    var g = b.closest('.card-swatch');
    var card = g.closest('.card'); if(!card) return;
    var label = card.querySelector('.cw-label');
    var i = b.dataset.cw;
    Array.prototype.forEach.call(g.children, function(o){
      o.setAttribute('aria-pressed', String(o===b));
    });
    Array.prototype.forEach.call(card.querySelectorAll('.card-media img'), function(im){
      im.classList.toggle('on', im.dataset.cw===i);
    });
    card.classList.remove('swapped');   /* show the front of the new colour */
    if(label) setBi(label, b.dataset.nameEn, b.dataset.nameAr);
    /* Everything below used to keep the first colourway's state. Picking
       Graphite on the sold-out Guard Long Sleeve kept the Sold out badge,
       kept "Back Thursday" where the price belongs and kept all four sizes
       struck through -- on a colourway with ten units in stock. Each swatch
       now carries its own colourway's whole card state. */
    if(b.dataset.colour) card.dataset.colour = b.dataset.colour;
    var link = card.querySelector('.card-link');
    if(link && card.dataset.sku)
      link.href = productUrl(card.dataset.sku, b.dataset.colour, link.href);

    var tag = card.querySelector('.card-tag');
    if(tag){
      var ten = b.dataset.tagEn || '';
      tag.hidden = !ten;
      tag.className = 'card-tag' + (b.dataset.tagCls ? ' ' + b.dataset.tagCls : '');
      setBi(tag, ten, b.dataset.tagAr || '');
    }
    var price = card.querySelector('.price');
    if(price && b.dataset.price !== undefined){
      if(b.dataset.price){
        /* The mark is whatever the section rendered into this card, not a
           language choice made here. Every price on the page is server-rendered
           with one mark in both languages, so substituting a different one on
           click gave the clicked card a currency the other eight did not have --
           and the rewritten <em> carries no data-en/data-ar pair, so the
           language toggle could never take it back.

           A card in the restock state has no <em> of its own -- the branch
           below writes a bare <span> -- so the mark comes from another price on
           the page rather than from the setting the server-rendered prices do
           not use. */
        var markEl = price.querySelector('em') || d.querySelector('.price em');
        var mark = markEl ? markEl.textContent : CFG.money.en;
        price.innerHTML = (b.dataset.was ? '<s>'+b.dataset.was+'</s>' : '') +
          b.dataset.price + ' <em style="font-style:normal;color:var(--dust)">' +
          mark + '</em>';
      } else {
        /* Put back the line the section rendered, not a literal written here.
           A hardcoded pair overwrote the merchant's copy on the first swatch
           click; the card's own element is cloned back in, so the text and both
           language attributes survive. Nothing is written when the card never
           carried a restock line -- leaving what is there beats inventing it. */
        var restock = card.ogRestock;
        if(restock){
          var line = restock.cloneNode(true);
          line.textContent = root.dir==='rtl'
            ? (line.dataset.ar || line.dataset.en) : line.dataset.en;
          price.innerHTML = '';
          price.appendChild(line);
        }
      }
    }
    var sizes = card.querySelector('.sizes');
    if(sizes && b.dataset.sizes !== undefined){
      var live = b.dataset.sizes ? b.dataset.sizes.split(',') : [];
      Array.prototype.forEach.call(sizes.children, function(z){
        var ok = live.indexOf(z.textContent.trim()) > -1;
        z.classList.toggle('gone', !ok);
        z.disabled = !ok;
        z.removeAttribute('aria-pressed');
      });
    }
    cardBtnState(card);
    swipeSync(card);
  }

  /* ---- touch affordances ----
     Replaces the two hover behaviours that carry real content:
     the second product shot and the quick-add button. */
  var lio=null;
  function mountTouch(scope){
    if(!touch) return;
    scope = scope || d;
    Array.prototype.forEach.call(scope.querySelectorAll('.card'), function(card){
      if(card.ogTouchBound) return;
      var media=card.querySelector('.card-media');
      /* Marked bound only once there is a back to swap to. It was marked first,
         so a card whose lead colour had one photo was passed over for good --
         and fillCards() mounts its other colours' backs AFTER this has run. */
      if(!media || !media.querySelector('.back')) return;
      card.ogTouchBound = true;
      var dots=d.createElement('div');
      dots.className='card-dots';
      dots.setAttribute('aria-hidden','true');
      dots.innerHTML='<i></i><i></i>';
      media.appendChild(dots);
      media.addEventListener('click', function(e){
        if(e.target.closest('.pc-add')) return;
        card.classList.toggle('swapped');
      });
    });
    /* run the raking light once as each card arrives, since nothing hovers */
    if('IntersectionObserver' in window){
      if(!lio) lio=new IntersectionObserver(function(es){
        es.forEach(function(en){
          if(en.isIntersecting){ en.target.classList.add('lit'); lio.unobserve(en.target); }
        });
      },{threshold:.45});
      Array.prototype.forEach.call(scope.querySelectorAll('.tile, .card, .pc')  /* .lit also drives card reveals */, function(el){ lio.observe(el); });
    }
  }
  mountTouch(d);

  /* ---- search overlay ----
     Three groups in one panel: suggested queries, products, then support pages.
     The third group is the cheap fix for non-product queries, which fail on two
     thirds of sites and are exactly what an unsure buyer types here. */
  var srch=d.getElementById('srch'), srchIn=d.getElementById('srchInput'),
      srchOut=d.getElementById('srchResults'), sel=-1;
  /* Everything below builds rows with innerHTML, and the strings are now a
     merchant's copy and a product's title rather than literals in this file.
     The old code interpolated the raw query and stripped only <>& from it,
     which is a filter, not an escape. This is the escape. */
  function esc(v){
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function srchOpen(){
    if(!srch) return;
    srch.classList.add('on'); srch.setAttribute('aria-hidden','false');
    var sb=d.getElementById('searchBtn'); if(sb) sb.setAttribute('aria-expanded','true');
    d.body.classList.add('locked');
    srchRender(srchIn.value);
    trap(srch);
    /* a search overlay that does not focus its field is a search overlay you
       have to click into before you can type */
    setTimeout(function(){ try{ srchIn.focus(); srchIn.select(); }catch(e){} }, 90);
  }
  function srchShut(){
    if(!srch || !srch.classList.contains('on')) return false;
    srch.classList.remove('on'); srch.setAttribute('aria-hidden','true');
    var sb2=d.getElementById('searchBtn'); if(sb2) sb2.setAttribute('aria-expanded','false');
    srchIn.removeAttribute('aria-activedescendant');
    d.body.classList.remove('locked');
    srchIn.setAttribute('aria-expanded','false');
    untrap();
  }
  /* All four routes ship unset, because none of these pages exists on the store
     yet. Dropping the rows made the whole support group vanish and left the
     no-results branch offering nothing; they render with the same .soon
     affordance soon.liquid gives an unbuilt destination everywhere else, so
     search still finds them and the row says "not yet" rather than lying. */
  function helpRow(h, ar){
    var el = row(h.href ? '' : 'soon',
      '<span class="srch-q">'+(ar?h.ar:h.en)+'</span><svg class="ic arw" aria-hidden="true"><use href="#ph-arrow-right"/></svg>',
      h.href);
    if(!h.href) el.setAttribute('aria-disabled','true');
    return el;
  }
  function row(cls, html, href, act){
    var el = d.createElement(href ? 'a' : 'button');
    if(href) el.href = href;
    el.className = 'srch-item ' + cls;
    el.setAttribute('role','option');
    el.innerHTML = html;
    if(act) el.addEventListener('click', act);
    return el;
  }
  /* #srchResults is a role="listbox", and a listbox may only contain `option`
     and `group`. Its children were plain <div>s wrapping an <h5>, which
     plan/design-system.md has carried under "Still missing" since the overlay
     was written: aria-activedescendant pointed into a structure a screen reader
     could not walk. The group is labelled BY the heading rather than duplicating
     it in an aria-label, so the visible text and the announced name cannot
     drift. No pixel moves -- these are attributes on elements that already
     existed. */
  var groupSeq = 0;
  function group(label){
    var g=d.createElement('div'); g.className='srch-group';
    var h=d.createElement('h5'); h.className='t-editorial'; h.textContent=label;
    h.id = 'srch-grp-' + (++groupSeq);
    g.setAttribute('role','group');
    g.setAttribute('aria-labelledby', h.id);
    g.appendChild(h); return g;
  }
  function money(n){
    /* TWO DIGITS OR NONE, NEVER ONE. The catalogue publishes the exact price now
       rather than a rounded one, and toLocaleString's own default -- up to three
       fraction digits, no minimum -- renders 1,480.50 as "1,480.5". A whole
       price must not grow a ".00" the design never had either, so the count is
       read off the value. Every price on this store is whole, so this is
       byte-identical to what it rendered before on all of them.

       The locale is a theme setting so switching the whole site to Arabic-Indic
       digits is a settings change, not a code change. en-US keeps it a no-op. */
    var v = Number(n);
    if(!isFinite(v)) v = 0;
    var frac = v % 1 === 0 ? 0 : 2;
    var opts = {minimumFractionDigits:frac, maximumFractionDigits:frac};
    try{ return v.toLocaleString(CFG.money.locale || 'en-US', opts); }
    catch(e){ return v.toLocaleString('en-US', opts); }
  }
  /* Copy comes from Theme settings > Search, published through og-config. The
     group headings, the popular list and the empty state used to be string
     literals in this function, which meant the one part of the site a shopper
     reaches when they are already lost was the one part nobody could edit. */
  function sCopy(k, ar){
    var c = (CFG.search || {})[k];
    if(!c) return '';
    return (ar ? c.ar : c.en) || c.en || '';
  }
  function srchRender(q){
    if(!srchOut) return;
    srchOut.innerHTML=''; sel=-1;
    var ar = isAr();
    if(!norm(q)){
      var pop = (CFG.search && CFG.search.popular) || [];
      if(pop.length){
        var g0 = group(sCopy('popularLabel', ar) || (ar ? 'الأكثر بحثًا' : 'Popular'));
        pop.forEach(function(t){
          var label = (ar ? t.ar : t.en) || t.en;
          g0.appendChild(row('', '<span class="srch-q">'+esc(label)+'</span><svg class="ic arw" aria-hidden="true"><use href="#ph-arrow-right"/></svg>',
            searchUrl(t.en)));   /* always search the English term: the index is English */
        });
        srchOut.appendChild(g0);
      }
      srchIn.setAttribute('aria-expanded','true');
      return;
    }
    var r = ogSearch(q);
    if(r.queries.length){
      var g1 = group(sCopy('suggestLabel', ar) || (ar ? 'اقتراحات' : 'Suggestions'));
      r.queries.slice(0,5).forEach(function(t){
        g1.appendChild(row('', '<span class="srch-q">'+esc(t)+'</span><svg class="ic arw" aria-hidden="true"><use href="#ph-arrow-right"/></svg>',
          searchUrl(t)));
      });
      srchOut.appendChild(g1);
    }
    if(r.products.length){
      var g2 = group(sCopy('productsLabel', ar) || (ar ? 'منتجات' : 'Products'));
      /* one row per colour, as on the search page and the grid it hands over to */
      r.products.slice(0,4).forEach(function(p){
        /* A row with no image keeps the thumbnail box: dropping it would shift
           the text of that one row out of line with the others. thumb, not img:
           og-catalog publishes this 42x52 thumbnail at 160 (sharp at 3x) and the
           grid card's at 520, and searchRows() hands over the raw blob row. */
        var tsrc = p.thumb || p.img;
        var thumb = (tsrc && tsrc !== BLANK_PX)
          ? '<span class="srch-thumb ch-sm"><img src="'+esc(tsrc)+'" alt="" loading="lazy"></span>'
          : '<span class="srch-thumb ch-sm"></span>';
        g2.appendChild(row('',
          thumb +
          '<span class="srch-info"><b>'+esc(ar?p.ar:p.en)+'</b><u>'+esc(ar?p.cAr:p.cEn)+'</u></span>'+
          '<span class="srch-price">'+money(p.price)+'</span>',
          p.url || productUrl(p.sku, p.colour)));
      });
      srchOut.appendChild(g2);
    }
    if(r.help.length){
      var g3 = group(sCopy('helpLabel', ar) || (ar ? 'المساعدة' : 'Help'));
      r.help.forEach(function(h){
        g3.appendChild(helpRow(h, ar));
      });
      srchOut.appendChild(g3);
    }
    if(!r.products.length && !r.help.length && !r.queries.length){
      var none=d.createElement('div'); none.className='srch-none';
      var head = sCopy('noneHead', ar) || (ar ? 'لا نتائج لـ' : 'Nothing for');
      var body = sCopy('noneBody', ar) || (ar ? 'جرب مقاسًا أو لونًا.'
                                              : 'Try a size or a colour.');
      none.innerHTML = '<b>'+esc(head)+' “'+esc(q)+'”</b>'+esc(body);
      srchOut.appendChild(none);
      HELP.forEach(function(h){ srchOut.appendChild(helpRow(h, ar)); });
    }
    srchIn.setAttribute('aria-expanded','true');
  }
  if(srch){
    /* #searchBtn and #menuSearchBtn are header markup, so they are replaced by
       a header section swap and bind in mountHeader() instead. srchOpen and
       srchShut set aria-expanded on #searchBtn by id, so nothing here needs a
       handle to it. */
    on(d.getElementById('srchClose'), 'click', srchShut);
    srch.addEventListener('pointerdown', function(e){ if(e.target===srch) srchShut(); });
    srchIn.addEventListener('input', function(){ srchRender(srchIn.value); });
    /* arrows walk all three groups as one list, which is what the eye does */
    srchIn.addEventListener('keydown', function(e){
      var items=[].slice.call(srchOut.querySelectorAll('.srch-item'));
      if(e.key==='ArrowDown' || e.key==='ArrowUp'){
        if(!items.length) return;
        e.preventDefault();
        /* plain wrap in both directions. The old expression could not produce a
           negative, so the wrap-to-last branch under it was unreachable and
           ArrowUp on the first row did nothing. */
        sel = (sel + (e.key==='ArrowDown' ? 1 : -1) + items.length) % items.length;
        items.forEach(function(el,i){
          el.classList.toggle('sel', i===sel);
          if(!el.id) el.id = 'srch-opt-' + i;
        });
        items[sel].scrollIntoView({block:'nearest'});
        /* the highlight was purely visual: a combobox has to say which option
           is active or a screen reader follows none of this */
        srchIn.setAttribute('aria-activedescendant', items[sel].id);
      } else if(e.key==='Enter'){
        e.preventDefault();
        if(sel > -1 && items[sel]) items[sel].click();
        else if(norm(srchIn.value)) location.href=searchUrl(srchIn.value);
      }
    });
    /* the placeholder is a data-ph-* pair now, so only the results redraw */
    window.addEventListener('og:dir', function(){ setTimeout(function(){
      srchRender(srchIn.value);
    }, 20); });
  }

  /* the language machinery is at the top of this file, because it has to run
     before anything measures a rect or builds a roll-up */
  /* Both toggles are header markup and bind in mountHeader(), which re-binds
     them after a section swap. Binding here as well would double-fire and
     toggle the language straight back. */
  remeasureOnFonts();

  /* ---- newsletter ----
     It was a bare <button> in no <form> with no handler: the one control on the
     site that visibly did nothing, and its input had no name attribute, so even
     a native submit would have posted an empty field. It is a real
     {% form 'customer' %} now, which is what Shopify needs to create a customer
     with accepts_marketing set -- the input must be named contact[email] and
     nothing else will do.

     Enhanced, not replaced. Without JS the form posts natively and the page
     reloads with ?customer_posted=true, which still works. With JS the same
     request goes out through fetch so the reply lands in #subMsg and the reader
     keeps their place -- the design put a role="status" line under the field for
     exactly that and nothing had ever written a server's answer into it.

     Success is read off the redirect, not the HTML: Shopify answers a good
     submission by redirecting back with customer_posted=true, so there is no
     markup to parse and no locale to fight. A 422's message would come back in
     the SHOP's language, which is the same reason cartRow() stays in
     JavaScript, so it is never shown -- the address is checked here first and
     anything else is reported as a failure to send.

     Re-mountable: the footer is a section, so the theme editor replaces this
     form wholesale and a listener bound to the old one is left on a detached
     node. */
  var subBusy = false;
  function mountFooter(){
    var f=d.getElementById('subForm'), i=d.getElementById('subInput'), m=d.getElementById('subMsg');
    if(!f || !i || !m) return;

    /* Section-local copy, because a section cannot reach og-config. Missing or
       malformed, this falls back to the shipped strings rather than throwing --
       the whole file is one IIFE and a throw here would take the language
       toggle down with it. */
    var C = {okEn:'Thanks. You are on the list.', okAr:'شكرًا. أنت ضمن القائمة.',
             badEn:'That does not look like an email address.', badAr:'هذا لا يبدو بريدًا إلكترونيًا صحيحًا.',
             errEn:'That did not go through. Try again in a moment.', errAr:'لم يتم الإرسال. حاول مرة أخرى بعد قليل.'};
    try{
      var raw = d.getElementById('og-footer');
      if(raw){
        var got = JSON.parse(raw.textContent).sub || {};
        for(var k in got){ if(got[k]) C[k] = got[k]; }
      }
    }catch(e){}

    function say(en, ar, ok){
      m.hidden = false;
      m.classList.toggle('ok', !!ok);
      setBi(m, en, ar);
    }

    f.addEventListener('submit', function(e){
      e.preventDefault();
      if(subBusy) return;
      if(!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(i.value.trim())){
        say(C.badEn, C.badAr, false);
        i.focus();
        return;
      }
      /* urlencoded, not FormData: that is what the native form would send, and
         it is the one shape this endpoint is certain to accept. */
      var body;
      try{ body = new URLSearchParams(new FormData(f)).toString(); }
      catch(err){ f.submit(); return; }   /* no URLSearchParams -- let the browser do it */
      subBusy = true;
      fetch(f.action, {
        method: 'POST',
        body: body,
        headers: {'Content-Type':'application/x-www-form-urlencoded', 'Accept':'text/html'},
        credentials: 'same-origin'
      }).then(function(r){
        if(r.url && r.url.indexOf('customer_posted=true') > -1){
          say(C.okEn, C.okAr, true);
          f.reset();
        } else {
          say(C.errEn, C.errAr, false);
        }
      }).catch(function(){
        say(C.errEn, C.errAr, false);
      }).then(function(){ subBusy = false; });
    });
  }
  mountFooter();

  /* One shared entry point, so a second page can add to the bag and write a
     bilingual label without reaching into this closure or keeping its own copy
     of the counter. Everything here already existed; this only names it. */
  /* ---- the real catalogue -----------------------------------------
     og-catalog.liquid publishes the store's own products. This maps a row of
     that onto the shape PRODUCTS has always had, so productFor, stockFor,
     productUrl, catalogFrom, the bag and the collection grid all keep working
     and not one of them has to learn where the data came from.

     id is the product handle: unique per row, and what a variant lookup will
     want when the cart becomes real. hex is present whenever the colourway is
     in the shop's colour list, because og-catalog.liquid resolves that list for
     every row -- by the merchant's pick or by the colourway's name -- so one
     colourway carries one hex across every row and cannot show two different
     colours on one page. It is absent for a colour in no list, and swatchHex
     then falls through to the theme palette. sku and colour come from the
     title, never the handle: six of nineteen handles on this store contradict
     their own titles.

     A malformed payload returns null and the invented array below stands in. A
     broken blob should cost the page its catalogue, not its JavaScript. */
  var OG_BLOB = (function(){
    try{
      var raw = d.getElementById('og-catalog');
      if(!raw) return null;
      var parsed = JSON.parse(raw.textContent);
      if(!parsed || !parsed.products || !parsed.products.length) return null;
      /* Not silent: a catalogue past the Liquid for-loop cap is half a shop. */
      if(CFG.designMode && parsed.listed < parsed.total && window.console)
        console.warn('[OG] catalogue covers ' + parsed.listed + ' of ' + parsed.total +
                     ' products — the Liquid loop caps at 50.');
      /* og-catalog.liquid's collapse detector, reported. Design mode only, the
         way mountSection's try/catch is: both name a thing only the merchant
         can fix, in the one place they are already looking at the theme. The
         handles are named because "some products collide" is not actionable. */
      if(CFG.designMode && window.console){
        if(parsed.dupes && parsed.dupes.length)
          console.warn('[OG] two or more products share one family + colourway key, so ' +
                       'only the first is reachable and the others sell it: ' +
                       parsed.dupes.join(', ') +
                       ' — give each a distinct title tail, or set custom.family.');
        if(parsed.odd && parsed.odd.length)
          console.warn('[OG] title carries more than one " - " and names no colour list ' +
                       'entry, so it reads as having no colourway: ' + parsed.odd.join(', ') +
                       ' — pick a colourway on it, or leave one " - " in the title.');
      }
      return parsed;
    }catch(e){ return null; }
  })();
  /* Declared here rather than beside adoptLine(), which is its other caller:
     rowsFromBlob() runs at file scope some two hundred lines above that, where
     a var is hoisted but still undefined. Both paths must agree, because both
     write what they choose straight into a src. */
  var BLANK_PX = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  function rowsFromBlob(){
    if(!OG_BLOB) return null;
    var out = [];
    OG_BLOB.products.forEach(function(x){
      if(!x || !x.sku) return;
      var row = {
        id:x.h, sku:x.sku, colour:x.colour || 'default',
        en:x.en || '', ar:x.ar || x.en || '',
        cEn:x.cEn || '', cAr:x.cAr || x.cEn || '',
        /* The swatch of the colourway entry the merchant picked, or '' when
           nobody has picked one -- and '' is load-bearing, because swatchHex
           now reads this tier FIRST and a falsy value is what lets it fall
           through to the theme palette. Copied here for the reason CLAUDE.md
           records: a field the row mapper does not copy is a field that does
           not exist, and og-catalog publishing it changes nothing until this
           line exists. */
        hex:x.hex || '',
        /* A field the row mapper does not copy is a field that does not exist:
           publishing "sort" in og-catalog.liquid changed nothing until this
           line. `sort` is a NUMBER whose zero is meaningful -- typing 0 means
           first -- so it is normalised to null rather than coerced, and
           catalogFrom() below treats null as "no opinion". */
        sort:(typeof x.sort === 'number') ? x.sort : null,
        price:+x.price || 0, was:+x.was || 0,
        cats:x.cats || [], order:+x.order || 0, created:+x.created || 0,
        /* og-catalog publishes null for the two products with no featured
           image, and card() and cartRow() write this straight into a src --
           where '' re-requests the DOCUMENT as an image. Same pixel adoptLine()
           falls back to, so a product without a photo looks the same wherever
           it is drawn. */
        img:x.img || BLANK_PX, thumb:x.thumb || x.img || BLANK_PX, url:x.url || '',
        /* The second photo, which a rail card shows on hover. NEVER the blank
           pixel: fillCards() mounts whatever is here as a back, and a 1px
           transparent back is a card that goes blank under the pointer. Null
           means "no back", and the card keeps its front. */
        img2:x.img2 || null,
        /* Every photograph of the product, featured first: the quick view's
           gallery and a phone card's swipe. A field the row mapper does not copy
           is a field that does not exist, so og-catalog publishing it changes
           nothing until this line. Null below two -- one frame is nothing to
           page through -- and on the parity fixture, which never carries it. */
        imgs:(x.imgs && x.imgs.length > 1) ? x.imgs : null,
        sizes:x.sizes || [], stock:x.stock || {},
        /* Whether this product's page renders a measurements band, resolved in
           Liquid from the sheet the merchant picked and then from the one named
           after the family. A field the row mapper does not copy is a field
           that does not exist, so the quick view's size-guide link stays hidden
           until this line carries the flag through. */
        chart:x.chart === true,
        /* The bag's half of the row. Without these three the variant index is
           empty, every cart line falls through to adoptLine()'s fallback, and
           the page still renders -- which is exactly why this was worth a probe
           rather than a look: the parity lane came back with three correct rows
           and twelve products where nine were expected.

           Never `|| 0` a vid map either; an empty object is falsy in neither
           direction here, but `+x.v1` on a fourteen-digit id is fine while
           `x.v1 | 0` would truncate it to 32 bits. */
        vid:x.vid || {}, v1:+x.v1 || 0, stock1:+x.stock1 || 0
      };
      /* Only when the blob actually said. Coercing a missing avail to false
         marks every row sold out, which is exactly what it did to the parity
         fixture -- those rows omit it on purpose so inStock falls back to
         reading their size map. */
      if(typeof x.avail === 'boolean') row.avail = x.avail;
      out.push(row);
    });
    return out.length ? out : null;
  }

  /* ============================================================
     PRODUCT INDEX
     One list, shared. The collection grid, the search and anything
     that comes later read this instead of keeping their own copy.
     Stock is per size per colourway because both the size bar and
     the facet counts are computed from it without a round trip.
     ============================================================ */
  var PRODUCTS = [
    {id:'tee-blood',    sku:'tee',   colour:'blood',    en:'Seamless Compression Tee', ar:'\u062a\u064a\u0634\u064a\u0631\u062a \u0636\u0627\u063a\u0637 \u0633\u064a\u0645\u0644\u0633',
     cEn:'Blood',    cAr:'\u062f\u0645',        hex:'#5E1D1F', price:1480, was:1850, cats:['new','tops','seamless'],  order:1, img:'assets/img/products/card-seamless-tee-blood.webp',   stock:{S:6,M:2,L:9,XL:0}},
    {id:'tee-graphite', sku:'tee',   colour:'graphite', en:'Seamless Compression Tee', ar:'\u062a\u064a\u0634\u064a\u0631\u062a \u0636\u0627\u063a\u0637 \u0633\u064a\u0645\u0644\u0633',
     cEn:'Graphite', cAr:'\u062c\u0631\u0627\u0641\u064a\u062a', hex:'#353E43', price:1480, was:1850, cats:['new','tops','seamless'], order:2, img:'assets/img/products/card-seamless-tee-graphite.webp',   stock:{S:4,M:7,L:5,XL:3}},
    {id:'tee-violet',   sku:'tee',   colour:'violet',   en:'Seamless Compression Tee', ar:'\u062a\u064a\u0634\u064a\u0631\u062a \u0636\u0627\u063a\u0637 \u0633\u064a\u0645\u0644\u0633',
     cEn:'Violet',   cAr:'\u0628\u0646\u0641\u0633\u062c\u064a', hex:'#3B3550', price:1480, was:0,    cats:['new','tops','seamless'], order:3, img:'assets/img/products/card-seamless-tee-violet.webp',  stock:{S:3,M:5,L:4,XL:2}},
    {id:'tee-cobalt',   sku:'tee',   colour:'cobalt',   en:'Seamless Compression Tee', ar:'\u062a\u064a\u0634\u064a\u0631\u062a \u0636\u0627\u063a\u0637 \u0633\u064a\u0645\u0644\u0633',
     cEn:'Cobalt',   cAr:'\u0623\u0632\u0631\u0642',   hex:'#26406B', price:1480, was:0,    cats:['new','tops','seamless'], order:4, img:'assets/img/products/card-seamless-tee-cobalt.webp',  stock:{S:0,M:4,L:6,XL:1}},
    {id:'ls-blood',     sku:'ls',    colour:'blood',    en:'Guard Long Sleeve', ar:'\u062a\u064a\u0634\u064a\u0631\u062a \u062c\u0627\u0631\u062f \u0643\u0645 \u0637\u0648\u064a\u0644',
     cEn:'Blood',    cAr:'\u062f\u0645',        hex:'#5E1D1F', price:1690, was:0,    cats:['tops','seamless'],        order:5, img:'assets/img/products/card-guard-longsleeve-blood.webp',    stock:{S:0,M:0,L:0,XL:0}},
    {id:'ls-graphite',  sku:'ls',    colour:'graphite', en:'Guard Long Sleeve', ar:'\u062a\u064a\u0634\u064a\u0631\u062a \u062c\u0627\u0631\u062f \u0643\u0645 \u0637\u0648\u064a\u0644',
     cEn:'Graphite', cAr:'\u062c\u0631\u0627\u0641\u064a\u062a', hex:'#353E43', price:1690, was:0,    cats:['tops','seamless'],        order:6, img:'assets/img/products/card-guard-longsleeve-graphite.webp',    stock:{S:2,M:4,L:3,XL:1}},
    {id:'pant-black',   sku:'pant',  colour:'black',    en:'Heavyweight Sweatpant', ar:'\u0628\u0646\u0637\u0644\u0648\u0646 \u0642\u0637\u0646 \u062b\u0642\u064a\u0644',
     cEn:'Black',    cAr:'\u0623\u0633\u0648\u062f',   hex:'#121719', price:2150, was:0,    cats:['new','bottoms'],          order:7, img:'assets/img/products/card-sweatpant-black.webp',  stock:{S:5,M:8,L:6,XL:4}},
    {id:'pant-wash',    sku:'pant',  colour:'washed',   en:'Heavyweight Sweatpant', ar:'\u0628\u0646\u0637\u0644\u0648\u0646 \u0642\u0637\u0646 \u062b\u0642\u064a\u0644',
     cEn:'Washed',   cAr:'\u0645\u063a\u0633\u0648\u0644', hex:'#3F4247', price:2150, was:0,    cats:['bottoms'],                order:8, img:'assets/img/products/card-sweatpant-washed.webp',   stock:{S:0,M:3,L:5,XL:2}},
    {id:'short-black',  sku:'short', colour:'black',    en:'Training Short', ar:'\u0634\u0648\u0631\u062a \u062a\u062f\u0631\u064a\u0628',
     cEn:'Black',    cAr:'\u0623\u0633\u0648\u062f',   hex:'#121719', price:1240, was:0,    cats:['new','bottoms'],          order:9, img:'assets/img/products/card-training-short-black.webp', stock:{S:0,M:0,L:0,XL:0}}
  ];
  /* What PRODUCTS cannot carry: prose, and a hand-picked frame order. Keyed by
     sku for the copy and by row id for the gallery, because the long sleeve
     reuses the tee's photography in reverse and that is a per-colourway call. */
  var COPY = {
    tee:  {en:'Knitted in one piece with zoned support through the shoulders and lats.',
           ar:'\u0645\u0646\u0633\u0648\u062c \u0645\u0646 \u0642\u0637\u0639\u0629 \u0648\u0627\u062d\u062f\u0629 \u0628\u062f\u0639\u0645 \u0645\u062e\u0635\u0635 \u0639\u0646\u062f \u0627\u0644\u0623\u0643\u062a\u0627\u0641 \u0648\u0627\u0644\u0638\u0647\u0631.'},
    ls:   {en:'Longer cut with a raised collar, for the first sets before you warm up.',
           ar:'\u0642\u0635\u0629 \u0623\u0637\u0648\u0644 \u0628\u064a\u0627\u0642\u0629 \u0645\u0631\u062a\u0641\u0639\u0629\u060c \u0644\u0644\u0645\u062c\u0645\u0648\u0639\u0627\u062a \u0627\u0644\u0623\u0648\u0644\u0649 \u0642\u0628\u0644 \u0627\u0644\u0625\u062d\u0645\u0627\u0621.'},
    pant: {en:'Heavyweight loopback cotton, tapered through the leg with a deep waistband.',
           ar:'\u0642\u0637\u0646 \u062b\u0642\u064a\u0644\u060c \u0645\u062f\u0628\u0628 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0642 \u0645\u0639 \u062d\u0632\u0627\u0645 \u0639\u0631\u064a\u0636.'},
    short:{en:'Cut short and lined, with a zip pocket that stays shut under load.',
           ar:'\u0642\u0635\u0629 \u0642\u0635\u064a\u0631\u0629 \u0645\u0628\u0637\u0646\u0629\u060c \u0628\u062c\u064a\u0628 \u0628\u0633\u062d\u0627\u0628 \u064a\u0638\u0644 \u0645\u063a\u0644\u0642\u064b\u0627 \u0623\u062b\u0646\u0627\u0621 \u0627\u0644\u062a\u0645\u0631\u064a\u0646.'}
  };
  var GALLERY = {
    'tee-blood'   : ['assets/img/products/seamless-tee-blood.front.webp','assets/img/products/seamless-tee-blood.back.webp'],
    'tee-graphite': ['assets/img/products/seamless-tee-graphite.front.webp','assets/img/products/seamless-tee-graphite.back.webp'],
    'ls-blood'    : ['assets/img/products/seamless-tee-blood.back.webp','assets/img/products/seamless-tee-blood.front.webp'],
    'ls-graphite' : ['assets/img/products/seamless-tee-graphite.back.webp','assets/img/products/seamless-tee-graphite.front.webp'],
    'pant-black'  : ['assets/img/products/card-sweatpant-black.webp','assets/img/products/sweatpant-black.front.webp'],
    'pant-wash'   : ['assets/img/products/card-sweatpant-washed.webp','assets/img/products/sweatpant-black.back.webp']
    /* violet, cobalt and short have card art and nothing else. buildGallery
       falls back to c.img, and qaDot already hides the dots and both arrows
       when there is one frame, so they degrade to a single-image sheet. */
  };
  /* One row per sku x colourway on the way in, one entry per sku on the way
     out: the grid sells colourways, the quick view sells a product that has
     colourways. Sizes are derived from the stock counts rather than listed
     again, which is where the two copies disagreed. */
  function catalogFrom(list){
    /* Keyed by a handleized product title and read with a sku that arrives on a
       card's data-sku, so it takes the same no-prototype treatment COLLECTIONS
       does: on a plain literal, `CATALOG['constructor']` answers truthy and the
       quick view opens on an object that has no colourways. */
    var out = Object.create(null);
    list.forEach(function(x){
      var g = out[x.sku];
      if(!g){
        g = out[x.sku] = {
          en:x.en, ar:x.ar,
          descEn:(COPY[x.sku]||{}).en || '', descAr:(COPY[x.sku]||{}).ar || '',
          price:money(x.price), was:x.was ? money(x.was) : '',
          colours:[]
        };
      }
      g.colours.push({
        /* key is the PRODUCTS row id. Without it the quick view -- where most
           adds come from -- cannot get back to a numeric price, and '1,480'
           multiplies to NaN. */
        key:x.id, url:x.url || '',
        id:x.colour, en:x.cEn, ar:x.cAr, hex:swatchHex(x.colour, x.hex),
        img:x.img, gallery:GALLERY[x.id] || x.imgs || [],
        price:money(x.price), was:x.was ? money(x.was) : '',
        /* "No Size option" and "no size left" are different facts and the sheet
           has to tell them apart: both give an empty sizes list, and reading
           that list alone told the shopper the one product without sizes was
           sold out whatever its stock said. Only a catalogue row carries a
           sizes array, so the invented rows below answer false and keep the
           reference's behaviour exactly. */
        sizeless:!!x.sizes && !x.sizes.length, stock1:x.stock1 | 0,
        /* The invented rows carry no chart flag, and false is right for them:
           the reference has one product page and the theme's quick view must
           not offer a #sizing band the store has no sheet for. */
        chart:x.chart === true,
        sizes:ALL_SIZES.filter(function(z){ return (x.stock[z] | 0) > 0; }),
        /* Carried through so the sort below has something to read. */
        sort:x.sort
      });
    });
    /* THE SWATCH ORDER. The merchant sets it once per colour, on the colour
       list, and it decides the order everywhere a colourway is offered: a
       card's sibling chips, the quick view, and the product page's swatch row.
       Before this the order was whichever way the catalogue happened to list
       the products, which is a thing no one chose.

       Stable, and blind to a colour with no number: the fourteen entries on
       this store's colour list carry 10 to 140, so the order here is the
       merchant's rather than the catalogue's, and a colour added later with no
       number falls in after all of them rather than jumping the row. Array
       .sort is stable in every engine this ships to, so equal keys keep their
       catalogue order -- and an unset colour sorts as Infinity rather than 0,
       because 0 is a number a merchant can mean and "no opinion" belongs after
       every opinion, not before them. */
    Object.keys(out).forEach(function(k){
      out[k].colours.sort(function(a, b){
        var x = (typeof a.sort === 'number') ? a.sort : Infinity;
        var y = (typeof b.sort === 'number') ? b.sort : Infinity;
        return x - y;
      });
    });
    return out;
  }
  /* Sizes are read off the catalogue before it is derived, because catalogFrom
     filters each colourway's sizes against this list. */
  /* The store's catalogue replaces the invented one whenever it is there.
     This is the line the quick view has been waiting for: CATALOG is keyed by
     real product families from here, so qaOpen stops returning early on every
     real handle -- which is what made three separate bypasses necessary. */
  var LIVE_ROWS = rowsFromBlob();
  if(LIVE_ROWS) PRODUCTS = LIVE_ROWS;
  ALL_SIZES = sizesFromCatalogue(PRODUCTS);
  CATALOG = catalogFrom(PRODUCTS);

  /* The live cards can be filled only now: mountCards() and friends ran
     above, against PRODUCTS before this line swapped in the store's rows. So
     fill, then run the per-card initialisers again over what was filled -- the
     chips are new nodes, the size row is rebuilt, and a card that has a back
     photo only since the fill needs its tap-to-swap. mountSection() does the
     same in the same order on every editor re-render. */
  fillCards(d);
  mountCards(d);
  mountSwatches(d);
  mountTouch(d);

  /* ============================================================
     THE BAG -- Shopify's cart, not a local one.

     The reference keeps its bag in localStorage, because it is a static site
     with nowhere to put one. Here the cart IS the platform's: the initial state
     is server-rendered into #og-cart by snippets/og-cart.liquid, and every
     mutation goes through the AJAX Cart API. Nothing about the bag survives a
     reload except what Shopify itself holds, which is the point -- a bag that
     cannot reach checkout is a bag that lies.

     WHAT DID NOT CHANGE, DELIBERATELY. A row is still sku + colourway + size +
     qty, rowKey() is still that triple, and cartRow() still reads name, price,
     image and stock out of the catalogue at render time. The Shopify line key
     rides along on the row for API calls and never reaches the DOM. So the
     drawer, the cart page, the badge, the meter and both parity lanes are
     untouched by this: only the four functions that OWNED the data changed.

     THE PRICE STILL COMES FROM THE CATALOGUE, not from the cart line. Shopify's
     line price is authoritative for what will be charged, but the design's
     summary is computed from catalogue prices plus a delivery setting, and the
     pixel run compares that arithmetic. Checkout is where the platform's number
     wins, and checkout is one button away.

     This sits after PRODUCTS because it indexes and revalidates against it.
     ============================================================ */
  /* The v2 localStorage bag is retired -- Shopify holds the cart now. Cleared
     rather than ignored: a stale key that a future reader might mistake for
     live state is worse than no key, and this store was never public, so there
     is nothing in it worth migrating. */
  mem.del('og:bag');
  /* Placeholder, like every other number on this site: free over 2,500, and a
     flat fee under it. See plan/pages/cart.md -- the fee below the threshold is
     the one figure the brand has never stated. */
  /* Coerced, not defaulted with ||. A blank number setting is null, and
     `CFG.cart.delivery || 70` would clobber a deliberate 0 — which is a real
     value meaning delivery is always free. */
  var FREE_OVER = isFinite(+CFG.cart.freeOver) ? +CFG.cart.freeOver : 2500;
  var DELIVERY  = isFinite(+CFG.cart.delivery) ? +CFG.cart.delivery : 70;
  var bag=[], bagSubs=[], bagNote=null, bagLast=null;

  /* The accessor this site has never had: nothing anywhere mapped a SKU and a
     colourway back to a row. */
  /* Rows adopted from a cart line the catalogue could not explain. They are kept
     BESIDE the catalogue, never pushed into it: PRODUCTS is the same array
     OG.products hands the collection controller, so a row appended here would
     render as a phantom product in the grid -- one with no categories, a
     synthetic sku and a place in "Shop all" it has not earned. */
  var ADOPTED = [];
  function productFor(sku, colour){
    var i;
    for(i=0;i<PRODUCTS.length;i++){
      if(PRODUCTS[i].sku===sku && PRODUCTS[i].colour===colour) return PRODUCTS[i];
    }
    for(i=0;i<ADOPTED.length;i++){
      if(ADOPTED[i].sku===sku && ADOPTED[i].colour===colour) return ADOPTED[i];
    }
    return null;
  }
  /* A row published by the page that is looking at it, for a product the
     catalogue window never reached. It goes in ADOPTED for that list's own
     reason -- PRODUCTS is the array the collection grid renders, and a row
     appended there is a phantom product in "Shop all" -- but it is indexed
     exactly like a catalogue row, so productFor, stockFor and variantFor answer
     for it and the add reaches Shopify with a real variant id.

     The catalogue always wins: a row it already has is never shadowed by a
     second opinion from one page. Ids are copied, never coerced -- fourteen
     digits do not survive a bitwise operator. */
  function adoptRow(row){
    if(!row || !row.sku || !row.colour) return null;
    if(productFor(row.sku, row.colour)) return null;
    ADOPTED.push(row);
    var s;
    /* Never over an id something already claims. A line already in the bag was
       adopted from #og-cart at boot under its own identity, and re-pointing its
       variant here would change the key its rendered row is stepping. */
    if(row.vid){
      for(s in row.vid){
        if(row.vid[s] && !VIX[row.vid[s]])
          VIX[row.vid[s]] = {sku:row.sku, colour:row.colour, size:s};
      }
    }
    if(row.v1 && !VIX[row.v1] && !(row.sizes && row.sizes.length))
      VIX[row.v1] = {sku:row.sku, colour:row.colour, size:''};
    return row;
  }
  function stockFor(sku, colour, size){
    var p=productFor(sku,colour);
    if(!p) return 0;
    if(size) return p.stock[size] | 0;
    /* No size given. Only the one product without a Size option can answer
       that, and its quantity is published as stock1 rather than as a ""
       key -- see og-catalog.liquid for why the map could not carry it. Asking a
       sized product for its sizeless stock is a caller bug, not a zero. */
    return (p.sizes && p.sizes.length) ? 0 : (p.stock1 | 0);
  }
  function rowKey(r){ return r.sku+'|'+r.colour+'|'+r.size; }

  /* ---- variant ids, both directions --------------------------------
     The one place the design's key and Shopify's key meet. og-catalog.liquid
     publishes a per-size vid map and a v1 for the product that has no Size
     option; this turns that into a lookup in the other direction, because a
     cart line arrives carrying a variant id and nothing else. */
  var VIX = Object.create(null);
  function indexVariants(){
    VIX = Object.create(null);
    for(var i=0;i<PRODUCTS.length;i++){
      var p=PRODUCTS[i], s;
      if(p.vid){ for(s in p.vid){ if(p.vid[s]) VIX[p.vid[s]] = {sku:p.sku, colour:p.colour, size:s}; } }
      /* The sized products' v1 is their first size, already mapped above with
         the size it belongs to -- so this only ever fills in the sizeless one,
         and the sizes test says so rather than trusting that. A sized product
         whose v1 escaped the map above (a variant carrying a blank size value)
         would otherwise be indexed as sizeless, and stockFor() answers 0 for a
         sizeless read of a sized row -- which is the one input that makes the
         boot reconciliation below delete a line it merely could not describe.
         Left out of VIX it is adopted from its own cart payload instead. */
      if(p.v1 && !VIX[p.v1] && !(p.sizes && p.sizes.length))
        VIX[p.v1] = {sku:p.sku, colour:p.colour, size:''};
    }
  }
  indexVariants();

  /* NEVER `| 0` A VARIANT ID. They are fourteen digits -- 46498753675343 --
     and the bitwise operators this file uses freely to coerce quantities
     truncate to 32 bits, so `id | 0` silently returns a different, valid-looking
     number. Every coercion here is `||`. */
  function variantFor(sku, colour, size){
    var p=productFor(sku,colour);
    if(!p) return 0;
    if(size) return (p.vid && p.vid[size]) || 0;
    return (p.sizes && p.sizes.length) ? 0 : (p.v1 || 0);
  }

  /* A cart line whose product is outside the catalogue's fifty-row window, or
     whose product was unpublished while it sat in the bag. cartRow() returns
     null when productFor() misses, so without this the row would vanish from
     the list while the badge went on counting it -- a bag quietly smaller than
     you left it, which plan/pages/cart.md calls the worst version of this page.

     The line's own payload becomes a catalogue row. Identity is the variant id
     rather than the handle, because one product with two colourways would
     otherwise collapse into a single row. */
  function adoptLine(vid, fb){
    if(!vid || !fb) return null;
    var sku='v'+vid, colour='default', size=fb.size ? String(fb.size) : '';
    var row={sku:sku, colour:colour, id:sku+'-'+colour, h:fb.h||'', url:fb.url||'',
             en:fb.en||'', ar:fb.ar||fb.en||'', cEn:fb.cEn||'', cAr:fb.cAr||fb.cEn||'',
             /* Not null and not '': cartRow() writes p.img straight into a src,
                where null fetches "/null" for a 404 and '' re-requests the
                DOCUMENT as an image -- the trap og-catalog.liquid already
                records for its own null images. A transparent pixel over
                .cart-thumb's --slab background is the empty state. */
             price:+fb.price||0, was:0, img:fb.img||BLANK_PX, thumb:fb.img||BLANK_PX,
             cats:[], sizes:size?[size]:[], stock:{}, vid:{}, v1:vid,
             order:9999, created:0, tags:'', type:'', avail:true};
    /* The cap, not a measured figure: nothing outside the catalogue window
       publishes stock, and a 0 here would have revalidate() drop the row on
       sight. Shopify refuses an over-add at the wire, which is the real check. */
    row.stock[size] = CFG.stock.cap|0 || 99;
    /* Both, because stockFor() reads a different field for each shape: the map
       for a sized row, stock1 for a sizeless one -- and a line whose product
       has no Size option arrives with size ''. With only the map, stockFor()
       answered 0 for it, revalidate() dropped it, and the boot reconciliation
       below posted quantity 0 to Shopify -- deleting a line the shopper really
       had, for the sole reason that this row could not describe it. */
    row.stock1 = CFG.stock.cap|0 || 99;
    if(size) row.vid[size]=vid;
    ADOPTED.push(row);
    VIX[vid] = {sku:sku, colour:colour, size:size};
    return VIX[vid];
  }

  function sizeOf(opts){
    if(!(opts instanceof Array)) return '';
    for(var i=0;i<opts.length;i++){
      var o=opts[i];
      if(o && String(o.name||'').toLowerCase().trim()==='size') return String(o.value||'');
    }
    return '';
  }

  /* ---- reading the cart --------------------------------------------
     #og-cart is server-rendered on every page, so the bag is right on the
     first paint with no round trip -- the same trade og-catalog makes. */
  function rowsFrom(items, shape){
    var out=[];
    if(!(items instanceof Array)) return out;
    for(var i=0;i<items.length;i++){
      var it=items[i]; if(!it) continue;
      var n=shape(it);
      if(!n.qty || !n.vid) continue;
      var at=VIX[n.vid] || adoptLine(n.vid, n.fb);
      if(!at) continue;
      out.push({sku:at.sku, colour:at.colour, size:at.size,
                qty:n.qty, key:n.key||'', vid:n.vid});
    }
    return out;
  }
  function readBag(){
    var blob=null;
    try{
      var el=d.getElementById('og-cart');
      if(el) blob=JSON.parse(el.textContent);
    }catch(e){}
    if(!blob) return [];
    return rowsFrom(blob.items, function(it){
      return {vid:it.vid, key:it.key, qty:it.qty|0, fb:it.fb};
    });
  }
  /* The shape /cart.js and /cart/change.js both answer with. */
  function syncFrom(c){
    if(!c || !(c.items instanceof Array)) return false;
    bag = rowsFrom(c.items, function(it){
      return {vid:it.variant_id, key:it.key, qty:it.quantity|0,
              fb:{en:it.product_title, ar:it.product_title,
                  cEn:'', cAr:'', size:sizeOf(it.options_with_values),
                  price:(it.price|0) / (CFG.money.cents||100),
                  img:it.image||null, url:it.url||''}};
    });
    return true;
  }

  /* ---- the wire ----------------------------------------------------
     ONE REQUEST AT A TIME. The stepper can be pressed twice before the first
     response lands, and two overlapping /cart/change.js calls resolve in
     whatever order the network picks -- so the first response can overwrite the
     second write and the quantity silently walks backwards. Serialising costs
     nothing at this volume and removes the whole class. */
  var cartQ = (window.Promise ? Promise.resolve() : null);
  function queue(fn){
    if(!cartQ) return fn();
    cartQ = cartQ.then(fn, fn);
    return cartQ;
  }
  function cartUrl(route){ return (route || '') + '.js'; }
  function cartFetch(url, body){
    var opt = {headers:{'Accept':'application/json'}, credentials:'same-origin'};
    if(body){
      opt.method='POST';
      opt.headers['Content-Type']='application/json';
      opt.body=JSON.stringify(body);
    }
    return fetch(url, opt).then(function(res){
      return res.text().then(function(txt){
        var j={};
        try{ j=JSON.parse(txt); }catch(e){}
        if(!res.ok){
          /* Shopify puts the human sentence in "description"; 422 on an add is
             the sold-out wall and is the one a shopper will actually meet. */
          var err=new Error(j.description || j.message || ('cart error ' + res.status));
          err.status=res.status;
          throw err;
        }
        return j;
      });
    });
  }
  /* A later edit that works clears the last one that did not. Without this
     the refusal stays on screen for the rest of the visit and starts
     describing a bag that has since changed. */
  function clearFailed(){
    if(!bagNote || !bagNote.failed || !bagNote.failed.length) return;
    bagNote.failed = [];
    if(!bagNote.dropped.length && !bagNote.clamped.length) bagNote = null;
  }
  /* An optimistic edit the server refused: say why, then ASK THE SERVER what
     the cart is rather than reconstructing what it was.

     Reconstruction was tried twice and neither granularity is sound. A
     whole-bag snapshot reverts edits made after it was taken; a row-scoped one
     fixes that but still cannot describe two failures against the SAME row --
     press quick add twice before either answers, both 422, and the second
     rollback puts back a row the first had just removed, leaving a line
     Shopify never accepted. On this store that is not hypothetical: every
     variant is sold out, so every add is refused.

     One read of /cart.js cannot drift, is the same reconciliation syncFrom()
     already performs on success, and a refused write is not a hot path.

     IT MUST BE RETURNED, AND BOTH CATCH HANDLERS RETURN IT. That is the whole
     of what puts it inside the queue: cartQ chains on what a queued job hands
     back, so a catch that called this and returned undefined released the queue
     the instant the write failed and left the GET racing whatever was pressed
     next. A "+" queued behind it would then answer FIRST, and the older read
     would land afterwards and overwrite the display with the cart as it was
     before -- one piece on screen, two at Shopify, silently, which is the exact
     reordering queue() exists to prevent. Returning it also means
     OG.cartReady(), and so the checkout button's wait, spans the recovery.

     Its own failure is swallowed: offline, the note on screen is the honest
     answer and the next load reconciles. */
  function fail(key, msg){
    var note = bagNote || {dropped:[], clamped:[], failed:[]};
    if(!note.failed) note.failed = [];
    note.failed.push({key:key, msg:msg});
    bagNote = note;
    emitBag();
    return cartFetch(cartUrl(CFG.routes.cart))
      .then(function(c){ if(syncFrom(c)) emitBag(); })
      .catch(function(){});
  }

  /* Stock is client-side and a size can sell out between visits. Clamp what is
     left, drop what is gone, and keep a note of both -- a bag that is quietly
     smaller than you left it is the worst version of this page. */
  function revalidate(){
    var dropped=[], clamped=[];
    bag = bag.filter(function(r){
      var have=stockFor(r.sku, r.colour, r.size);
      if(!have){ dropped.push(r); return false; }
      if(r.qty > have){ r.qty = have; clamped.push(r); }
      return true;
    });
    bagNote = (dropped.length || clamped.length)
      ? {dropped:dropped, clamped:clamped, failed:[]} : null;
    return bagNote;
  }

  function bagCount(){
    return bag.reduce(function(a,r){ return a + r.qty; }, 0);
  }
  function bagSubtotal(){
    return bag.reduce(function(a,r){
      var p=productFor(r.sku, r.colour);
      return a + (p ? p.price * r.qty : 0);
    }, 0);
  }
  function bagDelivery(){
    if(!bag.length) return 0;
    return bagSubtotal() >= FREE_OVER ? 0 : DELIVERY;
  }
  function bagTotal(){ return bagSubtotal() + bagDelivery(); }

  /* Subscription rather than polling, the same shape as onResize: the badge,
     the drawer and the cart page stay in step without knowing about each other. */
  function emitBag(){
    for(var i=0;i<bagSubs.length;i++){ try{ bagSubs[i](bag); }catch(e){} }
  }
  function onBag(fn){ bagSubs.push(fn); try{ fn(bag); }catch(e){} }

  /* Every add path on the site funnels through here, and every one of them ran
     synchronously before. It still LOOKS synchronous: the row is written and
     emitted first, so the badge, the drawer and the page move at the speed of
     the press, and the server reconciles a moment later. The clamp against
     local stock stays -- it is what keeps the stepper honest between requests
     -- but Shopify's answer is the one that survives. */
  function bagAdd(sku, colour, size, qty){
    if(!sku || !colour) return;
    var sz = size || '';
    var have=stockFor(sku, colour, sz);
    if(!have) return;                       /* never offer what does not exist */
    var vid=variantFor(sku, colour, sz);
    /* No variant id means the catalogue never published one for this triple.
       Adding would post nothing and report success, so refuse instead. */
    if(!vid) return;
    clearFailed();
    var want=Math.max(1, (qty|0) || 1), key=sku+'|'+colour+'|'+sz, found=null;
    for(var i=0;i<bag.length;i++){ if(rowKey(bag[i])===key) found=bag[i]; }
    if(found) found.qty = Math.min(have, found.qty + want);
    else bag.push({sku:sku, colour:colour, size:sz, qty:Math.min(have, want), key:'', vid:vid});
    bagLast = key;
    emitBag();
    if(drawerOnAdd) openCart();
    return queue(function(){
      return cartFetch(cartUrl(CFG.routes.cartAdd), {items:[{id:vid, quantity:want}]})
        /* /cart/add.js answers with the added lines only, never the whole cart,
           so the line keys and the new totals need a second read. Every other
           call returns the cart and needs none. */
        .then(function(){ return cartFetch(cartUrl(CFG.routes.cart)); })
        .then(function(c){ if(syncFrom(c)) emitBag(); })
        .catch(function(err){ return fail(key, err.message); });
    });
  }
  function rowFor(key){
    for(var i=0;i<bag.length;i++){ if(rowKey(bag[i])===key) return bag[i]; }
    return null;
  }
  function applyQty(key, q){
    for(var i=0;i<bag.length;i++){
      if(rowKey(bag[i])!==key) continue;
      if(q<=0) bag.splice(i,1); else bag[i].qty=q;
      return true;
    }
    return false;
  }
  function sendQty(line, q, key){
    return cartFetch(cartUrl(CFG.routes.cartChange), {id:line, quantity:q})
      .then(function(c){ if(syncFrom(c)) emitBag(); })
      .catch(function(err){ return fail(key, err.message); });
  }
  function bagSet(key, qty){
    var row = rowFor(key);
    if(!row) return;
    clearFailed();
    var line=row.key;
    var have=stockFor(row.sku, row.colour, row.size);
    var q=Math.min(have, Math.max(0, qty|0));
    applyQty(key, q);
    emitBag();
    if(line) return queue(function(){ return sendQty(line, q, key); });
    /* A row added moments ago has no line key yet, and its own add is still in
       the queue. Dropping the edit here is what the old comment on this branch
       claimed was safe, and it was not: the add's answer REPLACES bag wholesale
       through syncFrom(), so a press made inside that round trip vanished --
       the row visibly went, Shopify was never told, and the add's reply put it
       back at its old quantity with nothing logged. Defer instead. Writes are
       already serialised, so by the time this runs the add has landed and the
       row can be found by its own key; re-applying locally first keeps the
       press visible across the hand-off. */
    return queue(function(){
      var now = rowFor(key);
      if(!now || !now.key) return;      /* the add never landed; nothing to change */
      applyQty(key, q);
      emitBag();
      return sendQty(now.key, q, key);
    });
  }
  function bagRemove(key){ bagSet(key, 0); }

  bag = readBag();
  var boot = revalidate();
  /* Revalidation is not a local edit any more. The row it drops is a line
     Shopify still holds, and a cart that disagrees with its own checkout is
     worse than one that is merely out of date -- so the removals go back to the
     server too. Failures are swallowed on purpose: the row is already gone from
     the page, and the note simply reappears on the next visit, which is a
     truthful outcome rather than a silent one.

     Safe to run unconditionally because readBag() only emits rows it could
     resolve, and a row it could not resolve was adopted with the stock cap. So
     a stock of 0 here means genuinely sold out, never "the catalogue failed to
     load" -- which would otherwise empty a shopper's cart on a bad response. */
  if(boot && boot.dropped.length){
    boot.dropped.forEach(function(r){
      if(!r.key) return;
      queue(function(){
        return cartFetch(cartUrl(CFG.routes.cartChange), {id:r.key, quantity:0})
          .catch(function(){});
      });
    });
  }

  function bits(parts, sep){
    return parts.filter(function(v){ return v != null && v !== ''; }).join(sep);
  }

  /* ---- one row renderer, for both surfaces ----
     The drawer and the cart page share it. Two copies of a renderer is exactly
     how CATALOG and PRODUCTS drifted, and only one of those still exists. */
  function cartRow(r, editable){
    var p=productFor(r.sku, r.colour); if(!p) return null;
    var have=stockFor(r.sku, r.colour, r.size), key=rowKey(r);
    var href=productUrl(p.sku, p.colour);
    var el=d.createElement('div');
    el.className='cart-row';
    el.dataset.key=key;
    el.innerHTML =
      '<a class="cart-thumb ch-sm" href="'+href+'" tabindex="-1" aria-hidden="true">' +
        '<img src="'+p.img+'" alt="" loading="lazy"></a>' +
      '<div class="cart-info">' +
        '<h3><a class="cart-name" href="'+href+'"></a></h3>' +
        '<p class="cart-var t-data"></p>' +
        '<p class="cart-note t-data"></p>' +
      '</div>' +
      '<div class="cart-end">' +
        (editable
          /* remove leads the column: it lines up with the product name, which is
             what it removes, instead of hanging below the stepper */
          ? '<button class="cart-x" type="button">' +
              '<svg class="ic" aria-hidden="true"><use href="#ph-plus"/></svg></button>'
          : '') +
        '<span class="cart-price">' + money(p.price * r.qty) + ' <em class="egp">' + CFG.money.en + '</em></span>' +
        (editable
          ? '<div class="qty ch-sm line" role="group">' +
              '<button class="qty-btn" data-step="-1" type="button">' +
                '<svg class="ic" aria-hidden="true"><use href="#ph-minus"/></svg></button>' +
              '<b class="qty-n">'+r.qty+'</b>' +
              '<button class="qty-btn" data-step="1" type="button">' +
                '<svg class="ic" aria-hidden="true"><use href="#ph-plus"/></svg></button>' +
            '</div>'
          : '<span class="cart-qty t-data">× '+r.qty+'</span>') +
      '</div>';

    setBi(el.querySelector('.cart-name'), p.en, p.ar);
    /* Joined, not concatenated. Nine of nineteen products on this store have no
       colourway and one has no size, where a fixed ' · ' printed a separator
       with nothing on one side of it -- " · M", or for the multipack " · "
       alone. The reference could not show this: all nine of its products carry
       both. A row that has both is byte-identical to what this wrote before. */
    setBi(el.querySelector('.cart-var'), bits([p.cEn, r.size], ' · '),
                                         bits([p.cAr, r.size], ' · '));

    /* the collection's per-size rule, not a fourth threshold */
    var note=el.querySelector('.cart-note');
    if(have<=CFG.stock.low) setBi(note, 'Only '+have+' left', 'باقي '+have+' فقط');
    else note.hidden=true;

    if(editable){
      var name = bits([p.en, p.cEn, r.size], ', '),
          nameAr = bits([p.ar, p.cAr, r.size], '، ');
      /* "+" alone is meaningless in a list of six identical steppers */
      label(el.querySelector('[data-step="-1"]'),
            'Decrease quantity, '+name, 'إنقاص الكمية، '+nameAr);
      label(el.querySelector('[data-step="1"]'),
            'Increase quantity, '+name, 'زيادة الكمية، '+nameAr);
      label(el.querySelector('.cart-x'),
            'Remove '+name, 'إزالة '+nameAr);
      el.querySelector('[data-step="1"]').disabled = r.qty >= have;
      el.querySelector('[data-step="-1"]').disabled = false;
    }
    return el;
  }
  /* aria-label is not a text node, so it needs its own bilingual pair */
  function label(el, en, ar){
    if(!el) return;
    el.setAttribute('aria-label', isAr() ? ar : en);
    el.dataset.alEn=en; el.dataset.alAr=ar;
  }

  /* Red while the goal is unmet, neutral once it is -- the same rule, and the
     same component, as the collection's load-more meter. */
  function meterState(){
    var sub=bagSubtotal(), left=Math.max(0, FREE_OVER - sub), done=left<=0 && bag.length>0;
    return {
      pct: FREE_OVER ? Math.min(100, sub / FREE_OVER * 100) : 100,
      done: done,
      en: done ? 'Free delivery' : money(left)+' '+CFG.money.en+' to free delivery',
      ar: done ? 'شحن مجاني'
               : money(left)+' '+CFG.money.ar+' حتى الشحن المجاني'
    };
  }
  function pieces(c){
    return {en: c+(c===1 ? ' piece' : ' pieces'),
            ar: c+' '+(c===1 ? 'قطعة'
                     : c===2 ? 'قطعتان'
                     : c<=10 ? 'قطع' : 'قطعة')};
  }

  /* ---- the drawer shows the whole bag, and edits it ---- */
  /* The three handles this reads are declared and assigned by mountDrawer(),
     up beside openCart(). They are deliberately NOT re-declared here: `var
     dEmpty = null` at this point is not a declaration, it is an assignment that
     runs after the mount and puts all three back to null -- which is exactly
     what it did, and the drawer rendered its empty state over a bag holding
     three rows while every other surface counted them correctly. It looked
     fine, `node --check` passed, and the section swap "fixed" it, because the
     swap re-ran the mount. */
  /* An add the server refused, said where the shopper is looking.

     bagAdd() opens this panel optimistically and rolls the row back when
     /cart/add.js answers 422 -- the sold-out wall, and the refusal a shopper
     will actually meet. Only the bag page read bagNote, so everywhere else the
     drawer slid open, the row appeared and then vanished with Shopify's own
     sentence thrown away unread.

     It borrows a line that is already there rather than adding one: the meta
     line when there are rows, the empty state's second line when there are not.
     tools/dom_parity.py compares this subtree against the reference node for
     node and theme-drawer.spec.js photographs it open, so a new element -- even
     a hidden one -- fails both. Both lines carry data-en/data-ar, so clearing
     the message restores the merchant's copy from the element itself. */
  function failMsg(){
    var f = bagNote && bagNote.failed;
    return (f && f.length) ? f[f.length-1].msg : '';
  }
  function drawerSay(el, msg){
    if(!el) return;
    if(msg){ el.textContent = msg; return; }
    if(el.dataset && el.dataset.en != null)
      el.textContent = isAr() ? (el.dataset.ar || el.dataset.en) : el.dataset.en;
  }

  /* ---- drawer cross-sell -----------------------------------------
     Not inStock(), which now lives in this same IIFE and is exported -- the two
     differ on the SIZELESS row and that difference is the whole point. A garment
     with no Size option carries its quantity in stock1 and has an empty stock
     map, so inStock() would read that empty map and call it permanently
     unavailable; this one falls through to stock1. Do not merge them.

     Not p.sizes either -- the reference's rows carry no sizes key and the parity
     fixture's do, so a rule branching on it would answer differently on the two
     sides. The stock MAP is identical on both and the answer is a boolean, so
     iteration order cannot matter. avail is read only when the payload actually
     carried it: coercing a missing one marked all nine fixture rows sold out
     once already. */
  function railBuyable(p){
    if(p.avail === false) return false;
    var z, st = p.stock || {};
    for(z in st){ if((st[z] | 0) > 0) return true; }
    return (p.stock1 | 0) > 0;
  }
  function railShares(a, b){
    for(var i=0;i<b.length;i++){ if(a.indexOf(b[i]) > -1) return true; }
    return false;
  }
  /* bag[0] is the OLDEST line, so the neighbourhood does not jump on every add.
     Its collections are the anchor, the way main-product's related rail takes
     the page's own collection. Overlap, not cats[0]: cats is the shop's own
     collection order, which nobody here chose. Held by SKU (family), so a bag
     holding Blood is never offered Graphite. */
  function railPicks(){
    var first = bag[0], anchor = first ? productFor(first.sku, first.colour) : null;
    var cats = (anchor && anchor.cats) || [];
    var held = Object.create(null), seen = Object.create(null), out = [], i, p;
    for(i=0;i<bag.length;i++) held[bag[i].sku] = true;
    /* TWO PASSES, in-stock first. Requiring stock outright emptied the rail on
       this catalogue entirely: eighteen of nineteen products are sold out, so
       after dropping what is already in the bag there was nothing left and the
       band never appeared. The rest of the site shows sold-out garments with a
       badge rather than hiding them -- the collection grid does, and so does
       every card -- so hiding them here was the inconsistency, not showing them.
       In-stock still comes first; sold-out only fills what is left over. */
    var spare = [], spared = Object.create(null);
    for(i=0;i<PRODUCTS.length;i++){
      p = PRODUCTS[i];
      if(!p || !p.sku || held[p.sku] || seen[p.sku]) continue;
      /* An anchor in no collection drops the filter rather than emptying the rail. */
      if(cats.length && !railShares(cats, p.cats || [])) continue;
      /* seen is marked on the row that is TAKEN, never on the first row of the
         family the loop walks past: marking it here would let a sold-out first
         colourway stand in for a buyable sibling and the rail would offer a
         garment with a Sold out line while the same one is in stock in another
         colour. The spare bucket keeps its own map so it still holds one
         colourway per family. */
      if(railBuyable(p)){
        seen[p.sku] = true;
        out.push(p);
        if(out.length >= railCap) return out;
      } else if(spare.length < railCap && !spared[p.sku]){
        spared[p.sku] = true;
        spare.push(p);
      }
    }
    for(i=0;i<spare.length && out.length < railCap;i++){
      p = spare[i];
      if(seen[p.sku]) continue;
      seen[p.sku] = true;
      out.push(p);
    }
    return out;
  }
  /* One <a>, spans inside it. No add control, no size picker: the drawer is a
     receipt and theme-drawer.spec.js asserts it stays one. setBi, not T() --
     there is no T() in this IIFE, and the pair lets applyLang flip the language
     with no rebuild at all. Currency hardcoded exactly as cartRow does it, so
     the rail cannot disagree with the row above it. */
  /* The site's own card, from the shared surface. No quick-add: that button
     fires qaOpen through a document-level delegate, and the quick view over an
     open drawer destroys trapPrev, untraps the drawer while it is still
     aria-modal="true", and drops body.locked -- two defects docs/decisions.md
     records as fixed. The whole card still navigates; .card-link's ::after
     already covers the photograph. */
  function railCard(p, i){
    return card(p, i, {add:false, imgSizes:SZ_DRAWER, swipe:false});
  }
  /* The edge fade, same shape as tabFade: it says which way there is more to
     see. RTL reports scrollLeft negative, hence Math.abs, and the 2px tolerance
     is for sub-pixel scroll positions.

     A DELIBERATE DUPLICATE of tabFade, and it stays one. The two trees hold
     different signatures for that function -- this one takes the row as an
     argument, the reference's takes none and closes over its own tabRow -- so
     `tabFade(dRailTrack)` would collapse twelve lines here and be unwritable
     there. Every JS change on this branch lands in both trees by rule, and
     rewriting the reference's tabFade to take a parameter purely so this file
     can save the lines is an edit to a file whose job is to be the frozen
     design spec. Twelve duplicated lines cost less than that. */
  function railFade(){
    if(!dRailTrack) return;
    var max = dRailTrack.scrollWidth - dRailTrack.clientWidth;
    var x = Math.abs(dRailTrack.scrollLeft);
    var none = max <= 2, atStart = x <= 2, atEnd = x >= max - 2;
    dRailTrack.classList.toggle('fade-end',   !none && atStart);
    dRailTrack.classList.toggle('fade-start', !none && atEnd);
    dRailTrack.classList.toggle('fade-both',  !none && !atStart && !atEnd);
  }
  /* The half of the room question a media query cannot answer, because it turns
     on how many rows the bag holds -- the CSS carries the one-row floor and can
     see nothing else. */
  function railRoom(){
    return window.innerHeight >= railChromeH + bag.length * railRowH + railCardH;
  }
  /* One condition, read by the renderer and by the resize job below, because two
     copies of it would be two answers to the same question. */
  function railWanted(){
    return railOn && bag.length > 0 && bag.length <= railOver && railRoom();
  }
  function renderRail(){
    if(!dRail || !dRailTrack) return;
    var picks = railWanted() ? railPicks() : [];
    dRail.hidden = picks.length === 0;
    dRailTrack.innerHTML = '';
    picks.forEach(function(p, i){ dRailTrack.appendChild(railCard(p, i)); });
    /* Bound once for the life of this track. dragScroll has no internal guard
       and the drawer is a section, so a per-render binding would stack a pair of
       listeners on every theme-editor edit -- the rzJobs trap, one file over.

       dragScroll BY NAME, never through window.OG, which is the mistake this
       once-flag turns permanent. The first renderRail runs during module
       evaluation -- bag = readBag(), onBag fires its subscriber synchronously,
       renderDrawer falls through to here -- and `window.OG = {...}` is four
       hundred lines further down, so on any load whose server-rendered cart
       already holds one or two lines the surface does not exist yet. Through OG
       the drag silently never bound, for the life of that page, on the one
       configuration a shopper actually arrives in. This function is in the same
       IIFE as dragScroll, exactly as the reference is. */
    if(!railBound){
      railBound = true;
      dragScroll(dRailTrack, railFade);
      dRailTrack.addEventListener('scroll', railFade, {passive:true});
    }
    railFade();
  }
  /* The room question re-answered when the viewport changes, which a bag change
     alone cannot catch: between the one-row floor the CSS holds and the two-row
     one it cannot express, dragging a window or rotating a tablet with the
     drawer open left a band standing in the state the budget had already refused
     -- and .drawer-scroll is the only shrinkable child, so the difference comes
     out of the shopper's own cart.

     onResize by name, not through window.OG: the surface is assigned hundreds of
     lines below and this runs during module evaluation, which is exactly how the
     drag binding silently never bound. Registered ONCE for the life of the page
     rather than per mount, because rzJobs has no removal path and a job bound per
     mount stacks a stale writer on every theme-editor edit; this one survives any
     number of section swaps because renderRail reads dRail and dRailTrack by name
     rather than capturing them.

     Only when the answer actually flips. A resize fires many frames, and
     renderRail rebuilds the track -- which would reset the carousel's scroll
     position under the shopper's thumb for every one of them. */
  onResize(function(){
    if(dRail && railWanted() === dRail.hidden) renderRail();
  });

  /* ---- the drawer edits the bag ----------------------------------
     Hashim, 5 Oct 2026: "should be able to remove and change qty from cart
     drawer". The drawer was a read-only receipt -- every line, no controls --
     on the reasoning that editing belonged to the bag page. In practice it is
     the surface a shopper is looking at when they notice they added two, so it
     takes the bag page's own row now: the same cartRow(r, true), the same
     stepper and remove, the same bagSet() underneath, so the stock cap, the
     queue and the server's answer are all the ones the page already relies on.

     renderDrawer() rebuilds the list on every bag change, and twice per edit
     (once at the press, once when /cart/change.js answers), so whatever had
     focus is destroyed both times. The bag page's rule carries over: a stepper
     press keeps focus on that stepper, or the row's other one once this one is
     disabled at the stock cap; a removal moves to the next row's remove, the
     row before it if it was the last, and the drawer's close button once the
     bag is empty. Never <body>, and a stepper press never lands on Remove,
     where the next Enter would delete the line. */
  var dFocus = null;
  function drawerRow(key){
    for(var i=0;i<bag.length;i++){ if(rowKey(bag[i])===key) return bag[i]; }
    return null;
  }
  function drawerAfter(row){
    var rows = [].slice.call(dList.querySelectorAll('.cart-row')), i = rows.indexOf(row);
    var next = rows[i + 1] || rows[i - 1];
    return {key: next ? next.dataset.key : null, sel: '.cart-x'};
  }
  function drawerEdit(e){
    var row = e.target.closest && e.target.closest('.cart-row');
    if(!row) return;
    var key = row.dataset.key, cur = drawerRow(key);
    var step = e.target.closest('.qty-btn');
    if(step){
      if(step.disabled || !cur) return;
      var want = cur.qty + (+step.dataset.step);
      dFocus = want > 0 ? {key: key, sel: '[data-step="' + step.dataset.step + '"]'} : drawerAfter(row);
      bagSet(key, want);
      return;
    }
    if(e.target.closest('.cart-x')){
      if(!cur) return;
      dFocus = drawerAfter(row);
      bagRemove(key);
    }
  }
  /* Which row control had focus, read before the rebuild destroys it. */
  function drawerMark(){
    var el = d.activeElement;
    if(!dList || !el || !dList.contains(el)) return null;
    var row = el.closest && el.closest('.cart-row');
    if(!row) return null;
    return {
      key: row.dataset.key,
      sel: el.classList.contains('cart-x') ? '.cart-x'
         : el.dataset.step ? '[data-step="' + el.dataset.step + '"]'
         : el.classList.contains('cart-name') ? '.cart-name' : null
    };
  }
  function drawerRefocus(mark){
    /* Truthiness, not !== null: a render that runs before the declaration's
       assignment has been reached sees undefined, not null. */
    var explicit = !!dFocus;
    var key = explicit ? dFocus.key : (mark ? mark.key : null);
    var sel = explicit ? dFocus.sel : (mark ? mark.sel : null);
    dFocus = null;
    if(!explicit && !mark) return;
    var row = null;
    Array.prototype.forEach.call(dList.querySelectorAll('.cart-row'), function(r){
      if(key !== null && r.dataset.key === key) row = r;
    });
    if(!row){
      /* The bag emptied under the press: the empty state has no row to hold
         focus, and the drawer's own close button is the one control left. */
      if(explicit){ var x = d.getElementById('drawerClose'); if(x) x.focus({preventScroll:true}); }
      return;
    }
    var el = sel ? row.querySelector(sel) : null;
    if(el && el.disabled) el = null;
    if(!el && sel && sel.indexOf('data-step') > -1){
      el = [].filter.call(row.querySelectorAll('.qty-btn'), function(b){ return !b.disabled; })[0] || null;
    }
    if(!el) el = row.querySelector('.cart-name');
    if(el) el.focus({preventScroll:true});
  }

  function renderDrawer(){
    if(!dEmpty || !dList || !dFoot) return;
    /* Before anything is hidden or rebuilt: hiding the list drops focus too. */
    var mark = drawerMark();
    var has=bag.length>0, refused=failMsg();
    dEmpty.hidden=has; dList.hidden=!has; dFoot.hidden=!has;
    /* The empty state is margin:auto 0 -- it centres by absorbing free space and
       a second visible sibling would break that composition. */
    if(!has && dRail){ dRail.hidden = true; }
    drawerSay(dEmpty.querySelector('.t-data'), has ? '' : refused);
    if(!has){ drawerRefocus(mark); return; }
    /* Every row, not just the one added. A panel that says "4 pieces" over a
       single line is a panel that is lying about its own contents. */
    dList.innerHTML='';
    bag.forEach(function(r){
      var el=cartRow(r, true);
      if(el) dList.appendChild(el);
    });
    drawerRefocus(mark);
    var c=bagCount(), pc=pieces(c), m=meterState();
    var meta=d.getElementById('drawerMeta');
    if(meta && refused) meta.textContent = refused;
    else if(meta) setBi(meta, pc.en+' · '+money(bagSubtotal())+' '+CFG.money.en,
                              pc.ar+' · '+money(bagSubtotal())+' '+CFG.money.ar);
    var bar=d.getElementById('drawerMeterI'), lab=d.getElementById('drawerMeterT');
    if(bar) bar.style.setProperty('--p', m.pct+'%');
    if(bar) bar.parentNode.classList.toggle('done', m.done);
    if(lab) setBi(lab, m.en, m.ar);
    renderRail();
  }

  /* menuCountEl is declared at module scope near mountHeader, which re-points
     it after a swap; this subscriber reads it by name so it never needs an
     unsubscribe. #cartCount is in chrome-bottom, which is still a snippet. */
  var cartCountEl=d.getElementById('cartCount');
  onBag(function(){
    var c=bagCount();
    if(cartCountEl) cartCountEl.textContent=c;
    if(menuCountEl) menuCountEl.textContent=c;
    renderDrawer();
  });
  /* the drawer's own strings are written by JS, so they need redrawing on a swap */
  window.addEventListener('og:dir', function(){ setTimeout(renderDrawer, 20); });


  /* Non-product queries fail on two thirds of e-commerce sites, and on a
     cash-on-delivery market these four are exactly what an unsure buyer types.
     Track your order was a fifth and went with its rows: with no tracking link
     behind it, helpRow() rendered it .soon and the panel offered a third dead
     row to somebody who had already failed to find what they wanted. */
  var HELP = [
    {en:'Size guide',          ar:'\u062f\u0644\u064a\u0644 \u0627\u0644\u0645\u0642\u0627\u0633\u0627\u062a', href:CFG.help.sizeGuide, keys:'size guide sizing measure fit chart mokas \u0645\u0642\u0627\u0633 \u0645\u0642\u0627\u0633\u0627\u062a \u062f\u0644\u064a\u0644'},
    {en:'Delivery & returns',  ar:'\u0627\u0644\u0634\u062d\u0646 \u0648\u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639',   href:CFG.help.returns,  keys:'delivery shipping returns exchange cod cash on delivery shehn \u0634\u062d\u0646 \u0627\u0633\u062a\u0628\u062f\u0627\u0644 \u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0627\u0644\u062f\u0641\u0639'},
    /* Literals and not CFG lookups: these two handles are set by us, in the
       admin, and there is no merchant setting behind either the way there is
       for the size guide and the returns page. Contact carries the keys the
       WhatsApp row used to answer -- contact, support, help -- because that row
       was the one a shopper looking for a person found, and it went. */
    {en:'About',               ar:'\u0645\u0646 \u0646\u062d\u0646',             href:'/pages/about',      keys:'about who story brand made in egypt cairo an7na \u0645\u0646 \u0646\u062d\u0646 \u0642\u0635\u0629 \u0627\u0644\u0639\u0644\u0627\u0645\u0629'},
    {en:'Contact',             ar:'\u062a\u0648\u0627\u0635\u0644 \u0645\u0639\u0646\u0627', href:'/pages/contact',   keys:'contact support help message email whatsapp wa talk reach tawasol wats \u062a\u0648\u0627\u0635\u0644 \u0627\u062a\u0635\u0644 \u0645\u0633\u0627\u0639\u062f\u0629 \u0631\u0633\u0627\u0644\u0629 \u0627\u064a\u0645\u064a\u0644 \u0648\u0627\u062a\u0633\u0627\u0628'}
  ];
  /* Arabizi: Arabic typed in Latin with digits for the sounds Latin lacks. It is
     how a large share of Egyptian users type on a Latin keyboard, and no amount
     of Arabic support catches it. Twenty hand-written entries covers most of the
     volume at this catalogue size; a transliteration engine would not be worth
     its weight until the range is many times bigger. */
  /* Arabizi -- Arabic typed in Latin with digits for the sounds Latin lacks --
     and Arabic, both mapped onto what this catalogue actually says. The
     reference's table pointed at its own invented vocabulary: 'tee', 'pant',
     'blood', 'cobalt', 'graphite', 'violet'. Not one of those words appears in
     a real product title, so every Arabizi query resolved to a term the store
     has never heard of. The right-hand side below is the live catalogue's own
     wording -- shirt, compression, seamless, shorts, red, grey, blue, purple.

     Keys are normalised at boot rather than written pre-folded. norm() maps
     ي to ى and the alef forms to ا, so an Arabic key typed naturally here
     could never match the folded query it is meant to catch, and would fail
     silently -- the table would simply do nothing for the language it exists
     to serve. */
  var SYN_RAW = {
    'tishirt':'shirt','tshirt':'shirt','tichert':'shirt','t-shirt':'shirt','tee':'shirt',
    'تيشيرت':'shirt','قميص':'shirt',
    'bantalon':'sweatpants','pantalon':'sweatpants','banta':'sweatpants','trousers':'sweatpants',
    'joggers':'sweatpants','pant':'sweatpants','بنطلون':'sweatpants','بنطال':'sweatpants',
    'short':'shorts','shortat':'shorts','شورت':'shorts',
    'kam tawil':'long sleeve','komm':'long sleeve','longsleeve':'long sleeve','كم طويل':'long sleeve',
    'daghet':'compression','dagheta':'compression','ضاغط':'compression','ضغط':'compression',
    'seamless':'seamless','سيملس':'seamless','بدون خياطة':'seamless',
    'tank':'tank','تانك':'tank','حمالات':'tank',
    'a7mar':'red','ahmar':'red','أحمر':'red','احمر':'red',
    'aswad':'black','eswed':'black','أسود':'black','اسود':'black',
    'azra2':'blue','azraq':'blue','أزرق':'blue','ازرق':'blue',
    'ramadi':'grey','gray':'grey','رمادي':'grey',
    'banafsagi':'purple','بنفسجي':'purple',
    'geish':'army','army':'army','زيتي':'army',
    'sadr':'chest','ma2as':'size','mokas':'size','مقاس':'size','se3r':'price','سعر':'price','far3':'branch'
  };
  /* Fold Arabic before matching or the same word typed with a different alef
     will not find itself. Applied to the query and to what it is matched
     against, never to what is displayed. */
  function norm(v){
    return String(v || '').toLowerCase()
      .replace(/[\u064B-\u065F\u0670\u0640]/g, '')      /* diacritics and tatweel */
      .replace(/[\u0623\u0625\u0622]/g, '\u0627')        /* alef variants */
      .replace(/[\u0624]/g, '\u0648')                     /* waw with hamza */
      .replace(/[\u0626\u064A]/g, '\u0649')              /* yeh variants */
      .replace(/[\u0629]/g, '\u0647')                     /* teh marbuta */
      .replace(/[\u0621]/g, '')                            /* standalone hamza */
      .replace(/[\u0660-\u0669]/g, function(c){ return String.fromCharCode(c.charCodeAt(0) - 0x0660 + 48); })
      .replace(/[^\w\u0600-\u06FF ]+/g, ' ')
      .replace(/\s+/g, ' ').trim();
  }
  /* Token by token, not phrase by phrase. Matching whole strings meant a query
     mixing languages found nothing: "tishirt a7mar" would look for the literal
     phrase "tee a7mar", which appears nowhere. Multi-word synonyms are folded
     first, then everything is split and mapped, and a product has to contain
     every token rather than any of them. */
  /* Built once, after norm() is defined, so a key written in natural Arabic is
     folded the same way the query will be and the two can actually meet. */
  var SYN = (function(){
    var out = Object.create(null);
    for(var k in SYN_RAW) out[norm(k)] = SYN_RAW[k];
    return out;
  })();
  function tokens(q){
    var n = norm(q);
    for(var k in SYN){ if(k.indexOf(' ') > -1 && n.indexOf(k) > -1) n = n.split(k).join(SYN[k]); }
    return n.split(' ').filter(Boolean).map(function(t){ return SYN[t] || t; });
  }
  /* The list search actually runs over. Real products when the catalogue blob
     is present, the hardcoded PRODUCTS array when it is not -- a missing or
     malformed payload costs search its results, not the page its JavaScript.
     The two row shapes differ (a catalogue row has tags/type/handle, a
     hardcoded one has sku/colour/cats) so haystack reads whichever it finds. */
  /* Parsed once, above, where the catalogue is built. Search keeps reading the
     raw blob rows rather than the mapped ones because haystack wants tags, type
     and handle, which the PRODUCTS shape does not carry. */
  var CATALOG_ROWS = OG_BLOB ? OG_BLOB.products : null;
  function searchRows(){ return CATALOG_ROWS || PRODUCTS; }

  function haystack(p){
    var extra = p.tags != null
      ? [p.tags, p.type, p.h]
      : [p.sku, p.colour, (p.cats || []).join(' ')];
    return norm([p.en, p.ar, p.cEn, p.cAr].concat(extra).concat([p.price, 'egp']).join(' '));
  }

  /* Suggestions are derived from the catalogue rather than a seed list. The
     reference's list named seven invented terms and, as VERIFICATION recorded,
     missed three of its own nine rows; against a real catalogue a fixed list
     goes stale the first time a product is added. Words come from the titles,
     colourways and tags that are actually there, so a suggestion can never
     point at nothing. */
  var SUGGEST = (function(){
    /* Same reason sizesFromCatalogue's is: these keys are words out of titles,
       colourways and tags, and norm() lowercases them. */
    var seen = Object.create(null), out = [];
    searchRows().forEach(function(p){
      var bits = [p.en, p.cEn, p.tags != null ? p.tags : (p.cats || []).join(' ')];
      norm(bits.join(' ')).split(' ').forEach(function(w){
        if(w.length < 3 || seen[w]) return;
        seen[w] = 1; out.push(w);
      });
    });
    return out.sort();
  })();

  function ogSearch(q){
    var ts = tokens(q);
    if(!ts.length) return {products:[], help:[], queries:[]};
    var hit = function(hay){ return ts.every(function(t){ return hay.indexOf(t) > -1; }); };
    return {
      products: searchRows().filter(function(p){ return hit(haystack(p)); }),
      help:     HELP.filter(function(h){ return hit(norm(h.en + ' ' + h.ar + ' ' + h.keys)); }),
      queries:  ts.length === 1
        ? SUGGEST.filter(function(t){ return t.indexOf(ts[0]) === 0 && t !== ts[0]; }).slice(0, 5)
        : []
    };
  }

  /* ---- focus trap ----
     One implementation, shared. Three overlays needed one and none had it, so
     Tab walked straight out of an open dialog into the page behind it and there
     was no way back except the mouse. Focus goes to the first control on open
     and returns to whatever opened it on close, which is the half people forget.
     Hidden controls are filtered out per call rather than cached, because a
     panel's contents change while it is open. */
  var trapEl=null, trapPrev=null;
  function trapables(){
    return [].filter.call(
      trapEl.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'),
      function(el){ return el.offsetWidth || el.offsetHeight || el.getClientRects().length; });
  }
  function trapKey(e){
    if(e.key !== 'Tab' || !trapEl) return;
    var f = trapables(); if(!f.length) return;
    var first=f[0], last=f[f.length-1];
    if(e.shiftKey && (d.activeElement===first || !trapEl.contains(d.activeElement))){
      e.preventDefault(); last.focus();
    } else if(!e.shiftKey && d.activeElement===last){
      e.preventDefault(); first.focus();
    }
  }
  /* EVERY focus() BELOW PASSES preventScroll, AND ALL THREE MATTER.

     Opening an overlay sets body.locked, which the MutationObserver further
     down turns into lenis.stop(), which puts html{overflow:clip} on the page.
     Restoring focus to the opener afterwards then asks the browser to scroll a
     sticky button into view against a root that has just been un-clipped, and
     it answers by putting the document at 0. Lenis's onNativeScroll commits
     that -- animatedScroll = targetScroll = 0 -- so the page never springs
     back; it simply is at the top now.

     Measured on the collection page at 1200px: scrollY 2400 before opening the
     filter sheet, 2400 with it open, 0 after closing it. With preventScroll,
     2400 throughout. Nothing here is specific to that sheet -- the menu, the
     cart drawer, the quick view and the search overlay all come through trap()
     and untrap(), so all six overlays were doing it.

     A PROGRAMMATIC el.click() CANNOT SEE THIS. It leaves document.activeElement
     on <body>, so trapPrev is <body>, and focusing <body> scrolls nothing. That
     is why it shipped: every existing test clicks that way. The spec that
     covers it uses a real page.click() and says so. */
  var NOSCROLL = {preventScroll:true};
  function trap(el){
    trapPrev = d.activeElement; trapEl = el;
    d.addEventListener('keydown', trapKey, true);
    setTimeout(function(){
      if(!trapEl) return;
      var dlg = trapEl.querySelector('[role="dialog"]');
      if(dlg){ dlg.setAttribute('tabindex','-1'); dlg.focus(NOSCROLL); return; }
      var f = trapables();
      if(f.length) f[0].focus(NOSCROLL);
    }, 80);
  }
  function untrap(){
    d.removeEventListener('keydown', trapKey, true);
    trapEl = null;
    if(trapPrev && trapPrev.focus) trapPrev.focus(NOSCROLL);
    trapPrev = null;
  }

  /* ---- theme editor ----
     Editing a section re-renders its markup, so everything bound per element
     arrives dead. Most of this file is delegated from the document and needs
     nothing; what is left is DOM state and the four initialisers that bind to
     elements directly -- the look carousel, the tab row's drag and edge fades,
     the touch affordances with the raking light, and the marquee wordmark.
     Each is a mount*(box) that re-queries its own roots from the replaced
     section and refuses to bind the same element twice, so calling one again
     costs nothing. main.css has shipped `.design-mode .rv{clip-path:none}` in
     anticipation of this since the port.

     Wrapped in try/catch on purpose: a mount failure should cost the merchant
     one section, not the whole page. It is silent in production and loud in the
     editor, where a developer is the one looking. */
  function mountSection(box){
    if(!box || box.nodeType !== 1) return;
    try{
      applyLang(isAr(), false, box);
      fillCards(box);
      mountCards(box);
      mountCats(box);
      mountSwatches(box);
      mountTabRows(box);
      mountLook(box);
      mountTouch(box);
      Array.prototype.forEach.call(box.querySelectorAll('.rv'), function(el){
        if(rvs.indexOf(el) === -1) rvs.push(el);
      });
      revealCheck();
      if(typeof fillGap === 'function') fillGap();
      var mk = box.querySelector('#marqMark');
      if(mk && !mk.firstChild && typeof WORDMARK === 'string') mk.innerHTML = WORDMARK;
      /* Chrome is sections now, and chrome binds to elements this swap has
         just replaced. Re-mount only what this box actually contains, so
         editing the hero does not restart the ribbon's rotation. */
      if(box.querySelector('#ribbon') || box.id === 'shopify-section-announcement-ribbon') mountRibbon();
      if(box.querySelector('#subForm')) mountFooter();
      if(box.querySelector('#drawer')) mountDrawer();
      if(box.querySelector('#menuBtn') || box.querySelector('.hdr')) mountHeader();
    }catch(err){
      if(CFG.designMode && window.console && console.error)
        console.error('[OG] section mount failed', err);
    }
  }
  function unmountSection(box){
    if(!box || box.nodeType !== 1) return;
    /* Two things outlive a removed section. revealCheck holds detached nodes
       until they satisfy a rect test they never can, and the raking-light
       observer only unobserves a target when it intersects, which a detached
       node never does. */
    for(var i=rvs.length-1; i>=0; i--) if(box.contains(rvs[i])) rvs.splice(i,1);
    if(lio) Array.prototype.forEach.call(box.querySelectorAll('.tile, .card, .pc'), function(el){
      lio.unobserve(el);
    });
  }
  d.addEventListener('shopify:section:load',   function(e){ mountSection(e.target); });
  d.addEventListener('shopify:section:unload', function(e){ unmountSection(e.target); });

  window.OG = {
    add:      bagAdd,
    bagItems: function(){ return bag; },
    cartReady: function(){ return cartQ; },
    goCheckout: goCheckout,
    bagNote:  function(){ return bagNote; },
    bagAdd:   bagAdd,
    bagSet:   bagSet,
    bagRemove:bagRemove,
    bagCount: bagCount,
    subtotal: bagSubtotal,
    delivery: bagDelivery,
    total:    bagTotal,
    onBag:    onBag,
    cartRow:  cartRow,
    meter:    meterState,
    pieces:   pieces,
    money:    money,
    bits:     bits,
    stockFor: stockFor,
    productFor: productFor,
    freeOver: function(){ return FREE_OVER; },
    cdnSet:   cdnSet,
    ladderWide: LADDER_WIDE,
    openCart: openCart,
    quickView:qaOpen,
    isAr:     isAr,
    setBi:    setBi,
    soldOut:  soldOut,
    adoptRow: adoptRow,
    /* The transparent pixel every row falls back to. Exported rather than
       written out a second time: '' in a src re-requests the DOCUMENT as an
       image and null fetches "/null", so the one place that gets this right
       should be the only place that spells it. */
    blankPx:  BLANK_PX,
    /* Reveals have to go through here. A .rv element is clipped to zero height,
       which empties its intersection rect, so IntersectionObserver reports 0 and
       it can never trigger its own reveal. revealCheck reads bounding rects for
       exactly that reason; anything built after load has to join it rather than
       roll its own observer and quietly never appear. */
    reveal:   function(el){ rvs.push(el); revealCheck(); },
    trap:     trap,
    untrap:   untrap,
    products: PRODUCTS,
    /* The sizes the catalogue actually carries. The collection grid derived its
       own literal list before this; one shop, one size run. */
    allSizes: ALL_SIZES,
    help:     HELP,
    search:   ogSearch,
    norm:     norm,
    tokens:   tokens,
    lock:     function(on){ d.body.classList.toggle('locked', !!on); },
    store:    store,
    onResize: onResize,
    /* Kept in step with the reference's surface. The collection template is
       still a stub, so nothing here uses it yet -- but the reference's
       category pills call OG.dragScroll, and the port should not have to add
       it under time pressure later. */
    dragScroll: dragScroll,
    productCard: card,
    inStock:  inStock,
    T:        T,
    swatchHex: swatchHex,
    productUrl: productUrl,
    collectionUrl: collectionUrl,
    cfg:      CFG
  };
})();

(function(){
  if(!window.Lenis) return;
  /* Honour a reduced-motion preference: smoothing scroll is exactly the kind
     of motion that setting is asking us not to add. */
  if(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  /* Lenis banks every wheel delta onto targetScroll -- scrollTo(targetScroll + delta)
     -- so the target tracks where the page is *going*, not where it is. A hard flick
     banks far more than one screen of backlog (measured: 2479px, three viewports).
     Reversing then has to spend that backlog before the page visibly turns around,
     so it keeps travelling the old way while you scroll against it: that is what
     reads as stuck and laggy when you scrub up and down. Answer a reversal from
     where the page actually is, and never let the target run more than one viewport
     ahead. Normal scrolling never reaches either bound, so the feel is unchanged. */
  function clampBacklog(e){
    var lead = lenis.targetScroll - lenis.animatedScroll;
    if(lead !== 0 && e.deltaY !== 0){
      if(Math.sign(e.deltaY) !== Math.sign(lead)){
        lenis.targetScroll = lenis.animatedScroll;
      } else if(Math.abs(lead) > window.innerHeight){
        lenis.targetScroll = lenis.animatedScroll + Math.sign(lead) * window.innerHeight;
      }
    }
    return true;              /* returning false would swallow the event entirely */
  }
  /* duration:1.05 used to be set here, and it never elapsed: fromTo() restarts the
     tween on every wheel event, so what it really set was a fixed per-frame gain --
     an implicit lerp of ~0.111, a 150ms time constant. A fast scrub reverses every
     60-140ms, i.e. faster than the filter settles, so most of the input averaged
     away to nothing: only 20% of commanded travel actually rendered. lerp states
     the time constant directly -- 1/(60*lerp) seconds -- and 0.3 gives 56ms, short
     enough to track a scrub and still visibly a glide. Measured with the clamp
     above: 20% -> 57% of commanded travel rendered at a 100ms half-period. */
  var lenis = new Lenis({
    lerp:0.3, smoothWheel:true, touchMultiplier:1.6,
    virtualScroll: clampBacklog
  });
  window.__lenis = lenis;
  function raf(time){ lenis.raf(time); requestAnimationFrame(raf); }
  requestAnimationFrame(raf);
  /* an open overlay locks the body; Lenis has to stand down with it */
  var body = document.body;
  new MutationObserver(function(){
    body.classList.contains('locked') ? lenis.stop() : lenis.start();
  }).observe(body, {attributes:true, attributeFilter:['class']});
})();

/* ============================================================
   COLLECTION AND SEARCH
   One template, two sources. Reads OG.products, so the catalogue
   lives in one place. Appended to main.js, so it runs everywhere
   and has to say so and stand down.
   ============================================================ */
(function(){
  var d=document, root=d.documentElement;
  var grid=d.getElementById('grid');
  if(!grid || !window.OG) return;                 /* not this page */

  var P = OG.products;
  /* Every IIFE in this file is its own top-level scope: the bundle's first one
     has closed by the time this is evaluated, so naming its locals directly
     throws -- and an uncaught throw here stops the bundle, taking the quick
     view and the product page down with the grid. Anything shared crosses on
     the OG surface, with a fallback so a missing surface degrades rather than
     throwing. */
  var swatchHex = OG.swatchHex || function(id, hex){ return hex; };
  var collectionUrl = OG.collectionUrl;
  var CFG = OG.cfg || {routes:{all:'/collections/all'}, stock:{low:2, productLow:3}, money:{en:'EGP', ar:'\u062c.\u0645'}};
  /* The sizes this shop actually sells, in the order a size runs. The reference
     hardcoded four because it invented four; this catalogue has five and one
     product with none at all. Derived, so a merchant's 2XL reaches the facet
     instead of being silently dropped. */
  var SIZES = (OG.allSizes && OG.allSizes.length) ? OG.allSizes : ['S','M','L','XL'];
  /* Researched batch sizes: 10-30 for a category on a pointer, 15-30 on touch
     loaded in one go, because touch devices fire scroll events too unreliably
     to lazy-load against. Below the batch size the button never appears, which
     is correct and will stay true until the catalogue grows past it. */
  /* 820 is the site's desktop threshold everywhere else; 900 was a fifth
     breakpoint that no longer exists in the stylesheet at all. */
  function batch(){ return window.matchMedia('(min-width:820px)').matches ? 24 : 18; }

  /* The collection on screen, published by main-collection.liquid. The
     reference could hardcode eight because it invented all eight; a store has
     as many as the merchant makes and exactly one of them is being looked at.
     A missing blob degrades to one unnamed collection rather than throwing. */
  /* The collection on screen, published by main-collection.liquid. The
     reference could hardcode eight because it invented all eight; a store has
     as many as the merchant makes and exactly one is being looked at.

     Read through a function, not an IIFE, because mountCollection() runs it
     again after a section swap -- the editor replaces the blob along with
     everything around it, so a value captured once would be the merchant's
     previous edit for the rest of the session. A missing or malformed payload
     degrades to one unnamed collection with no pills and no tiles rather than
     throwing: this IIFE is the whole collection page, and a throw here takes
     the filters, the sheets and the grid down together. */
  var COL, COLLECTIONS, CATS, ED;
  function readCollection(){
    var parsed = null;
    try{
      var el = d.getElementById('og-collection');
      parsed = el ? JSON.parse(el.textContent) : null;
    }catch(e){ parsed = null; }
    COL = parsed || {handle:'', en:'Shop all', ar:'\u062a\u0633\u0648\u0642 \u0627\u0644\u0643\u0644', subEn:'', subAr:'', cats:[]};
    /* No prototype: this map is read with a key that comes off the query
       string, and on a plain object literal `?c=constructor` resolves to the
       Object constructor -- truthy, so the `|| COLLECTIONS['']` fallback below
       never fires and the next line dereferences its missing .sub. */
    COLLECTIONS = Object.create(null);
    COLLECTIONS[''] = {en:COL.en, ar:COL.ar, sub:[COL.subEn || '', COL.subAr || '']};
    if(COL.handle) COLLECTIONS[COL.handle] = COLLECTIONS[''];
    /* The pill row and the sheet's Category facet: real collections the
       merchant picked, whose handles are exactly what og-catalog puts in a
       product's cats. One source, so a pill cannot disagree with the menu. */
    CATS = COL.cats || [];
    /* The editorial tiles, in order. An empty array is the merchant turning the
       interruption off, and the grid closes over it: the tile was never a card,
       so it never entered the slice, never consumed a --i stagger index and
       never touched S.shown or the load-more count. */
    ED = COL.editorial || [];
  }

  var SORTS = [
    {id:'featured',  en:'Featured',        ar:'مختارات'},
    {id:'newest',    en:'Newest',          ar:'الأحدث'},
    {id:'price-asc', en:'Price, low to high',  ar:'السعر: من الأقل'},
    {id:'price-desc',en:'Price, high to low',  ar:'السعر: من الأعلى'}
  ];
  /* Derived from the catalogue rather than listed again, first-wins in
     catalogue order, and resolved through swatchHex so a colourway cannot show
     one colour here and another on a card. Built once: P is the global
     catalogue blob, which a section swap does not touch. */
  var COLOURS = [];
  P.forEach(function(p){
    /* A product whose title carries no " - Colour" tail has no colourway to
       offer. og-catalog gives those rows colour:'default' with an empty label,
       and nine of nineteen products on this store are that shape -- so without
       this the facet's first row was a nameless grey square with a count of 9,
       which is not a filter, it is a question. The reference cannot have this
       row: every one of its invented products has a colourway. */
    if(!p.cEn) return;
    if(!COLOURS.some(function(c){ return c.id===p.colour; }))
      COLOURS.push({id:p.colour, en:p.cEn, ar:p.cAr, hex:swatchHex(p.colour, p.hex)});
  });

  var S = {cat:'', q:'', sizes:[], colours:[], cats:[], stock:false, min:'', max:'', sort:'featured', shown:0};
  var setBi = OG.setBi, norm = OG.norm;
  var $ = function(id){ return d.getElementById(id); };
  var T = OG.T;
  /* The card lives on the shared surface now -- see the hoist comment there.
     The size filter is this page's state, so it travels as an argument. */
  var card = OG.productCard, inStock = OG.inStock;

  function source(){
    if(S.q){
      /* OG.search answers with the catalogue blob's RAW rows -- h rather than
         id, img null for a product with no photo -- and card() wrote that null
         straight into a src. The grid's own rows are the shaped ones, so each
         hit is swapped for its row; a hit with no row (there is none today)
         is kept rather than dropped. */
      var byId = Object.create(null);
      P.forEach(function(x){ byId[x.id] = x; });
      return OG.search(S.q).products.map(function(x){ return byId[x.h || x.id] || x; });
    }
    if(!S.cat) return P.slice();
    return P.filter(function(p){ return p.cats.indexOf(S.cat) > -1; });
  }
  /* A facet's own values are counted with every other facet applied but not
     itself, or ticking one value would zero all the others in the same group. */
  function match(p, skip){
    if(skip!=='size'   && S.sizes.length && !S.sizes.some(function(z){ return p.stock[z]; })) return false;
    if(skip!=='colour' && S.colours.length && S.colours.indexOf(p.colour) === -1) return false;
    if(skip!=='cat'    && S.cats.length && !S.cats.some(function(c){ return p.cats.indexOf(c) > -1; })) return false;
    if(skip!=='stock'  && S.stock && !inStock(p)) return false;
    if(skip!=='price'){
      if(S.min !== '' && p.price < +S.min) return false;
      if(S.max !== '' && p.price > +S.max) return false;
    }
    return true;
  }
  function results(){
    var out = source().filter(function(p){ return match(p); });
    var by = {
      'featured':  function(a,b){ return a.order - b.order; },
      /* On a real creation date, not on the catalogue's position. `order` was a
         hand-assigned recency weight on the nine invented products, so reversing
         it WAS newest-first; it is forloop.index over the merchant's own
         collection order now, which makes reversing it merely the opposite of
         featured -- oldest-first if featured is newest-first, and either way one
         of the two labels is lying. og-catalog publishes created (p.created_at
         as a unix stamp) and rowsFromBlob copies it; nothing read it until now.
         The invented rows carry no created, so NaN falls through to the old
         comparator and that path is unchanged. */
      'newest':    function(a,b){ return (b.created - a.created) || (b.order - a.order); },
      'price-asc': function(a,b){ return a.price - b.price; },
      'price-desc':function(a,b){ return b.price - a.price; }
    }[S.sort];
    /* an unrecognised ?sort= reached out.sort(undefined) */
    out.sort(by || function(a,b){ return a.order - b.order; });
    /* sold out ranks last whatever the sort, and is never hidden */
    /* ONE CARD PER COLOUR. Every row is a card -- a garment in four colours is
       four cards side by side, and a colour filter leaves the colour it asked
       for. The client's call on 16 Oct 2026, reversing 4 Oct's one card per
       garment; the quick view and the product page still hold the garment
       together. Everything that counts reads this -- the count line, Showing X
       of Y, Load more, the editorial tile's threshold -- so all of it counts
       colours, as the menu and every other count on the site now do. */
    return out.filter(inStock).concat(out.filter(function(p){ return !inStock(p); }));
  }
  /* Facet counts are cards too, so "Blue (1)" is one card in the grid. */
  function countFor(kind, value){
    return source().filter(function(p){
      if(!match(p, kind)) return false;
      if(kind==='size')   return p.stock[value] > 0;
      if(kind==='colour') return p.colour === value;
      if(kind==='cat')    return p.cats.indexOf(value) > -1;
      return true;
    }).length;
  }
  /* THE CATEGORIES THIS PAGE CAN BE NARROWED BY. Hashim, 16 Oct 2026, on a
     collection opened from the home page: "a collection from the homepage is
     not a category ... it should show everything in that collection and the
     user can filter on the category he wants." /collections/compressions
     pressed its own name, then offered the same four shirts again as
     Compression and as Tops, then Bottoms 0 and Tanks 0 struck through.

     So a page never offers itself, nor a category holding none of it (a dead
     pill) or all of it (a second All). Counted on source() alone, before any
     facet, so the row keeps its shape while a shopper ticks sizes -- a pill
     the shopper's own filters empty is still struck, as it always was. On
     Shop all every category holds some of the shop and not all of it, so that
     page offers every one, and the parity lanes' pills are untouched. */
  function offered(){
    var src = source(), total = src.length;
    return CATS.filter(function(c){
      if(c.id === S.cat) return false;
      var k = src.filter(function(p){ return p.cats.indexOf(c.id) > -1; }).length;
      return k > 0 && k < total;
    });
  }
  function activeCount(){
    return S.sizes.length + S.colours.length + S.cats.length + (S.stock?1:0) +
           (S.min!==''?1:0) + (S.max!==''?1:0);
  }

  /* ---- url is the state ------------------------------------------
     Filters, sort and depth all live in it. Anything that changes the
     grid without changing the URL is a bug: over 90% of load-more
     implementations break the back button, and the way they break it
     is by keeping state out of the address bar. */
  /* A facet value that this page does not publish is not a filter. The query
     string is typed by anyone, and it outlives the shop: a size the store
     stopped selling, a colourway whose label went blank, a pill the merchant
     deleted. Kept, such a value is counted as active, printed into the count
     line and the chips through innerHTML, and looked up by renderChips() as an
     entry that is not there -- where the undefined it finds throws out of
     apply(), out of boot(), and out of the top-level mountCollection() call,
     taking the rest of the bundle with it. Checked once here against SIZES,
     COLOURS and CATS rather than made safe at five render sites. */
  function only(csv, valid){
    return String(csv || '').split(',').filter(function(v){
      return v && valid.indexOf(v) > -1;
    });
  }
  function idsOf(rows){ return (rows || []).map(function(r){ return r.id; }); }
  function catKeys(){
    var out = [], k;
    for(k in COLLECTIONS) out.push(k);
    return out;
  }
  /* Price is arithmetic, not a label. A non-numeric ?min= filtered nothing --
     every comparison against NaN is false -- while still reading as one active
     filter and printing itself into the chip row. */
  function num(v){
    if(v === null || v === '') return '';
    var n = +v;
    return isFinite(n) ? String(n) : '';
  }
  function readURL(){
    var u = new URLSearchParams(location.search);
    /* On a storefront the collection IS the page, so its handle is the default
       rather than something only ?c= can say, and a value this page does not
       publish is not accepted -- it reaches both a map lookup in header() and
       the grid's own filter in source().

       WHAT AN UNRECOGNISED ONE FALLS BACK TO IS THE PAGE'S OWN HANDLE, not ''.
       The empty string is not "no opinion" here: source() reads it as "no
       category filter" and returns the whole catalogue, while header() still
       resolves it to this collection's title -- so a rejected value rendered
       every product in the shop under one collection's name. The two states
       have to stay distinguishable. Reachable without anyone typing anything:
       writeURL() stamps ?c=<handle> into every URL this page produces, and
       Shopify redirects a renamed handle, so a bookmarked /collections/old?c=old
       arrives at /collections/new carrying a handle that is no longer a key.
       Degrading to COL.handle leaves the page showing the set its header names,
       and writeURL() then rewrites the stale parameter out of the address bar.
       On /collections/all COL.handle is '' and the whole catalogue IS the
       page's own set, so that lane is unchanged. */
    S.cat     = only(u.get('c') || COL.handle || '', catKeys())[0] || COL.handle || '';
    S.q       = u.get('q') || '';
    S.sizes   = only(u.get('size'), SIZES);
    S.colours = only(u.get('colour'), idsOf(COLOURS));
    /* After S.cat and S.q: what a page offers depends on what it holds, and a
       category it does not offer is not a filter -- ?cat=all-tops on a page
       of tops would press a pill that is not on screen. */
    S.cats    = only(u.get('cat'), idsOf(offered()));
    S.stock   = u.get('stock') === '1';
    S.min     = num(u.get('min'));
    S.max     = num(u.get('max'));
    /* The last parameter in this block taken verbatim. results() already
       degraded an unknown one to the featured comparator, so the grid was
       right -- but renderSorts() ticks the option whose id matches, so the
       sheet is a radiogroup with nothing selected over a grid that is in fact
       sorted, and writeURL() re-stamps the bad value into every URL the page
       goes on to produce. */
    S.sort    = only(u.get('sort') || 'featured', idsOf(SORTS))[0] || 'featured';
    S.shown   = Math.max(batch(), +(u.get('n') || 0) || 0);
  }
  function writeURL(push){
    /* keep anything this page does not own -- utm_*, fbclid, a share id --
       rather than rebuilding the query and dropping it on the first filter. */
    var u = new URLSearchParams(location.search);
    ['c','q','size','colour','cat','stock','min','max','sort','n']
      .forEach(function(k){ u.delete(k); });
    if(S.cat) u.set('c', S.cat);
    if(S.q) u.set('q', S.q);
    if(S.sizes.length) u.set('size', S.sizes.join(','));
    if(S.colours.length) u.set('colour', S.colours.join(','));
    if(S.cats.length) u.set('cat', S.cats.join(','));
    if(S.stock) u.set('stock','1');
    if(S.min!=='') u.set('min', S.min);
    if(S.max!=='') u.set('max', S.max);
    if(S.sort!=='featured') u.set('sort', S.sort);
    if(S.shown > batch()) u.set('n', S.shown);
    var url = location.pathname + (u.toString() ? '?' + u : '');
    try{ history[push ? 'pushState' : 'replaceState'](null, '', url); }catch(e){}
  }

  /* ---- render ------------------------------------------------------ */
  function header(){
    var c = COLLECTIONS[S.cat] || COLLECTIONS[''];
    var title = S.q ? T('Results for “','“نتائج ') + S.q + '”' : T(c.en, c.ar);
    $('colTitle').textContent = title;
    $('colCrumb').textContent = S.q ? T('Search','بحث') : T(c.en, c.ar);
    var sub = $('colSub');
    if(S.q){ sub.textContent = ''; sub.hidden = true; }
    else { sub.hidden = !c.sub[0]; sub.textContent = T(c.sub[0], c.sub[1]); }
    d.title = title + ' | OVERGUARD';
  }
  function renderCount(){
    var n = results().length;
    var el = $('colCount');
    var noun = n === 1 ? T('piece','قطعة')
             : n === 2 ? T('pieces','قطعتان')
             : n <= 10 ? T('pieces','قطع') : T('pieces','قطعة');
    /* The size names are the merchant's own option values, so they are written
       as text and never as markup. Every string this file puts on the page from
       the catalogue is set the same way -- an escape helper that has to be
       remembered at each call site is the bug, not the fix. */
    el.innerHTML = '<b></b>';
    el.firstChild.textContent = n;
    el.appendChild(d.createTextNode(' ' + noun +
      (S.sizes.length ? ' ' + T('in','مقاس') + ' ' + S.sizes.join(', ') : '')));
    var fn = $('filterN'), a = activeCount();
    fn.hidden = !a; fn.textContent = a;
    $('fsN').textContent = n;
    /* "Show 1 results" was on screen for as long as this button has existed.
       The count line above already carries the Arabic dual and the two plural
       forms; the button was written once and never counted. setBi rather than
       textContent, so the language toggle finds it like everything else. */
    setBi($('fsUnit'),
      n === 1 ? 'result' : 'results',
      n === 1 ? 'نتيجة' : n === 2 ? 'نتيجتان' : n <= 10 ? 'نتائج' : 'نتيجة');
  }
  /* One tile per interruption, cycled by TILE ordinal -- not by card index.
     The real catalogue is nineteen products and batch() is 24 on a desktop, so
     the grid breaks at i=8 AND at i=16: with one hardcoded tile the live page
     printed the same photograph twice on one screen. Indexing by i instead of n
     would put tiles[0] in both slots whenever there is one tile, and the parity
     lane -- which renders exactly one interruption, ever -- could never tell
     you.

     Built with DOM calls, not innerHTML. Two of these strings are a merchant's
     copy now, and esc() lives inside the search IIFE at the top of this file:
     it is not on the OG surface and cannot be reached from here. textContent is
     right because it cannot be anything else, rather than because somebody
     remembered to escape.

     No data-en/data-ar pair. The og:dir listener at the foot of this IIFE
     re-runs boot(false), which rebuilds the grid through T(); a pair would be a
     second mechanism for one string. card() ships the same way. */
  function editorial(n){
    var e = ED[n % ED.length];
    var a = d.createElement('a');
    a.className = 'ed ch line';
    a.href = e.url || CFG.routes.all;

    /* The <img> is unconditional and its src is never empty. The pixel harness
       masks every img over the finished screenshot, and .ed img is
       width:100%/height:100% inside .ed's fixed aspect-ratio -- so the img's box
       IS the tile's box, caption included. Drop the element on a blank picker,
       or move the photograph to a CSS background, and the slab, its gradient and
       the caption stop being masked and get graded against magenta in all
       thirty-six cells. Liquid guarantees the string. */
    var img = d.createElement('img');
    /* .ed spans the whole grid, so it takes the wide ladder: 2720px at 1440 on
       a 2x screen. A picked file is a CDN URL and gets the ladder; the shipped
       one arrives with its companions already listed in `set`. sizes and set
       before src. */
    var set = (window.OG && OG.cdnSet ? OG.cdnSet(e.img || '', OG.ladderWide) : '') || e.set || '';
    if(set){ img.sizes = 'calc(100vw - 80px)'; img.srcset = set; }
    img.src = e.img || '';
    /* Decorative, permanently: the <h3> is the link's accessible name, and the
       payload carries no alt key for a picked file's own image.alt to leak
       through. */
    img.alt = '';
    img.setAttribute('loading', 'lazy');
    a.appendChild(img);

    var cap = d.createElement('div'); cap.className = 'ed-cap';
    var h3  = d.createElement('h3');  h3.className  = 't-assault';
    h3.textContent = T(e.en, e.ar);
    var note = d.createElement('p');
    note.textContent = T(e.noteEn, e.noteAr);
    cap.appendChild(h3); cap.appendChild(note);
    a.appendChild(cap);
    return a;
  }
  function renderGrid(){
    var r = results(), slice = r.slice(0, S.shown), tile = 0;
    grid.innerHTML = '';
    slice.forEach(function(p, i){
      /* the tile is not a card: it must not eat a slot or shift the batching */
      if(i && i % 8 === 0 && r.length > 8 && ED.length) grid.appendChild(editorial(tile++));
      /* one colour per card, so no row of the others under it */
      grid.appendChild(card(p, i, {sizes: S.sizes, swatches: false}));
    });
    var more = $('colMore'), btn = $('moreBtn');
    var seen = Math.min(S.shown, r.length);
    more.hidden = !r.length;
    btn.hidden = r.length <= S.shown;
    more.classList.toggle('done', btn.hidden);
    if(!btn.hidden) $('moreN').textContent = '(' + (r.length - S.shown) + ')';
    $('colProgI').style.setProperty('--p', (r.length ? seen / r.length * 100 : 100) + '%');
    $('colProgress').innerHTML = T('Showing','معروض') + ' <b>' + seen + '</b> ' +
      T('of','من') + ' <b>' + r.length + '</b>';
    renderZero(r.length);
  }
  function renderZero(n){
    var z = $('colZero');
    if(n){ z.hidden = true; z.innerHTML=''; grid.hidden = false; return; }
    grid.hidden = true; z.hidden = false;
    var emptyCollection = source().length === 0 && !activeCount();
    var sizeOnly = S.sizes.length && activeCount() === S.sizes.length;
    var head, body, acts = '';
    if(emptyCollection){
      head = T('Nothing here yet','لا يوجد شيء هنا بعد');
      body = S.q ? T('No products match that search.','لا توجد منتجات تطابق البحث.')
                 : T('This collection is empty. It will fill with the next drop.','هذه المجموعة فارغة. ستمتلئ مع الإصدار القادم.');
      acts = '<a class="btn ch-sm" href="' + CFG.routes.all + '"><span>' + T('Shop all','تسوق الكل') + '</span></a>';
    } else if(sizeOnly){
      head = T('Nothing in','لا يوجد شيء بمقاس') + ' ' + S.sizes.join(', ');
      body = T('Everything in this collection is out of stock in that size right now.','كل ما في هذه المجموعة غير متوفر بهذا المقاس حاليًا.');
      acts = '<button class="btn ch-sm" data-act="clear-size"><span>' + T('Show all sizes','اعرض كل المقاسات') + '</span></button>';
    } else {
      head = T('No matches','لا نتائج');
      body = T('Nothing fits every filter you have on.','لا يوجد ما يطابق كل عوامل التصفية.');
      acts = (S.sizes.length ? '<button class="btn ch-sm" data-act="clear-size"><span>' + T('Ignore size','تجاهل المقاس') + '</span></button>' : '') +
             '<button class="btn ch-sm" data-act="clear-all"><span>' + T('Clear all filters','مسح كل التصفية') + '</span></button>';
    }
    z.innerHTML = '<svg class="mark" viewBox="0 0 1000 792" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" d="M466.6 772.6C468.5 771.2 468.5 766.7 468.5 441.8L468.5 112.5L466.0 107.3C464.0 103.1 455.8 94.7 423.1 62.8C400.9 41.2 381.4 22.7 379.7 21.8C376.7 20.1 366.4 20.0 203.0 20.0L29.5 20.1L24.0 22.3C17.4 24.9 17.1 26.2 19.4 38.0C21.6 48.6 25.7 58.1 34.3 72.4L41.5 84.5L42.2 107.5C42.7 120.2 43.2 211.1 43.5 309.5C44.1 509.3 43.6 495.9 51.3 511.3C56.7 522.3 70.0 535.7 85.5 545.9C92.9 550.7 122.6 566.2 176.0 592.9C255.4 632.6 278.0 644.4 305.5 660.5C350.2 686.7 389.4 715.0 432.0 752.0C457.9 774.4 461.3 776.4 466.6 772.6Z M510.0 759.6C528.4 741.4 565.3 712.8 593.0 695.3C634.7 669.0 684.5 642.9 764.5 605.5C821.3 579.0 862.5 558.5 873.1 551.5C884.6 543.9 894.0 533.9 898.9 523.9C906.5 508.4 906.2 511.3 906.0 438.7L905.9 373.9L924.2 355.2C945.0 334.0 952.2 324.8 952.4 319.2L952.5 315.5L849.1 315.2C773.5 315.1 745.4 315.3 744.4 316.1C738.8 320.8 747.4 338.2 764.5 356.9C775.8 369.2 775.0 364.1 775.0 423.3C775.0 471.0 774.9 474.8 773.2 476.3C772.3 477.1 761.4 481.9 749.0 486.8C694.3 508.7 659.4 525.5 628.2 545.1C623.0 548.4 618.5 550.8 618.0 550.5C617.1 550.0 617.8 545.9 621.3 532.5C628.0 506.9 627.5 522.2 627.5 323.3C627.5 176.8 627.7 142.9 628.8 141.8C630.8 139.8 783.8 139.4 786.5 141.4C788.4 142.8 788.5 144.5 788.8 193.8C789.0 238.1 789.3 244.9 790.6 245.4C797.9 248.2 909.7 214.0 927.8 203.3L932.2 200.8L931.5 148.0C931.1 119.0 931.0 93.5 931.4 91.4C932.1 88.0 935.1 84.5 954.5 65.0C978.7 40.6 982.3 36.0 981.8 30.2C981.1 21.9 974.3 20.2 936.0 18.9C884.3 17.2 577.0 18.2 574.3 20.0C569.9 23.1 497.4 93.2 495.2 96.5C493.9 98.4 491.6 102.7 490.2 106.2L487.5 112.5L486.8 284.0C486.3 378.3 485.6 482.5 485.2 515.5C483.8 620.9 483.8 771.0 485.2 772.7C486.1 773.9 487.7 774.1 491.4 773.6C496.3 773.1 496.9 772.6 510.0 759.6Z M328.5 544.4C307.3 529.8 284.2 517.3 226.5 489.4C200.1 476.6 177.1 464.9 175.4 463.3L172.2 460.5L171.6 432.0C171.3 416.3 171.3 344.4 171.7 272.1L172.5 140.6L175.8 139.8C181.5 138.3 324.7 139.3 326.2 140.8C327.3 141.9 327.6 176.8 328.0 324.8L328.6 507.5L331.3 519.5C332.7 526.1 334.9 534.6 336.0 538.5C338.1 545.7 338.5 550.0 337.2 550.0C336.8 550.0 332.9 547.5 328.5 544.4Z"/></svg>' +
      '<h3 class="t-assault"></h3><p></p><div class="z-acts">' + acts + '</div>';
    z.querySelector('h3').textContent = head;
    z.querySelector('p').textContent = body;
  }
  function renderChips(){
    var c = $('chips'); c.innerHTML='';
    function chip(label, act){
      var b=d.createElement('button');
      b.className='chip'; b.setAttribute('aria-label', T('Remove filter: ','إزالة التصفية: ') + label);
      b.innerHTML = '<svg class="ic" aria-hidden="true"><use href="#ph-plus"/></svg>';
      b.insertBefore(d.createTextNode(label), b.firstChild);
      b.addEventListener('click', function(){ act(); apply(true); });
      c.appendChild(b);
    }
    S.sizes.forEach(function(z){
      chip(T('Size','مقاس') + ' ' + z, function(){ S.sizes = S.sizes.filter(function(x){ return x!==z; }); });
    });
    S.colours.forEach(function(id){
      var col = COLOURS.filter(function(x){ return x.id===id; })[0];
      chip(T(col.en,col.ar), function(){ S.colours = S.colours.filter(function(x){ return x!==id; }); });
    });
    S.cats.forEach(function(id){
      var cat = CATS.filter(function(x){ return x.id===id; })[0];
      chip(T(cat.en,cat.ar), function(){ S.cats = S.cats.filter(function(x){ return x!==id; }); });
    });
    if(S.stock) chip(T('In stock','متوفر'), function(){ S.stock=false; });
    if(S.min!=='' || S.max!==''){
      chip((S.min||'0') + ' – ' + (S.max||'∞') + ' ' + CFG.money.en, function(){ S.min=''; S.max=''; });
    }
    if(activeCount() > 1){
      var b=d.createElement('button'); b.className='chip clear';
      b.textContent = T('Clear all','مسح الكل');
      b.addEventListener('click', clearAll);
      c.appendChild(b);
    }
  }
  /* Built as nodes, not as a string of markup. Every value in here is a
     merchant's: a size is a product option's own text, a colourway and a
     category are titles. Concatenated, one carrying a '<' rewrote the panel and
     one carrying a quote escaped its own data-v. A label set with textContent
     and an attribute set with setAttribute cannot do either, and there is no
     escape helper for a later row to forget to call. */
  function facetGroup(legend, rows){
    var fs = d.createElement('fieldset'); fs.className = 'facet';
    var lg = d.createElement('legend'); lg.textContent = legend;
    var ul = d.createElement('ul');
    fs.appendChild(lg); fs.appendChild(ul);
    rows.forEach(function(r){
      var li = d.createElement('li'), b = d.createElement('button');
      b.className = 'fv';
      b.dataset.k = r.k; b.dataset.v = r.v;
      b.setAttribute('aria-pressed', String(r.on));
      if(r.n === 0) b.disabled = true;
      var mark;
      if(r.hex){ mark = d.createElement('span'); mark.className = 'sw'; mark.style.setProperty('--c', r.hex); }
      else mark = d.createElement('i');
      var em = d.createElement('em'); em.textContent = r.label;
      b.appendChild(mark); b.appendChild(em);
      if(r.n != null){ var u = d.createElement('u'); u.textContent = r.n; b.appendChild(u); }
      li.appendChild(b); ul.appendChild(li);
    });
    return fs;
  }
  function facetNodes(){
    var frag = d.createDocumentFragment();
    frag.appendChild(facetGroup(T('Size','المقاس'), SIZES.map(function(s){
      return {k:'size', v:s, label:s, on:S.sizes.indexOf(s)>-1, n:countFor('size', s)};
    })));
    frag.appendChild(facetGroup(T('Colour','اللون'), COLOURS.map(function(c){
      return {k:'colour', v:c.id, label:T(c.en,c.ar), hex:c.hex,
              on:S.colours.indexOf(c.id)>-1, n:countFor('colour', c.id)};
    })));
    /* the pills' own list, and no group at all when there is nothing to offer */
    var cats = offered();
    if(cats.length) frag.appendChild(facetGroup(T('Category','الفئة'), cats.map(function(c){
      return {k:'cat', v:c.id, label:T(c.en,c.ar),
              on:S.cats.indexOf(c.id)>-1, n:countFor('cat', c.id)};
    })));
    /* One row, no count: "in stock" is a state rather than a value to tally. */
    frag.appendChild(facetGroup(T('Availability','التوفر'),
      [{k:'stock', v:'1', label:T('In stock only','المتوفر فقط'), on:S.stock, n:null}]));
    var price = d.createElement('fieldset'); price.className = 'facet';
    price.innerHTML = '<legend>' + T('Price','السعر') + '</legend><div class="facet-price">' +
      '<input type="number" inputmode="numeric" data-k="min" value="' + S.min + '" placeholder="' + T('Min','من') + '" aria-label="' + T('Minimum price','أقل سعر') + '">' +
      '<span>–</span>' +
      '<input type="number" inputmode="numeric" data-k="max" value="' + S.max + '" placeholder="' + T('Max','إلى') + '" aria-label="' + T('Maximum price','أعلى سعر') + '">' +
      '</div>';
    frag.appendChild(price);
    return frag;
  }
  function bindFacets(host, live){
    host.addEventListener('click', function(e){
      var b = e.target.closest('.fv'); if(!b || b.disabled) return;
      var k = b.dataset.k, v = b.dataset.v;
      if(k==='size')   S.sizes   = S.sizes.indexOf(v)>-1   ? S.sizes.filter(function(x){return x!==v;})   : S.sizes.concat(v);
      if(k==='colour') S.colours = S.colours.indexOf(v)>-1 ? S.colours.filter(function(x){return x!==v;}) : S.colours.concat(v);
      if(k==='cat')    S.cats    = (S.cats[0]===v) ? [] : [v];   /* single: one category, unlike size and colour */
      if(k==='stock')  S.stock   = !S.stock;
      apply(live);                 /* the rail applies live, the sheet waits */
    });
    /* Rebuilding the facet markup on every keystroke would take the caret with
       it, so a price edit repaints everything except the facets. */
    /* A keystroke is not a navigation. Typing 1500 into Min used to push four
       history entries, so Back deleted the digits one at a time instead of
       leaving the page -- the same broken-back-button the load-more comment
       above is careful about, arriving by a different route. Replace while
       typing, push once the field is committed. */
    host.addEventListener('input', function(e){
      var i = e.target.closest('input[data-k]'); if(!i) return;
      S[i.dataset.k] = i.value;
      apply(live, false, true);
    });
    host.addEventListener('change', function(e){
      var i = e.target.closest('input[data-k]'); if(!i) return;
      S[i.dataset.k] = i.value;
      apply(live, true, true);
    });
  }
  /* ---- category pills ---------------------------------------------
     The same state the sheet's Category facet owns, so the two cannot
     disagree: both read and write S.cats and both re-render from it.

     Single-select, and the facet was changed to match. Two controls over one
     state have to agree about how many values it holds -- a single-select
     pill row over a multi-select facet is exactly the kind of disagreement
     that got the facet rail removed. Category now behaves the way size
     already did: one choice, taken from either control.

     role="group" with aria-pressed toggles, not a tablist (that implies one
     selection and owned panels) and not a radiogroup (single-select). It is
     the same pattern .fv already uses. */
  function renderPills(){
    var host = $('colPills'); if(!host) return;
    /* A page with no category to narrow by draws no row at all: a lone All
       over everything the page holds is a label pretending to be a control. */
    var cats = offered();
    host.hidden = !cats.length;
    if(!cats.length){ host.innerHTML = ''; pillFade(); return; }
    /* everything the other facets allow, before category narrows it */
    var base = source().filter(function(p){ return match(p, 'cat'); }).length;
    /* Nodes for the same reason the facet panel builds them: a pill's label is
       the merchant's collection title. */
    function pill(v, on, label, n, off){
      var b = d.createElement('button');
      b.className = 'col-pill ch-sm line';
      b.dataset.v = v;
      b.setAttribute('aria-pressed', String(on));
      if(off) b.disabled = true;
      var s = d.createElement('span'); s.textContent = label;
      var c = d.createElement('b'); c.textContent = n;
      b.appendChild(s); b.appendChild(c);
      return b;
    }
    host.innerHTML = '';
    /* THE FIRST PILL IS ALWAYS ALL: everything the page holds. On a
       /collections/<handle> page that is the whole collection, which the
       title and the crumb already name. It named the collection itself from
       22 Sep, pressed, which read as a category filter switched on -- and
       Hashim, 16 Oct 2026: "a collection from the homepage is not a
       category ... no filter should be selected". The 22 Sep report it
       answered ("the category isn't selected" over /collections/bottoms) was
       a category reached by its collection URL; categories route through
       ?cat= on Shop all now, where the category's own pill is pressed. */
    host.appendChild(pill('', S.cats.length === 0, T('All', '\u0627\u0644\u0643\u0644'), base, false));
    cats.forEach(function(c){
      var n = countFor('cat', c.id);
      host.appendChild(pill(c.id, S.cats.indexOf(c.id) > -1, T(c.en, c.ar), n, !n));
    });
    pillFade();
  }
  /* Which way there is more to see. RTL reports scrollLeft as negative, hence
     the Math.abs; the 2px tolerance is for sub-pixel scroll positions, and the
     `none` guard stops a row that fits showing a fade at all. */
  function pillFade(){
    var row = $('colPills'); if(!row) return;
    var max = row.scrollWidth - row.clientWidth, x = Math.abs(row.scrollLeft);
    var none = max <= 2, atStart = x <= 2, atEnd = x >= max - 2;
    row.classList.toggle('fade-end',   !none && atStart);
    row.classList.toggle('fade-start', !none && atEnd);
    row.classList.toggle('fade-both',  !none && !atStart && !atEnd);
  }

  /* ---- apply -------------------------------------------------------
     live=false means the grid does not move underneath a sheet that is
     open: refiltering the page mid-selection is disorienting, which is
     why touch gets an explicit Show N results and a pointer does not. */
  function apply(live, push, keepFacets){
    if(live !== false){ S.shown = batch(); renderGrid(); }
    renderCount(); renderChips(); renderPills();
    if(!keepFacets) bindFacetsLight();
    writeURL(push !== false);
  }
  var fsBody = null;   /* re-pointed by mountCollection() */
  function bindFacetsLight(){
    if(!fsBody) return;
    fsBody.innerHTML = '';
    fsBody.appendChild(facetNodes());
    /* one index per group, so the entrance cascade counts real groups rather
       than guessing at how many there are */
    [].forEach.call(fsBody.querySelectorAll('.facet'), function(f, i){
      f.style.setProperty('--n', i);
    });
  }
  function clearAll(){
    S.sizes=[]; S.colours=[]; S.cats=[]; S.stock=false; S.min=''; S.max='';
    apply(true);
  }

  /* ---- sheets, reusing the quick view's panel ---------------------- */
  function sheet(id, scrimId, openerId){
    var el=$(id), scrim=$(scrimId);
    /* Resolved on every use, never captured. The opener lives in
       main-collection and the panel in collection-sheets, and those two sections
       swap independently: editing the title replaces #filterBtn while #fs is
       untouched, so this object is not rebuilt and a captured handle would go on
       writing aria-expanded onto a detached button while the live one told a
       screen reader the dialog was closed. Reassign, don't re-capture -- the
       rule this block's own comment is about, and the one handle in it that was
       still holding a node. */
    function opener(){ return openerId ? $(openerId) : null; }
    function open(){
      /* Never lock the body for a panel that cannot be seen. The CSS above
         should make this unreachable; this is the seatbelt. */
      if(getComputedStyle(el).display === 'none') return;
      var sc = el.querySelector('.qa-scroll');
      if(sc) sc.scrollTop = 0;            /* the filter and sort sheets too */
      el.classList.add('on'); scrim.classList.add('on');
      el.setAttribute('aria-hidden','false'); d.body.classList.add('locked');
      var ob = opener(); if(ob) ob.setAttribute('aria-expanded','true');
      OG.trap(el);
    }
    function shut(){
      if(!el.classList.contains('on')) return;
      el.classList.remove('on'); scrim.classList.remove('on');
      el.setAttribute('aria-hidden','true'); d.body.classList.remove('locked');
      var oc = opener(); if(oc) oc.setAttribute('aria-expanded','false');
      OG.untrap();
    }
    scrim.addEventListener('click', shut);
    el.addEventListener('pointerdown', function(e){ if(e.target===el) shut(); });
    return {open:open, shut:shut, el:el, opener:opener};
  }
  /* The two sheets live in collection-sheets.liquid and their openers live in
     main-collection.liquid -- two sections that swap INDEPENDENTLY in the
     editor. So every guard below is on the element actually being bound, not on
     one element standing in for the pair: editing the title must not leave
     Filter unable to open a sheet that was never replaced.

     Every handler calls fs/ss by name rather than holding fs.open, because a
     swap rebuilds those objects and a captured method would keep opening the
     detached panel. Reassign, don't re-capture -- the rule the chrome sections
     already record. */
  var fs = null, ss = null;
  function bindSheets(){
    /* Release before the old handles go out of scope, the way mountHeader() and
       mountDrawer() do. A merchant editing collection-sheets while a panel is
       OPEN gets the whole subtree replaced: the rebuild below wraps a fresh,
       closed #fs, so nothing is left holding the open one -- body.locked stays
       set, Lenis stays stopped through the MutationObserver, the trap keeps
       intercepting Tab into a detached tree, and Escape cannot undo it because
       shut() returns early on a panel that is not .on. Only a reload could.

       Gated on the node having LEFT the document, not merely on .on: editing
       main-collection also runs this, and there the sheets are untouched and
       may legitimately be open on screen. */
    [fs, ss].forEach(function(s){
      if(!s || !s.el || !s.el.classList.contains('on') || d.contains(s.el)) return;
      d.body.classList.remove('locked');
      OG.untrap();
      /* The opener is in the OTHER section and this swap did not touch it, so
         it is still telling a screen reader the dialog is open. shut() cannot
         correct that later -- the rebuilt panel never had .on -- and it is the
         same per-call resolve shut() uses. */
      var o = s.opener && s.opener();
      if(o) o.setAttribute('aria-expanded','false');
    });
    var fsEl = $('fs'), ssEl = $('ss');
    if(fsEl && !fsEl.__ogSheet){ fsEl.__ogSheet = 1; fs = sheet('fs','fsScrim','filterBtn'); }
    if(ssEl && !ssEl.__ogSheet){ ssEl.__ogSheet = 1; ss = sheet('ss','ssScrim','sortBtn'); }
    bindOnce($('filterBtn'), 'click', function(){ if(fs) fs.open(); });
    bindOnce($('sortBtn'),   'click', function(){ if(ss) ss.open(); });
    bindOnce($('fsClose'),   'click', function(){ if(fs) fs.shut(); });
    bindOnce($('ssClose'),   'click', function(){ if(ss) ss.shut(); });
    bindOnce($('fsClear'),   'click', function(){ clearAll(); });
    bindOnce($('fsApply'),   'click', function(){
      S.shown = batch(); renderGrid(); renderCount(); if(fs) fs.shut();
    });
  }
  function bindOnce(el, ev, fn){
    if(!el || el['__og_' + ev]) return;
    el['__og_' + ev] = 1;
    el.addEventListener(ev, fn);
  }
  d.addEventListener('keydown', function(e){
    if(e.key==='Escape'){ if(fs) fs.shut(); if(ss) ss.shut(); }
  });

  function renderSorts(){
    var b = $('ssBody'); b.innerHTML='';
    SORTS.forEach(function(o){
      var btn=d.createElement('button');
      btn.setAttribute('role','radio'); btn.setAttribute('aria-checked', String(o.id===S.sort));
      btn.style.setProperty('--n', b.children.length);
      btn.textContent = T(o.en,o.ar);
      btn.addEventListener('click', function(){ S.sort=o.id; renderSorts(); apply(true); ss.shut(); });
      b.appendChild(btn);
    });
  }

  /* ---- category pills, load more, grid clicks ---------------------- */
  /* Delegated on the row, because renderPills() replaces its innerHTML on
     every apply() and a handler bound to a pill would go with it. */
  /* Named rather than an IIFE because mountCollection() calls it again: a swap
     replaces #colPills, #moreBtn and #grid with fresh nodes, and these handlers
     would otherwise sit on the detached ones.

     pillFade and barState are deliberately registered OUTSIDE this, once.
     Both are onResize jobs, rzJobs has no removal path, and both already
     survive a swap -- pillFade re-queries #colPills by id on every call, and
     barState reads `bar`, which the mount re-points. Registering them per mount
     stacks one stale writer per merchant edit, which is the trap the chrome
     sections record. */
  function bindGrid(){
    var row = $('colPills');
    if(row && !row.__ogPills){
      row.__ogPills = 1;
      row.addEventListener('click', function(e){
      var b = e.target.closest('.col-pill'); if(!b || b.disabled) return;
      var v = b.dataset.v || '';
      /* Single-select, and the sheet's Category facet matches. S.cats stays an
         array so match(), countFor(), the chips and ?cat= are untouched -- it
         just never holds more than one. Tapping the active pill clears it,
         which is the same toggle the size bar had, and All is the other way
         to the same place. */
      S.cats = (!v || S.cats[0] === v) ? [] : [v];
      apply(true);
    });
      row.addEventListener('scroll', pillFade, {passive:true});
      /* Touch keeps the browser's own momentum; a mouse gets nothing by
         default, which is what makes a narrow desktop window feel stuck. */
      if(OG.dragScroll) OG.dragScroll(row, pillFade);
    }
    bindOnce($('moreBtn'), 'click', function(){
      S.shown += batch(); renderGrid(); renderCount(); writeURL(true);
    });
    bindOnce(grid, 'click', function(e){
      var b = e.target.closest('.pc-add'); if(!b) return;
      e.preventDefault(); e.stopPropagation();
      var host = b.closest('[data-sku]');
      var own = host && host.querySelector('.card-link');
      if(host && OG.quickView) OG.quickView(host.dataset.sku, host.dataset.colour, own && own.getAttribute('href'));
    });
  }
  d.addEventListener('click', function(e){
    var a = e.target.closest('[data-act]'); if(!a) return;
    if(a.dataset.act==='clear-size'){ S.sizes=[]; apply(true); }
    if(a.dataset.act==='clear-all') clearAll();
  });

  /* the strip only changes surface on dock; changing its size would jump the page */
  var bar = null, barTick=null;   /* re-pointed by mountCollection() */
  function barState(){
    if(!bar) return;
    var top = bar.getBoundingClientRect().top;
    var dock = parseFloat(getComputedStyle(bar).top) || 0;
    bar.classList.toggle('stuck', top <= dock + 1);
  }
  window.addEventListener('scroll', function(){
    if(barTick) return;
    barTick = requestAnimationFrame(function(){ barTick=null; barState(); });
  }, {passive:true});
  (OG.onResize || window.addEventListener.bind(window,'resize'))(barState);

  window.addEventListener('popstate', function(){ readURL(); boot(false); });
  window.addEventListener('og:dir', function(){ setTimeout(function(){ boot(false); }, 20); });
  /* Registered once, never per mount: see the note on bindGrid. */
  (OG.onResize || window.addEventListener.bind(window,'resize'))(pillFade);

  function boot(push){
    header(); renderSorts(); apply(true, push); barState();
  }

  /* ---- mount -------------------------------------------------------
     Everything this controller touches lives in a section, and a section swap
     replaces the whole subtree. Before this existed, the grid, the pills, the
     count, the chips and both sheets were captured once at parse time -- so
     the first edit a merchant made in the theme editor left this file writing
     into detached nodes and the page went blank until they reloaded. That is
     not a hypothetical now that the editorial tile is theirs to edit: the tile
     exists only in JavaScript, so the editor is the only place they can see
     what they typed.

     Handles are module-scope variables this re-points; every function reads
     them by name. Bindings are guarded per ELEMENT, so calling this on
     unchanged DOM costs nothing -- which is what lets it be unconditional.

     One host, one behaviour: ticking a facet updates the count on the button
     and nothing else moves until Show N results is pressed. With the grid
     behind a panel there is nothing to watch change, so deferring is right at
     every width rather than only on touch. */
  function mountCollection(){
    var g = d.getElementById('grid');
    if(!g) return;                      /* the sheets section without its page */
    grid   = g;
    bar    = $('colBar');
    fsBody = $('fsBody');
    readCollection();
    bindSheets();
    bindGrid();
    if(fsBody && !fsBody.__ogFacets){ fsBody.__ogFacets = 1; bindFacets(fsBody, false); }
    readURL();
    boot(false);
  }
  mountCollection();

  /* mountSection() lives in the bundle's first IIFE and cannot see any of this,
     so the collection page listens for its own swap. Either section rebuilds
     the whole thing: they share one controller and the cost of doing both is a
     re-render nobody can see. */
  d.addEventListener('shopify:section:load', function(e){
    var box = e.target;
    if(!box || !box.querySelector) return;
    if(box.querySelector('#grid') || box.querySelector('#fs') || box.querySelector('#ss')) {
      try{ mountCollection(); }
      catch(err){
        if(CFG.designMode && window.console && console.error)
          console.error('[OG] collection mount failed', err);
      }
    }
  });
})();

/* ============================================================
   CART
   Appended to main.js, so it loads on every page and has to say so
   and stand down. The bag itself lives in the shared script; this
   is the page that edits it.
   ============================================================ */
(function(){
  var doc = document;
  /* Not a capture: mountCart() re-points it. main-cart.liquid is a section, so
     the theme editor replaces the whole page on every edit -- and render(),
     restoreFocus() and the click delegate all read this by name. */
  var list = doc.getElementById('cartList');
  if(!list || !window.OG) return;              /* not this page */

  var $ = function(id){ return doc.getElementById(id); };
  var T = function(en, ar){ return OG.isAr() ? ar : en; };
  var money = OG.money;
  /* The row renderer's own join, borrowed rather than copied: this page and the
     drawer describe the same variant and must not disagree about a product with
     no colourway. */
  var bits = OG.bits || function(parts, sep){
    return parts.filter(function(v){ return v != null && v !== ''; }).join(sep);
  };
  var CFG = OG.cfg || {routes:{all:'/collections/all'}, stock:{low:2, productLow:3}, money:{en:'EGP', ar:'\u062c.\u0645'}};
  var lastRemoved = null;                      /* one level of undo, this visit only */
  /* The deliberate landing for the next render, or null for "nobody asked".
     One object rather than a bare row key, because a key alone cannot say
     WHICH control: a stepper aims at its own row and its own button, a removal
     aims at the NEXT row's Remove, and `{key:null, sel:'.cart-x'}` -- the last
     row going -- has to mean the heading rather than "nobody asked". Reading
     both from one bare key is what put a "+" press on Remove. */
  var focusTo = null;

  /* money() formats with the one locale setting, which ships en-US. The
     reference writes this sentence's number in Arabic-Indic digits with no
     separator, and that is a property of the Arabic copy rather than of the
     merchant's locale choice, so the Arabic half formats for itself. */
  function moneyAr(n){
    try{ return n.toLocaleString('ar-EG', {useGrouping:false}); }
    catch(e){ return String(n); }
  }
  function egp(v){
    return money(v) + ' <em class="egp">' + T(CFG.money.en, CFG.money.ar) + '</em>';
  }

  /* ---- render ------------------------------------------------- */
  function render(){
    var bag = OG.bagItems();

    /* Before the rebuild, because the rebuild destroys whatever had focus. */
    var mark = focusMark();
    list.innerHTML = '';
    bag.forEach(function(r){
      var el = OG.cartRow(r, true);
      if(el) list.appendChild(el);
    });

    var c = OG.bagCount(), p = OG.pieces(c);
    OG.setBi($('cartCountLine'), p.en, p.ar);

    var m = OG.meter();
    $('cartMeterI').style.setProperty('--p', m.pct + '%');
    $('cartMeterI').parentNode.classList.toggle('done', m.done);
    OG.setBi($('cartMeterT'), m.en, m.ar);

    var del = OG.delivery();
    $('sumSub').innerHTML = egp(OG.subtotal());
    /* Delivery is a line even when it is free and even when it is zero: an
       unexpected fee is the second most cited reason a cart is abandoned, and
       a fee absent from the summary is a fee discovered later. */
    $('sumDel').innerHTML = del ? egp(del) : T('Free','مجاني');
    $('sumTot').innerHTML = egp(OG.total());

    $('cartRail').hidden = !bag.length;
    renderZero(bag.length);
    renderAlert();
    restoreFocus(mark);
  }

  /* ---- three empty states, three answers ---------------------- */
  function renderZero(n){
    var z = $('cartZero');
    if(n){ z.hidden = true; z.innerHTML = ''; return; }
    z.hidden = false;

    var head, body, acts;
    if(lastRemoved){
      head = T('Your bag is empty','سلتك فارغة');
      body = T('You just took the last piece out.','أخرجت آخر قطعة للتو.');
      acts = '<button class="btn ch-sm" data-act="undo"><span>' +
             T('Put it back','أعده') + '</span></button>' +
             '<a class="btn ch-sm" href="' + CFG.routes.all + '"><span>' +
             T('Shop all','تسوق الكل') + '</span></a>';
    } else {
      head = T('Nothing in here yet','لا يوجد شيء هنا بعد');
      body = T('Free delivery over ' + money(OG.freeOver()) + ' ' + CFG.money.en + '.',
               'شحن مجاني فوق ' + moneyAr(OG.freeOver()) + ' ' + CFG.money.ar + '.');
      acts = '<a class="btn ch-sm" href="' + CFG.routes.all + '"><span>' +
             T('Shop the drop','تسوق الإصدار') + '</span></a>';
    }
    z.innerHTML = '<svg class="mark" viewBox="0 0 1000 792" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" d="M466.6 772.6C468.5 771.2 468.5 766.7 468.5 441.8L468.5 112.5L466.0 107.3C464.0 103.1 455.8 94.7 423.1 62.8C400.9 41.2 381.4 22.7 379.7 21.8C376.7 20.1 366.4 20.0 203.0 20.0L29.5 20.1L24.0 22.3C17.4 24.9 17.1 26.2 19.4 38.0C21.6 48.6 25.7 58.1 34.3 72.4L41.5 84.5L42.2 107.5C42.7 120.2 43.2 211.1 43.5 309.5C44.1 509.3 43.6 495.9 51.3 511.3C56.7 522.3 70.0 535.7 85.5 545.9C92.9 550.7 122.6 566.2 176.0 592.9C255.4 632.6 278.0 644.4 305.5 660.5C350.2 686.7 389.4 715.0 432.0 752.0C457.9 774.4 461.3 776.4 466.6 772.6Z M510.0 759.6C528.4 741.4 565.3 712.8 593.0 695.3C634.7 669.0 684.5 642.9 764.5 605.5C821.3 579.0 862.5 558.5 873.1 551.5C884.6 543.9 894.0 533.9 898.9 523.9C906.5 508.4 906.2 511.3 906.0 438.7L905.9 373.9L924.2 355.2C945.0 334.0 952.2 324.8 952.4 319.2L952.5 315.5L849.1 315.2C773.5 315.1 745.4 315.3 744.4 316.1C738.8 320.8 747.4 338.2 764.5 356.9C775.8 369.2 775.0 364.1 775.0 423.3C775.0 471.0 774.9 474.8 773.2 476.3C772.3 477.1 761.4 481.9 749.0 486.8C694.3 508.7 659.4 525.5 628.2 545.1C623.0 548.4 618.5 550.8 618.0 550.5C617.1 550.0 617.8 545.9 621.3 532.5C628.0 506.9 627.5 522.2 627.5 323.3C627.5 176.8 627.7 142.9 628.8 141.8C630.8 139.8 783.8 139.4 786.5 141.4C788.4 142.8 788.5 144.5 788.8 193.8C789.0 238.1 789.3 244.9 790.6 245.4C797.9 248.2 909.7 214.0 927.8 203.3L932.2 200.8L931.5 148.0C931.1 119.0 931.0 93.5 931.4 91.4C932.1 88.0 935.1 84.5 954.5 65.0C978.7 40.6 982.3 36.0 981.8 30.2C981.1 21.9 974.3 20.2 936.0 18.9C884.3 17.2 577.0 18.2 574.3 20.0C569.9 23.1 497.4 93.2 495.2 96.5C493.9 98.4 491.6 102.7 490.2 106.2L487.5 112.5L486.8 284.0C486.3 378.3 485.6 482.5 485.2 515.5C483.8 620.9 483.8 771.0 485.2 772.7C486.1 773.9 487.7 774.1 491.4 773.6C496.3 773.1 496.9 772.6 510.0 759.6Z M328.5 544.4C307.3 529.8 284.2 517.3 226.5 489.4C200.1 476.6 177.1 464.9 175.4 463.3L172.2 460.5L171.6 432.0C171.3 416.3 171.3 344.4 171.7 272.1L172.5 140.6L175.8 139.8C181.5 138.3 324.7 139.3 326.2 140.8C327.3 141.9 327.6 176.8 328.0 324.8L328.6 507.5L331.3 519.5C332.7 526.1 334.9 534.6 336.0 538.5C338.1 545.7 338.5 550.0 337.2 550.0C336.8 550.0 332.9 547.5 328.5 544.4Z"/></svg>' +
      '<h2 class="t-assault">' + head + '</h2><p>' + body + '</p>' +
      '<div class="z-acts">' + acts + '</div>';
  }

  /* ---- what revalidation changed ------------------------------ */
  function renderAlert(){
    var note = OG.bagNote(), box = $('cartAlert');
    if(!note){ box.hidden = true; box.innerHTML = ''; return; }
    box.innerHTML = '';
    /* The colourway and the size, when there are any. Nine of nineteen products
       on this store have no colourway and one has no size, and a fixed join
       printed the separator anyway: "in , M", or "in Black, " for the multipack.
       Same helper cartRow() uses, so the two paths cannot drift apart again. */
    /* A product title and a colourway are a merchant's words, so the name goes
       in as text and the sentence around it is built from nodes -- the rule the
       collection grid follows for the same strings. */
    function line(r, tail){
      var p = OG.productFor(r.sku, r.colour);
      var el = doc.createElement('p'), b = doc.createElement('b');
      b.textContent = p ? T(p.en,p.ar) : r.sku;
      var v = bits([p ? T(p.cEn,p.cAr) : r.colour, r.size], T(', ','، '));
      el.appendChild(b);
      el.appendChild(doc.createTextNode((v ? ' ' + T('in','بلون') + ' ' + v : '') + ' — ' + tail));
      return el;
    }
    note.dropped.forEach(function(r){
      box.appendChild(line(r, T('sold out while it was in your bag, so it has been removed.',
                                'نفد أثناء وجوده في سلتك، لذا تمت إزالته.')));
    });
    note.clamped.forEach(function(r){
      /* name the variant, not just the product: "only 7 left" of what? */
      box.appendChild(line(r, T('only ' + r.qty + ' left, so the quantity was reduced.',
                                'باقي ' + r.qty + ' فقط، فتم تقليل الكمية.')));
    });
    /* An edit the server refused. This is the only string on the page the theme
       did not write -- it is Shopify's own sentence, arriving over the wire --
       so it goes in as text and never as markup. */
    (note.failed || []).forEach(function(f){
      var p = doc.createElement('p');
      p.textContent = f.msg;
      box.appendChild(p);
    });
    box.hidden = false;
  }

  /* ---- editing ------------------------------------------------- */
  /* Both listeners below hang off nodes INSIDE the section, so a swap takes
     them with it. Guarded on the element rather than on a flag: a swap hands
     back new nodes that must be bound again, while a second mount over the same
     nodes must not stack a second handler on them. */
  function bindCart(){
    if(list && !list.__ogCart){ list.__ogCart = 1; bindList(); }
    var go = $('cartGo');
    if(go && !go.__ogCart){ go.__ogCart = 1; bindGo(go); }
  }

  function bindList(){
  list.addEventListener('click', function(e){
    var row = e.target.closest('.cart-row'); if(!row) return;
    var key = row.dataset.key;

    var step = e.target.closest('.qty-btn');
    if(step){
      var now = parseInt(row.querySelector('.qty-n').textContent, 10) || 0;
      var want = now + (+step.dataset.step);
      /* Stepping the last piece out empties the row, so it aims where a
         removal does. Otherwise the row survives and the press aims at itself. */
      focusTo = want > 0 ? {key: key, sel: '[data-step="' + step.dataset.step + '"]'}
                         : afterRow(row);
      OG.bagSet(key, want);
      return;
    }

    if(e.target.closest('.cart-x')){
      /* Removing a row destroys the element that had focus. Decide where it
         goes before the row is gone, not after. */
      focusTo = afterRow(row);
      OG.bagItems().forEach(function(r){
        if(r.sku + '|' + r.colour + '|' + r.size === key) lastRemoved = {
          sku:r.sku, colour:r.colour, size:r.size, qty:r.qty
        };
      });
      OG.bagRemove(key);
    }
  });
  }

  /* Where focus goes once THIS row is gone: the next row's Remove, the one
     before it if this was the last, and the heading if it was the only one.
     Read before the rebuild, while the row is still in the list. */
  function afterRow(row){
    var rows = [].slice.call(list.querySelectorAll('.cart-row'));
    var i = rows.indexOf(row);
    var next = rows[i + 1] || rows[i - 1];
    return {key: next ? next.dataset.key : null, sel: '.cart-x'};
  }

  /* Undo lives in the zero state, which render() rebuilds -- so it is delegated
     from the document and never needs re-binding. */
  doc.addEventListener('click', function(e){
    var b = e.target.closest('[data-act="undo"]');
    if(!b || !lastRemoved) return;
    OG.bagAdd(lastRemoved.sku, lastRemoved.colour, lastRemoved.size, lastRemoved.qty);
    lastRemoved = null;
  });

  /* Which control inside the list had focus, as a row key and a selector.
     render() runs TWICE for every edit now -- once optimistically the moment the
     button is pressed, and again when the server's answer lands -- and the
     second rebuild throws away the control the first one just focused. Without
     this every stepper press ends with focus on <body>, which returns a
     keyboard user to the top of the document mid-edit. The reference never
     needed it because nothing there was asynchronous. */
  function focusMark(){
    var el = doc.activeElement;
    if(!el || !list.contains(el)) return null;
    var row = el.closest && el.closest('.cart-row');
    if(!row) return null;
    return {
      key: row.dataset.key,
      /* The name link too, not just the two controls. It is the third and last
         focusable thing in a row -- the thumb beside it is tabindex="-1" and
         aria-hidden -- and recording nothing for it meant a reconcile render
         had nothing to restore: render() empties the list before this runs, so
         "leave focus where it was" is not available, the node is already gone
         and the browser has already dropped to <body>. cartRow() rebuilds the
         link, so naming it here restores the exact control the shopper was on. */
      sel: el.classList.contains('cart-x') ? '.cart-x'
         : el.dataset.step ? '[data-step="' + el.dataset.step + '"]'
         : el.classList.contains('cart-name') ? '.cart-name' : null
    };
  }
  function restoreFocus(mark){
    /* The seven cases this has to hold at once, so the code below can be read
       against a table rather than against a paragraph:

         1. pressed "+", still enabled        -> that "+"
         2. pressed "+", now disabled at cap  -> that row's "-", never Remove
         3. pressed "-", still enabled        -> that "-"
         4. pressed "-", row went to zero     -> the NEXT row's Remove
         5. pressed Remove                    -> the next row's Remove;
                                                 the heading if it was the last
         6. focus was on the name link and a reconcile render rebuilt the row
                                              -> that row's name link
         7. no deliberate target and no mark  -> leave focus where it is

       Two invariants across all seven: focus never lands on <body>, and a
       STEPPER press never lands on Remove, because the next Enter there would
       delete the line. Cases 1-5 come from focusTo, which the press set; 6 and
       7 come from mark, which is whatever had focus before the rebuild. */
    var explicit = focusTo !== null;
    var key = explicit ? focusTo.key : (mark ? mark.key : null);
    var sel = explicit ? focusTo.sel : (mark ? mark.sel : null);
    focusTo = null;
    var row = (key === null || key === undefined) ? null
            : list.querySelector('[data-key="' + key + '"]');
    if(!row){
      /* Only the deliberate path falls back to the heading (case 5). A row that
         has legitimately gone on a reconcile render should leave focus where it
         is rather than throw it to the top of the page (case 7). */
      if(explicit){ var h = $('cartTitle'); if(h){ h.tabIndex = -1; h.focus(); } }
      return;
    }
    var el = sel ? row.querySelector(sel) : null;
    /* Stepping to the cap disables the very button that was pressed, and
       focusing a disabled control silently drops focus to <body>. */
    if(el && el.disabled) el = null;
    if(!el && sel && sel.indexOf('data-step') > -1){
      /* Case 2. The row's other stepper, which cartRow() keeps enabled -- read
         off the property rather than the markup, since that is what cartRow()
         sets and what the browser refuses focus on. Deliberately not Remove:
         .cart-x is the first control in the row and would otherwise win. */
      el = [].slice.call(row.querySelectorAll('.qty-btn')).filter(function(b){
        return !b.disabled;
      })[0] || null;
    }
    /* Every row has a name link and it is never disabled, so this is the floor
       that keeps focus out of <body> when nothing else resolves. */
    if(!el) el = row.querySelector('.cart-name');
    if(el) el.focus();
  }

  /* The seam, and a real one now. Everything above this button is the theme's;
     everything past it is Shopify's checkout. The reference could only describe
     that boundary, so its handler lit the note underneath and stopped.

     It stays a <button type="button"> rather than becoming a form that posts to
     /cart. A <form> is a node reference/cart.html does not have, and
     tools/dom_parity.py compares the tree node for node -- the redirect costs
     nothing the form would have bought. */
  function bindGo(b){
  /* The leaving itself is OG.goCheckout(), shared with the drawer's button --
     the empty guard, the in-flight guard, the wait on pending cart writes and
     its 2.5s ceiling, the locale-root URL. This keeps only what is THIS page's:
     a disabled button and the lit note under it, so a press visibly took. Lit
     only for a press that counted; an empty bag or a second press does nothing
     here, exactly as before. */
  b.addEventListener('click', function(){
    if(b.disabled) return;
    if(!OG.goCheckout()) return;
    b.disabled = true;
    var s = $('cartSeam');
    if(s) s.classList.add('on');
  });
  }

  /* mountSection() lives in the bundle's FIRST IIFE and cannot see any of this,
     so this page listens for its own swap -- the same arrangement the collection
     controller needed. Without it the first edit a merchant makes in the theme
     editor leaves render() writing into a detached #cartList: the page goes
     blank, stays blank until a reload, and says nothing about why. */
  function mountCart(){
    var el = doc.getElementById('cartList');
    if(!el) return;
    list = el;
    bindCart();
    render();
  }

  bindCart();
  OG.onBag(render);
  window.addEventListener('og:dir', function(){ setTimeout(render, 20); });
  /* The press disables Checkout against a double submit, and a navigation is
     what normally ends that. Coming BACK is the case it does not cover:
     /checkout is same-origin and nothing in this file registers an unload
     listener, so this page is eligible for the back/forward cache and returns
     exactly as it left -- button disabled, seam note lit, no way back into
     checkout but a reload. Registered once at module scope rather than inside
     bindGo, because a section swap hands back a new button and a per-button
     listener would leave one stale writer behind per edit. */
  window.addEventListener('pageshow', function(e){
    if(!e.persisted) return;
    var g = $('cartGo'); if(g) g.disabled = false;
    var s = $('cartSeam'); if(s) s.classList.remove('on');
  });

  doc.addEventListener('shopify:section:load', function(e){
    var box = e.target;
    if(!box || !box.querySelector) return;
    if(!box.querySelector('#cartList')) return;
    try{ mountCart(); }
    catch(err){ if(CFG.designMode) console.error('[OG] cart mount failed', err); }
  });
})();

/* ============================================================
   PRODUCT DETAIL PAGE
   Appended to main.js, so it loads on the homepage too and has to
   say so and stand down. Everything it needs from the shared
   script comes through window.OG.
   ============================================================ */
(function(){
  var d = document, root = d.documentElement;
  var rail = d.getElementById('pdFrames');
  if(!rail) return;                       /* not this page */

  /* This IIFE does not require OG the way the grid and cart ones do, so the
     read is guarded rather than assumed. */
  var CFG = (window.OG && OG.cfg) || {routes:{all:'/collections/all'}, stock:{low:2, productLow:3}, money:{en:'EGP', ar:'\u062c.\u0645'}};

  /* ---- the product, read rather than declared -------------------
     This was a literal: one invented tee, two colourways, pre-formatted
     prices, per-size stock and four reference-relative image paths that 404 on
     a storefront. It is now assembled from the two blobs the page already
     carries.

     #og-catalog is the shop, published on every page: it holds the family key,
     each colourway's id, names, resolved swatch hex, price, compare-at, the
     per-size stock map and -- the part nothing else can supply -- the
     fourteen-digit variant ids the bag posts. #og-product adds only what that
     one deliberately omits, because it ships on /cart and /search too: each
     colourway's gallery, and the merchant's own wording for the button and the
     stock line.

     THE KEYS MUST BE THE CATALOGUE'S. PRODUCT.sku and every colours[i].id are
     copied off the catalogue row rather than re-derived here, because bagAdd
     looks the row up by exactly those two and answers by doing nothing at all
     when it misses. One derivation, in og-catalog.liquid, read everywhere. */
  function readJSON(id){
    var el = d.getElementById(id);
    if(!el) return null;
    try{ return JSON.parse(el.textContent); }catch(e){ return null; }
  }

  /* THE BLOB IS RE-READ, NOT CAPTURED, for the same reason the chrome sections
     re-point their handles: a section swap replaces the subtree, and the swap
     brings a NEW #og-product with it. Everything the merchant can edit in the
     theme editor -- the button's four words, the four stock lines, the refusal
     -- reaches this file only through here, so a blob read once meant the first
     edit rendered correctly from the server and was then written back over by
     syncAdd() from the old one, in the one place those settings can be seen at
     all. Declared once, assigned by readBlob(), read by name everywhere. */
  var BLOB, NAME_EN, NAME_AR, WORDS, STOCKW, SIZES;
  function readBlob(){
    BLOB = readJSON('og-product') || {};
    NAME_EN = BLOB.nameEn || '';
    NAME_AR = BLOB.nameAr || NAME_EN;
    /* The merchant's words, with the two tokens the controller fills. Absent
       when the blob failed to parse, which is what FALLBACK below is for. */
    WORDS = BLOB.labels || {};
    STOCKW = BLOB.stock || {};
    /* The shop's size run comes from the catalogue, which a section swap does
       not re-render -- but the catalogue is a fifty-row window and this product
       may be outside it, so a size only it sells is added here. Without that,
       buildSizes drew no chip for it and inStock counted no stock in it. A COPY:
       OG.allSizes is the array the collection facet reads. */
    var run = (window.OG && OG.allSizes && OG.allSizes.length)
      ? OG.allSizes.slice() : ['S','M','L','XL'];
    var mySizes = (BLOB.own && BLOB.own.sizes) || [];
    for(var i=0;i<mySizes.length;i++){
      if(run.indexOf(mySizes[i]) < 0) run.push(mySizes[i]);
    }
    SIZES = run;
  }
  readBlob();

  function bySort(a, b){
    return ((typeof a.sort === 'number') ? a.sort : Infinity)
         - ((typeof b.sort === 'number') ? b.sort : Infinity);
  }

  function blankPx(){ return (window.OG && OG.blankPx) || ''; }
  /* This product as a catalogue row, from what the page published about itself.
     PRODUCTS' shape exactly, because it is registered into the same lookup the
     bag reads -- and the ids are copied across untouched: `|| 0`, never `| 0`,
     on a number fourteen digits long. */
  function ownRow(){
    var own = BLOB.own;
    if(!own || typeof own.price !== 'number' || !BLOB.sku) return null;
    return {
      id:BLOB.handle || '', sku:BLOB.sku, colour:BLOB.colour || 'default',
      en:NAME_EN, ar:NAME_AR, cEn:own.cEn || '', cAr:own.cAr || own.cEn || '',
      hex:'', sort:(typeof own.sort === 'number') ? own.sort : null,
      price:own.price, was:own.was || 0,
      url:own.url || location.pathname,
      img:own.img || blankPx(), thumb:own.img || blankPx(),
      sizes:own.sizes || [], stock:own.stock || {},
      vid:own.vid || {}, v1:own.v1 || 0, stock1:own.stock1 | 0,
      cats:[], order:0, created:0
    };
  }

  function buildProduct(){
    var frames = BLOB.frames || {};
    var rows = (window.OG && OG.products) || [];
    var byHandle = {};
    for(var i=0;i<rows.length;i++) byHandle[rows[i].id] = rows[i];
    /* THE BLOB NAMES THE FAMILY, not the catalogue scan. #og-product carries one
       entry per product sharing this page's family key, derived in Liquid by the
       same rule og-catalog.liquid uses -- so its keys ARE the colourways this
       page offers, already found, and reading them back is one derivation
       rather than two that can disagree.

       It also settles a disagreement inside the reference itself. Its product
       page ships two colourways of the tee and its catalogue fixture carries
       four; the page is the one this lane photographs, and the page is the one
       that knows which garments it has pictures of. On the real store the
       question never arises, because the loop emits an entry for every sibling
       whether or not it has a photograph. */
    var mine = [];
    Object.keys(frames).forEach(function(h){ if(byHandle[h]) mine.push(byHandle[h]); });
    if(!mine.length){
      for(var j=0;j<rows.length;j++){ if(rows[j].sku === BLOB.sku) mine.push(rows[j]); }
    }
    /* THE PRODUCT THIS PAGE IS FOR IS ALWAYS ONE OF THEM. Everything above
       comes out of the catalogue's fifty-row window, and a garment outside it
       was either missing entirely -- the invented zero row this used to fall
       back to -- or, worse, present only through its siblings: two colourways
       inside the window and this one outside put a SIBLING's colour, price and
       gallery under this product's title, with the swatch it was opened on
       nowhere in the row.

       #og-product publishes this product's own row for exactly this, and
       registering it beside the catalogue is what makes the button real: the
       bag resolves stock and variant ids through productFor, so a row only this
       page knows has to be a row that lookup can find. */
    var own = ownRow();
    if(own){
      var listed = false;
      for(var k=0;k<mine.length;k++){ if(mine[k].id === own.id) listed = true; }
      if(!listed){
        if(window.OG && OG.adoptRow) OG.adoptRow(own);
        mine.push(own);
      }
    }
    /* The merchant's colour order, the same one catalogFrom() applies to the
       quick view and the grid's sibling dots. */
    mine.sort(bySort);
    var colours = mine.map(function(x){
      return {
        /* NOT `|| x.colour`. A product whose title names no colour keys as
           'default', and falling back to the key put the word "default" in the
           rail where the colour name goes and in every swatch's accessible
           name. Empty is the honest answer, and the section hides the row. */
        id:x.colour, en:x.cEn || '', ar:x.cAr || x.cEn || '',
        hex:x.hex || '', handle:x.id, url:x.url || '',
        price:x.price, was:x.was,
        stock:x.stock || {}, sizes:x.sizes || [],
        v1:x.v1, stock1:x.stock1,
        frames:frames[x.id] || []
      };
    });
    /* Neither the catalogue nor the page's own row: a blob that failed to parse,
       which is the only way through here now. buildColours would otherwise
       render an empty swatch row on a page that plainly has a colour.

       price is NULL rather than 0, and syncPrice() leaves the server's own
       markup alone rather than writing a number nothing on this page knows. An
       invented zero is what put "0 EGP" over a correct 1,480. */
    if(!colours.length){
      colours = [{
        id:BLOB.colour || 'default', en:'', ar:'', hex:'', handle:BLOB.handle || '',
        url:location.pathname, price:null, was:0, stock:{}, sizes:[],
        v1:0, stock1:0, frames:frames[BLOB.handle] || []
      }];
    }
    return { sku:BLOB.sku || '', colours:colours };
  }

  var PRODUCT = buildProduct();

  /* Which colourway this URL is. The page was served for ONE product, so the
     row whose handle matches it is the one to open on -- ?c= is only consulted
     when the handle is in no row, which is the shared-link case the reference
     wrote it for. */
  function indexOfHandle(h){
    for(var i=0;i<PRODUCT.colours.length;i++){ if(PRODUCT.colours[i].handle === h) return i; }
    return -1;
  }

  /* One pair per state, because the fallback is read exactly when the blob is
     not: a single 'Add to bag' for all four put that on a disabled button over
     a sold-out product, which is the one state where the words matter most. The
     four are the section's own defaults. */
  var FALLBACK = {
    gone:   ['Sold out', 'نفدت الكمية'],
    pick:   ['Select a size', 'اختر المقاس'],
    buy:    ['Add to bag', 'أضف إلى السلة'],
    notify: ['Notify me', 'أبلغني']
  };
  function say(pair, fallEn, fallAr, n, sz){
    var v = pair || [fallEn, fallAr];
    /* A TOKEN IS ONLY SUBSTITUTED WHERE THERE IS SOMETHING TO PUT IN IT. Five
       of the seven callers name no count and no size -- the button's four
       words, the two calm stock lines, the refusal -- and String.replace
       stringifies its replacement, so a merchant who typed [size] into the
       in-stock line got "In stock in undefined, ships today" under the Add
       button on every product page. The token is left standing instead: it is
       visibly a token, which is a merchant looking at their own copy rather
       than at a word the theme invented.

       [n] and [size], never {n} and {size}: braces inside a Liquid `default:`
       string are refused by the server with "Variable was not properly
       terminated", which is why the tokens are spelled this way. */
    return [String(v[0] == null ? fallEn : v[0]), String(v[1] == null ? fallAr : v[1])].map(function(t){
      if(n != null) t = t.replace(/\[n\]/g, n);
      if(sz != null) t = t.replace(/\[size\]/g, sz);
      return t;
    });
  }

  var col = 0, size = null;
  /* The pair of words an add the shop refused leaves behind. Cleared by the
     next thing the shopper does, so it never outlives the state it describes. */
  var failed = null;
  var $ = function(id){ return d.getElementById(id); };
  /* Module scope, re-POINTED by mountProduct() and never re-captured -- the
     rule all three chrome sections and the collection follow. Two sections feed
     this controller now, main-product and product-buybar, so every bar handle
     is guarded on itself rather than on one standing in for the pair. */
  var frames, count, colours, sizes, colourName, stockEl, addBtn, addLabel, priceEl,
      mbody, bar, bbAdd, bbLabel, bbVar, bbPrice, pdPrev, pdNext,
      fitBox, fitH, fitW, fitHv, fitWv, fitFits, fitSize, fitWhy, fitPick, fitPickL, fitErr;
  function grab(){
    frames=$('pdFrames'); count=$('pdCount'); colours=$('pdColours'); sizes=$('pdSizes');
    colourName=$('pdColourName'); stockEl=$('pdStock'); addBtn=$('pdAdd'); addLabel=$('pdAddLabel');
    priceEl=$('pdPrice'); mbody=$('mtabBody'); bar=$('buybar'); bbAdd=$('bbAdd');
    bbLabel=$('bbLabel'); bbVar=$('bbVar'); bbPrice=$('bbPrice');
    pdPrev=$('pdPrev'); pdNext=$('pdNext');
    fitBox=$('pdFinder'); fitH=$('fitH'); fitW=$('fitW'); fitHv=$('fitHv'); fitWv=$('fitWv');
    fitFits=$('fitFits'); fitSize=$('fitSize'); fitWhy=$('fitWhy'); fitPick=$('fitPick');
    fitPickL=$('fitPickL'); fitErr=$('fitErr');
  }
  grab();
  var isAr  = function(){ return (window.OG && OG.isAr) ? OG.isAr() : root.dir==='rtl'; };
  /* The fallback strips the same way OG.setBi does: a value that reaches
     data-en without it is one applyLang will hand to innerHTML. */
  var setBi = function(el,en,ar){
    if(window.OG && OG.setBi) return OG.setBi(el,en,ar);
    en = String(en == null ? '' : en).replace(/<[^>]*>/g, '');
    ar = String(ar == null ? '' : ar).replace(/<[^>]*>/g, '');
    el.dataset.en=en; el.dataset.ar=ar; el.textContent=isAr()?ar:en;
  };

  /* ---- gallery -------------------------------------------------
     Rebuilt per colourway rather than hidden and shown: the frames
     are not equivalent between colours, so holding the index across
     a change would land you on a different shot than you were on. */
  function buildGallery(){
    var c = PRODUCT.colours[col];
    frames.innerHTML='';
    c.frames.forEach(function(f,i){
      var fig=d.createElement('figure'), im=d.createElement('img');
      fig.className='rv';                 /* the site's own clip reveal */
      /* The frame is 4:5 and the photograph covers it, so a square upload is
         drawn by the frame's HEIGHT: 570px wide in a 456px frame at 820. The
         frame's height is about the viewport's less the header from 820px, and
         1.25 x its width below that. theme-hd.spec.js caught 46vw handing a 2x
         screen 900px where 1140 were needed. sizes and set before src; the
         parity frames are shipped files and get no set. */
      var set = window.OG && OG.cdnSet ? OG.cdnSet(f[0]) : '';
      if(set){ im.sizes = '(min-width:820px) calc(100vh - 96px), 125vw'; im.srcset = set; }
      im.src=f[0];
      /* Joined only where there is something to join. Every piece but the name
         is optional on this store -- nine products name no colour and NO image
         anywhere carries alt text -- so the fixed separators produced
         "Guardian Seamless Compression Shirt, Purple — " with a dangling em
         dash on every frame, and "OG Lifters Tank,  — " on the colourless ones.
         That string is what a screen reader reads and what Google indexes. */
      im.alt=[isAr()?NAME_AR:NAME_EN, isAr()?c.ar:c.en].filter(Boolean).join(', ')
             + (f[1] ? ' — ' + f[1] : '');
      im.loading = i ? 'lazy' : 'eager';
      if(!i) im.setAttribute('fetchpriority','high');
      fig.appendChild(im); frames.appendChild(fig);
      /* Stacked, they arrive as you reach them. Swiped, they are all at the same
         height, so revealing them one by one would mean revealing them all at
         once with extra steps. */
      if(window.matchMedia('(min-width:820px)').matches && window.OG && OG.reveal) OG.reveal(fig);
      else fig.classList.add('in');
    });
    frames.scrollLeft = 0;
    if(count) count.querySelector('i').textContent = c.frames.length;
    navPos(); syncCount();
    /* the frames have no height until the first image decodes */
    var first = frames.querySelector('img');
    if(first && !first.complete) first.addEventListener('load', navPos, {once:true});
  }
  function syncCount(){
    if(!count) return;
    var w = frames.clientWidth; if(!w) return;
    count.querySelector('b').textContent = frameIndex() + 1;
  }
  function frameIndex(){
    var w = frames.clientWidth;
    return w ? Math.round(Math.abs(frames.scrollLeft)/w) : 0;
  }
  /* Centre the arrows on the picture, not on the gallery box, which is taller
     by the model caption. Measured rather than assumed: 125vw is only exact
     while no scrollbar is eating into the viewport width. */
  function navPos(){
    var g = frames.parentNode;
    if(frames.clientHeight) g.style.setProperty('--fh', frames.clientHeight + 'px');
  }
  /* RTL scroll containers count scrollLeft down from zero, so the target has to
     carry the direction with it. Looping matches the quick view's gallery. */
  function pdGo(step){
    var n = frames.children.length, w = frames.clientWidth;
    if(!n || !w) return;
    var i = ((frameIndex() + step) % n + n) % n;
    /* same story as the quick view's gallery: a start-aligned pixel offset is
       not one of this scroller's centre-aligned snap points, so it snapped
       straight back. Measure and move by the difference, as the look carousel
       does -- relative, so RTL needs no sign of its own. */
    var fig = frames.children[i];
    if(fig){
      var tr = frames.getBoundingClientRect(), er = fig.getBoundingClientRect();
      frames.scrollTo({
        left: frames.scrollLeft + (er.left + er.width/2) - (tr.left + tr.width/2),
        behavior:'smooth'
      });
    }
    if(count) count.querySelector('b').textContent = i + 1;
  }

  /* ---- colour --------------------------------------------------- */
  function buildColours(){
    colours.innerHTML='';
    PRODUCT.colours.forEach(function(c,i){
      var b=d.createElement('button');
      b.style.setProperty('--c', (window.OG && OG.swatchHex) ? OG.swatchHex(c.id, c.hex) : c.hex);
      /* The accessible name through the bilingual channel, not as a bare
         attribute: aria-label is not a text node, so the language swap never
         reached it and every swatch on the site stayed English in Arabic.
         data-al-* is the pair applyLang writes onto aria-label. */
      if(c.en){
        b.setAttribute('aria-label', isAr() ? c.ar : c.en);
        b.dataset.alEn = c.en; b.dataset.alAr = c.ar;
      }
      b.setAttribute('aria-pressed', String(i===col));
      if(!inStock(c)) b.className='gone';
      b.addEventListener('click', function(){ pickColour(i, true); });
      colours.appendChild(b);
    });
  }
  /* "No Size option" and "no size left" are different facts, and reading the
     size map alone told the one product on this store that has neither that it
     was sold out whatever its stock said. A colourway with no sizes answers
     from its single variant, which is the same partition catalogFrom() draws
     for the quick view. */
  function sizeless(c){ return !c.sizes || !c.sizes.length; }
  function inStock(c){
    if(sizeless(c)) return (c.stock1 | 0) > 0;
    return SIZES.some(function(s){ return c.stock[s] > 0; });
  }

  function pickColour(i, pushed){
    col = i; size = null; failed = null;
    var c = PRODUCT.colours[col];
    buildGallery(); buildColours(); buildSizes(); syncAdd(); syncTable(); fitRun();
    setBi(colourName, c.en, c.ar);
    if(!pushed) return;
    /* EACH COLOURWAY IS ITS OWN PRODUCT, so it has a real page of its own and
       the address bar should say so: the swap is in-place, but the URL that
       comes out of it is one a shopper can copy, share, reload and land on
       exactly here. history.pushState rather than replaceState, so Back walks
       the colours the way it walks anything else.

       The row's own url is used when the catalogue published one. Falling back
       to ?c= keeps the reference's behaviour for a colourway with no page --
       and it keeps the REST of the query string, which the original rebuilt
       from one value and so destroyed every utm_ on a campaign link. */
    try{
      /* c is rewritten on BOTH branches, not only the fallback. Carried along
         unchanged it would name the colour that was showing when the link was
         opened, so the address after a swatch would say one colour in its path
         and another in its query -- and a reload would take the query, which
         reads as the swatch undoing itself. */
      var u = new URLSearchParams(location.search);
      u.set('c', c.id);
      var q = u.toString();
      history.pushState(null, '', (c.url || location.pathname) + (q ? '?' + q : '') + location.hash);
    }catch(e){}
  }

  /* ---- size ----------------------------------------------------- */
  function buildSizes(){
    var c = PRODUCT.colours[col];
    sizes.innerHTML='';
    /* .qa-sizes is repeat(4,1fr) in the stylesheet, which is what the design
       was drawn on -- four sizes. This store runs to 2XL, so a fifth chip wraps
       onto a row of its own. Written inline and ONLY when the count is not
       four, so the reference case sets no style at all and the shipped CSS is
       untouched: a rule in the stylesheet would match the parity lane too. */
    sizes.style.gridTemplateColumns = (SIZES.length === 4) ? '' : 'repeat(' + SIZES.length + ',1fr)';
    if(sizeless(c)) return;
    SIZES.forEach(function(s){
      var b=d.createElement('button');
      b.textContent=s;
      b.setAttribute('aria-pressed', String(size===s));
      if(!c.stock[s]) b.disabled = true;
      else b.addEventListener('click', function(){ pickSize((size===s) ? null : s); });
      sizes.appendChild(b);
    });
  }
  /* The one way a size is set: a chip press, which toggles, and the size
     finder's button, which never does -- pressing "Choose M" with M already
     chosen must not un-choose it. */
  function pickSize(s){
    size = s;
    /* Choosing a size is the next thing the shopper does, so the last
       refusal stops describing a press that is no longer on screen -- and
       syncAdd() can show this size's own stock line again. */
    failed = null;
    buildSizes(); syncAdd(); syncTable(); fitRun();
  }

  /* The label names the step that is missing, which is the pattern the
     quick view already uses. Out of stock is a different action, not a
     disabled button with no way forward. */
  function money(v){ return (window.OG && OG.money) ? OG.money(v) : String(v); }

  /* The price the rail shows was static markup the script never touched, while
     the sticky bar's was rewritten on every colour change -- so a family whose
     colourways are not all priced the same could show one number in the rail
     and another in the bar, on a page whose whole job is ordering the right
     thing. Both are written from the same row now. innerHTML rather than
     textContent, because the shape is <s>was</s>now <em>EGP</em> and the
     strikethrough comes and goes with the row. */
  function syncPrice(c){
    if(!priceEl || c.price == null) return;
    /* The currency mark is a free-text setting, and the server writes it into
       this same element through `| escape`. Written as TEXT into the <em> the
       markup already has rather than interpolated, so the two paths agree and a
       label carrying markup stays inert after the first colour change. */
    priceEl.innerHTML = (c.was && c.was > c.price ? '<s>' + money(c.was) + '</s>' : '')
      + money(c.price) + ' <em></em>';
    var mark = priceEl.querySelector('em');
    if(mark) mark.textContent = String(BLOB.money || 'EGP');
  }

  function syncAdd(){
    var c = PRODUCT.colours[col];
    var solo = sizeless(c);
    var left = solo ? (c.stock1 | 0) : (size ? c.stock[size] : null);
    var chosen = solo || !!size;
    var state = !inStock(c) ? 'gone' : !chosen ? 'pick' : left ? 'buy' : 'notify';
    var label = say(WORDS[state], FALLBACK[state][0], FALLBACK[state][1]);
    addBtn.disabled = (state==='gone' || state==='pick');
    setBi(addLabel, label[0], label[1]);
    syncPrice(c);
    if(bbLabel){
      setBi(bbLabel, label[0], label[1]);
      /* NOT addBtn.disabled, deliberately -- see product-buybar.liquid's header.
         The two buttons share a label and not a disabled state: the real one
         sits directly under the chips, so leaving it live in 'pick' would be a
         control that cannot act, while the bar is the only buy affordance on a
         screen where the chips are not visible, so 'no size yet' is a
         destination for it and the handler below routes there. Only a garment
         nobody can buy takes the bar out. */
      if(bbAdd) bbAdd.disabled = (state === 'gone');
      bbDock();
      /* Empty until there is a size to price. Before one is chosen the figure
         describes no variant the shopper has asked for, and it sat beside a
         button reading "Select a size" -- a number answering a question nobody
         had reached. `solo` is the exception and not an oversight: a garment
         with no Size option at all has exactly one price from the start, and
         waiting for a choice that never comes would hide it forever.
         Emptied rather than hidden here, because the currency beside it is
         rendered by Liquid and this function cannot see it; the CSS hides the
         pair off :empty, which is the idiom .pdp-stock already uses. */
      if(bbPrice) bbPrice.textContent =
        (solo || size) && c.price != null ? money(c.price) : '';
      if(bbVar){
        var cn = isAr() ? c.ar : c.en;
        var pick = say(WORDS.pick, 'Select a size', 'اختر المقاس');
        bbVar.textContent = solo ? cn
          : size ? cn + ' · ' + size
                 : cn + ' · ' + (isAr() ? pick[1] : pick[0]);
      }
    }
    /* Stock urgency only when it is true, and only about the chosen size. An
       add the shop refused outranks all of it until the next interaction --
       there was no error surface here at all, so a 422 was an optimistic row,
       an open drawer and a silent rollback. */
    if(failed){ setBi(stockEl, failed[0], failed[1]); stockEl.classList.remove('calm'); return; }
    var sz = solo ? '' : size;
    if(state==='buy' && left <= CFG.stock.productLow){
      /* A product with no Size option gets the sentence written for it. The
         sized string names a size in both languages -- "left in [size]",
         "مقاس [size]" -- and filling that token with an empty string ended the
         line mid-clause: "Only 2 left in ". Trimming the clause in the
         controller is not the answer either, because Arabic puts it somewhere
         English cannot; the shop writes two sentences instead. */
      var lo = solo
        ? say(STOCKW.lowSolo, 'Only [n] left', 'باقي [n] فقط', left)
        : say(STOCKW.low, 'Only [n] left in [size]', 'باقي [n] فقط مقاس [size]', left, sz);
      setBi(stockEl, lo[0], lo[1]); stockEl.classList.remove('calm');
    } else if(state==='notify'){
      var ou = say(STOCKW.out, '[size] is sold out', 'مقاس [size] نفد', left, sz);
      setBi(stockEl, ou[0], ou[1]); stockEl.classList.remove('calm');
    } else if(!inStock(c)){
      var gn = say(STOCKW.gone, 'Sold out', 'نفدت الكمية');
      setBi(stockEl, gn[0], gn[1]); stockEl.classList.remove('calm');
    } else {
      var ok = say(STOCKW['in'], 'In stock, ships today', 'متوفر، يُشحن اليوم');
      setBi(stockEl, ok[0], ok[1]); stockEl.classList.add('calm');
    }
  }

  /* ---- the table follows the chosen size ------------------------ */
  function syncTable(){
    if(!mbody) return;
    Array.prototype.forEach.call(mbody.rows, function(r){
      r.classList.toggle('on', !!size && r.dataset.size===size);
    });
  }

  /* ---- the size finder ------------------------------------------
     The shop's own height-and-weight sheet, from the product section's Size
     finder settings (snippets/size-finder.liquid publishes it as #og-fit): a
     grid whose first row is the heights and whose every other row is a weight
     followed by the size for each height. It began as the client's own section,
     sections/size-calculator.liquid, which nothing could render -- this
     template is Liquid, so the editor cannot add a section to it -- and whose
     grid lived in its script.

     The answer is the nearest grid point -- the sliders run a step past the
     grid at each end, so a value off the table takes its edge -- moved one
     size down or up the grid's own sizes for a tight or a relaxed fit, and
     then mapped onto a size THIS colourway is made in. The Guardians start at
     M; they are never told to buy an S, they are told why not. The reference
     page carries the same controller, minus the editor note. */
  var LADDER = ['XXS','XS','S','M','L','XL','2XL','3XL','4XL','5XL'];
  var fitGrid = null, fitMove = 0;
  function norm(s){
    s = String(s == null ? '' : s).trim().toUpperCase();
    var x = s.match(/^(X+)L$/);
    return (x && x[1].length > 1) ? x[1].length + 'XL' : s;
  }
  function rank(s){ var i = LADDER.indexOf(norm(s)); return i < 0 ? 99 : i; }
  /* Commas or tabs, so a range copied straight out of a spreadsheet pastes
     as it is; a ragged row is refused rather than guessed at. */
  function parseGrid(raw){
    var cut = function(l){
      var c = l.split(/\t|,/).map(function(x){ return x.trim(); });
      while(c.length && c[c.length-1] === '') c.pop();
      return c;
    };
    var lines = String(raw || '').split(/\r?\n/).filter(function(l){ return l.trim(); });
    if(lines.length < 2) return { bad: lines[0] || '' };
    var hs = cut(lines[0]).slice(1).map(parseFloat);
    if(!hs.length || hs.some(isNaN)) return { bad: lines[0] };
    var rows = [];
    for(var i=1;i<lines.length;i++){
      var c = cut(lines[i]), w = parseFloat(c[0]), got = c.slice(1);
      if(isNaN(w) || got.length !== hs.length || got.some(function(x){ return !x; })) return { bad: lines[i] };
      rows.push({ w:w, s:got });
    }
    var all = [];
    rows.forEach(function(r){ r.s.forEach(function(x){ if(all.indexOf(norm(x)) < 0) all.push(norm(x)); }); });
    all.sort(function(a,b){ return rank(a) - rank(b); });
    return { hs:hs, rows:rows, sizes:all };
  }
  /* nearest value, a tie going to the larger, which is Math.round's way */
  function near(list, v){
    var b = 0;
    for(var i=1;i<list.length;i++){
      var di = Math.abs(list[i]-v), db = Math.abs(list[b]-v);
      if(di < db || (di === db && list[i] > list[b])) b = i;
    }
    return b;
  }
  function lookup(h, w){
    var r = fitGrid.rows[near(fitGrid.rows.map(function(x){ return x.w; }), w)];
    return norm(r.s[near(fitGrid.hs, h)]);
  }
  function shift(sz, by){
    var i = fitGrid.sizes.indexOf(sz) + by;
    return fitGrid.sizes[Math.max(0, Math.min(fitGrid.sizes.length - 1, i))];
  }
  /* The garment's own size, spelt as its chip is -- the variant may say XXL
     where the grid says 2XL, and pickSize() needs the chip's own word. */
  function madeIn(want, made){
    var hit = null, best = null;
    made.forEach(function(s){
      if(norm(s) === want) hit = s;
      var dd = Math.abs(rank(s) - rank(want)), bd = best == null ? 1e9 : Math.abs(rank(best) - rank(want));
      if(dd < bd || (dd === bd && rank(s) > rank(best))) best = s;
    });
    return hit || best;
  }
  function num(n, ar){
    if(!ar) return String(n);
    try{ return n.toLocaleString('ar-EG', {useGrouping:false}); }catch(e){ return String(n); }
  }
  var FIT_EN = ['extra tight fit','standard fit','relaxed fit'],
      FIT_AR = ['\u0642\u064e\u0635\u0651\u0629 \u0636\u064a\u0651\u0642\u0629 \u062c\u062f\u064b\u0627','\u0642\u064e\u0635\u0651\u0629 \u0639\u0627\u062f\u064a\u0629','\u0642\u064e\u0635\u0651\u0629 \u0645\u0631\u064a\u062d\u0629'];
  function track(el){
    var lo = +el.min, hi = +el.max;
    el.style.setProperty('--p', ((+el.value - lo) / ((hi - lo) || 1) * 100) + '%');
  }
  /* Read on every mount, as the product blob is: a section swap brings a new
     #og-fit with whatever grid the merchant just typed. */
  function fitRead(){
    fitGrid = null; fitMove = 0;
    if(!fitBox) return;
    var raw = null;
    var el = d.getElementById('og-fit');
    try{ raw = JSON.parse(el.textContent).grid; }catch(e){}
    fitGrid = parseGrid(raw);
    if(fitGrid.bad != null){
      /* Only the theme editor is told, and it is told which row: a shopper
         just never sees the finder. fitErr is rendered in design mode alone. */
      if(fitErr){
        fitErr.textContent = 'The size finder is off: this row of its grid does not have one size for '
          + 'each height in the first row -- "' + fitGrid.bad + '". This line is only visible in the theme editor.';
        fitErr.hidden = false;
      }
      return;
    }
    var ws = fitGrid.rows.map(function(r){ return r.w; });
    fitH.min = Math.min.apply(null, fitGrid.hs) - 5; fitH.max = Math.max.apply(null, fitGrid.hs) + 5;
    fitW.min = Math.min.apply(null, ws) - 5;         fitW.max = Math.max.apply(null, ws) + 5;
    [].forEach.call(fitFits.querySelectorAll('button'), function(x){
      x.setAttribute('aria-pressed', String(x.dataset.fit === '0'));
    });
  }
  function fitRun(){
    if(!fitBox || !fitGrid || fitGrid.bad != null) return;
    var c = PRODUCT.colours[col];
    var made = sizeless(c) ? [] : c.sizes;
    var reach = made.some(function(s){ return fitGrid.sizes.indexOf(norm(s)) > -1; });
    fitBox.hidden = !reach;
    if(!reach) return;
    var h = +fitH.value, w = +fitW.value;
    track(fitH); track(fitW);
    setBi(fitHv, h + ' cm', num(h, 1) + ' \u0633\u0645');
    setBi(fitWv, w + ' kg', num(w, 1) + ' \u0643\u062c\u0645');
    fitH.setAttribute('aria-valuetext', isAr() ? fitHv.dataset.ar : fitHv.dataset.en);
    fitW.setAttribute('aria-valuetext', isAr() ? fitWv.dataset.ar : fitWv.dataset.en);
    var want = shift(lookup(h, w), fitMove), got = madeIn(want, made);
    var en = h + ' cm, ' + w + ' kg, ' + FIT_EN[fitMove + 1] + '.',
        ar = num(h, 1) + ' \u0633\u0645\u060c ' + num(w, 1) + ' \u0643\u062c\u0645\u060c ' + FIT_AR[fitMove + 1] + '.';
    if(norm(got) !== want){
      if(rank(got) > rank(want)){
        en += ' We\'d say ' + want + '; this piece starts at ' + got + '.';
        ar += ' \u0646\u0642\u062a\u0631\u062d ' + want + '\u060c \u0644\u0643\u0646 \u0647\u0630\u0647 \u0627\u0644\u0642\u0637\u0639\u0629 \u062a\u0628\u062f\u0623 \u0645\u0646 ' + got + '.';
      } else {
        en += ' We\'d say ' + want + '; this piece stops at ' + got + '.';
        ar += ' \u0646\u0642\u062a\u0631\u062d ' + want + '\u060c \u0644\u0643\u0646 \u0623\u0643\u0628\u0631 \u0645\u0642\u0627\u0633 \u0644\u0647\u0630\u0647 \u0627\u0644\u0642\u0637\u0639\u0629 ' + got + '.';
      }
    }
    var left = c.stock[got];
    if(!left){
      en += ' ' + got + ' is sold out in this colour.';
      ar += ' \u0645\u0642\u0627\u0633 ' + got + ' \u0646\u0641\u062f \u0641\u064a \u0647\u0630\u0627 \u0627\u0644\u0644\u0648\u0646.';
    }
    fitSize.textContent = got;
    setBi(fitWhy, en, ar);
    setBi(fitPickL, 'Choose ' + got, '\u0627\u062e\u062a\u0631 ' + got);
    fitPick.disabled = !left;
    fitPick.dataset.size = got;
  }
  function fitPress(e){
    var b = e.target.closest('button[data-fit]');
    if(!b) return;
    fitMove = +b.dataset.fit;
    [].forEach.call(fitFits.querySelectorAll('button'), function(x){
      x.setAttribute('aria-pressed', String(x === b));
    });
    fitRun();
  }
  function fitChoose(){
    if(fitPick.disabled || !fitPick.dataset.size) return;
    pickSize(fitPick.dataset.size);
    fitBox.open = false;
    /* Focus lands where the next press is: the Add button, now that it can
       act, else the chip that was just pressed. */
    var next = (addBtn && !addBtn.disabled) ? addBtn : sizes.querySelector('[aria-pressed="true"]');
    if(next) next.focus();
  }

  /* ---- support zones --------------------------------------------
     Re-read on every mount rather than captured once: the band is absent on any
     product whose spec sheet carries no support-zones photograph, so these two
     lists are empty on most pages and repopulate the moment a section swap
     brings the band back. */
  var pins = [], items = [];
  function zone(i){
    pins.forEach(function(p){
      var on = p.dataset.z === String(i);
      p.classList.toggle('on', on);
      p.setAttribute('aria-pressed', String(on));   /* it is a selected state, so say so */
    });
    items.forEach(function(t){ t.classList.toggle('on', t.dataset.z === String(i)); });
  }
  function bindZones(){
    pins  = [].slice.call(d.querySelectorAll('.zone-pin'));
    items = [].slice.call(d.querySelectorAll('.zone-item'));
    pins.forEach(function(p){
      if(p.__ogZone) return;
      p.__ogZone = 1;
      p.addEventListener('click', function(){ zone(p.dataset.z); });
      p.addEventListener('mouseenter', function(){ zone(p.dataset.z); });
      p.addEventListener('focus', function(){ zone(p.dataset.z); });
    });
    items.forEach(function(t){
      if(t.__ogZone) return;
      t.__ogZone = 1;
      t.addEventListener('click', function(){ zone(t.dataset.z); });
      /* the panels carry cursor:pointer and light up on selection, so they look
         like the control they are -- but they were unreachable without a mouse */
      t.addEventListener('focus', function(){ zone(t.dataset.z); });
      t.addEventListener('keydown', function(e){
        if(e.key==='Enter' || e.key===' '){ e.preventDefault(); zone(t.dataset.z); }
      });
    });
  }

  /* ---- sticky buy bar -------------------------------------------
     UP WHENEVER THE REAL BUTTON IS NOT ON SCREEN, in either direction. At load it
     is below the fold, so the bar is up from the first frame -- the first screen
     is where nothing has been decided and it used to have no way to buy at all.
     Reach the buy block and the bar drops away, because the real control is right
     there. Scroll past and it returns.

     This deliberately replaces "has it scrolled ABOVE you", whose reasoning was
     that a bar arriving before you have seen the product just covers the
     photograph. True, and outweighed: the photograph is still fully visible
     behind 63px of a 844px viewport, and the alternative was a product page whose
     opening screen could not be bought from.

     Rects on scroll, still not an IntersectionObserver: a jump that takes the
     button from below the fold to above it in one go crosses no threshold and
     fires no callback, so the observer would never run. A rect is always true.
     The 12px band at each edge is hysteresis -- inside it the state is kept, so
     the bar cannot chatter when the button sits exactly on an edge. */
  var bTick=null, bbShown=false, bbWired=false;
  function bbCheck(){
      if(!bar || !addBtn) return;
      var r = addBtn.getBoundingClientRect(), h = window.innerHeight;
      if(r.bottom < -12 || r.top > h + 12) bbShown = true;         /* fully away */
      else if(r.bottom > 12 && r.top < h - 12) bbShown = false;    /* clearly here */
      bar.classList.toggle('on', bbShown);
      bar.setAttribute('aria-hidden', String(!bbShown));
      /* aria-hidden alone left #bbAdd focusable while translated off-screen */
      bar.inert = !bbShown;
      bbDock();
  }
  /* The dock swap, and NOT a plain mirror of bbShown. `bb-up` takes the bag
     pill away outright, at every width now that the bar is no longer mobile
     only, on the premise that the bar stands in for it --
     and on a garment nobody can buy the bar is disabled, so that premise fails
     and the shopper is left with a grey strip that swallows taps and no route to
     the bag at all. One product on the store is in that state today, held
     there on purpose by tools/shopify/stock.json. So the class
     means "the bar is up AND it can act", and exactly one working bag
     affordance is on screen at any moment.

     Called from BOTH writers because the two inputs move independently: the rect
     changes on scroll and resize, the disabled state changes on a swatch press
     with no scroll at all -- and mountProduct runs bind() before pickColour(),
     so whichever ran last has to leave the class right. */
  function bbDock(){
      /* TWO classes, because the dock has two things to answer. bb-on is simply
         "the bar is on screen", and lifts the pill clear of a slab that would
         otherwise paint over it. bb-up is the stricter "and its button can act",
         and is the only one that takes the pill away. */
      d.body.classList.toggle('bb-on', !!bbShown);
      d.body.classList.toggle('bb-up', !!bbShown && !!bbAdd && !bbAdd.disabled);
  }
  function bindBuybar(){
    if(!bar) return;
    /* The window listeners are bound ONCE for the life of the page, because
       neither the window nor onResize has a removal path -- a job bound per
       mount stacks a stale writer per edit, which is the rzJobs trap the chrome
       sections already carry. bbCheck reads the CURRENT handles every time, so
       one listener survives any number of swaps. */
    if(!bbWired){
      bbWired = true;
      window.addEventListener('scroll', function(){
        if(bTick) return;
        bTick = requestAnimationFrame(function(){ bTick=null; bbCheck(); });
      }, {passive:true});
      (window.OG && OG.onResize ? OG.onResize : window.addEventListener.bind(window,'resize'))(bbCheck);
      /* The first evaluation runs at mount, BEFORE the gallery's images have sized
         the page, so the rect it reads is not the one the shopper gets -- and with
         no scroll and no resize, nothing ever re-ran it. The bar stayed down on
         exactly the screen it exists for. Watch the document's own box: every late
         image that grows the page re-answers the question. Bound once, like the
         listeners above, because neither this nor onResize has a removal path. */
      if(window.ResizeObserver) new ResizeObserver(function(){ bbCheck(); }).observe(d.body);
      window.addEventListener('load', bbCheck);
    }
    bbCheck();
  }

  /* ---- add -------------------------------------------------------
     THE PROMISE IS READ NOW. It used to be discarded: a 422 from the shop was
     an optimistic row, an open drawer, a silent rollback and nothing on this
     page saying so. bagAdd never rejects -- it swallows into fail() -- so the
     tell is the note it leaves behind, and the report goes into #pdStock, which
     already exists, is bilingual and hides itself when empty. */
  function add(){
    if(addBtn.disabled) return;
    var c = PRODUCT.colours[col];
    var left = sizeless(c) ? (c.stock1 | 0) : c.stock[size];
    if(!left) return;                    /* notify me is not a purchase */
    if(!window.OG || !OG.add) return;
    failed = null;
    var sz = sizeless(c) ? '' : size;
    /* The row key bagAdd builds for this triple, so the answer can be matched
       to the press that caused it. */
    var key = PRODUCT.sku + '|' + c.id + '|' + sz;
    var pending = OG.add(PRODUCT.sku, c.id, sz, 1);
    /* undefined is bagAdd's own refusal -- no stock, or no variant id published
       for this triple. It did nothing, so say so rather than leaving the page
       looking as though it worked. */
    if(!pending){ report(true, key); return; }
    pending.then(function(){ report(false, key); });
  }
  /* The note carries three unrelated kinds of trouble and only ONE of them is
     this press: `dropped` is what revalidate() found sold out at boot, which on
     this store is the ordinary case, and reporting it here put "that did not
     reach the bag" over an add Shopify accepted. bagAdd's synchronous refusal
     leaves no note at all, so that branch says so itself rather than asking.

     Even inside `failed` the list is every add on the site, so the entry is
     matched on the ROW KEY fail() recorded with it. Two presses can be in
     flight at once -- press M, press XL before M answers -- and the length
     alone made XL's success report M's refusal. */
  function report(refused, key){
    var note = (window.OG && OG.bagNote) ? OG.bagNote() : null;
    var list = (note && note.failed) || [];
    var mine = !!refused;
    for(var i=0; !mine && i<list.length; i++){ mine = list[i].key === key; }
    failed = mine ? say(BLOB.addFailed, 'That did not reach the bag. Try again.',
                        'لم يصل إلى السلة. حاول مرة أخرى.') : null;
    syncAdd();
  }

  /* ---- language -------------------------------------------------- */
  window.addEventListener('og:dir', function(){
    setTimeout(function(){ buildGallery(); syncAdd(); syncTable(); fitRun(); }, 20);
  });
  (OG.onResize || window.addEventListener.bind(window,'resize'))(function(){ navPos(); syncCount(); });

  /* ---- binding ---------------------------------------------------
     Every listener lives here and every one is flagged on the ELEMENT it is
     bound to, so a re-mount that replaced only one of the two sections rebinds
     that one and leaves the other's listener alone rather than stacking a
     second. */
  function bindOne(el, ev, fn){
    if(!el || el.__ogPdp === ev) return;
    el.__ogPdp = ev;
    el.addEventListener(ev, fn);
  }
  function bind(){
    bindOne(frames, 'scroll', syncCount);
    bindOne(pdPrev, 'click', function(){ pdGo(-1); });
    bindOne(pdNext, 'click', function(){ pdGo(1); });
    bindOne(addBtn, 'click', add);
    bindOne(bbAdd, 'click', function(){
      /* Nothing chosen yet. The bar is on the first screen now, so this is the
         most tapped control on the page and the chips have never been seen --
         sending the viewport a screen and a half to a control nobody has looked
         at, with nothing marked on arrival, reads as a teleport rather than an
         answer. So: if the chips are already in view do not scroll at all, and
         either way put focus on the first buyable one, so the ring marks the
         target and a screen reader says what just happened. */
      if(!size && !sizeless(PRODUCT.colours[col])){
        if(sizes){
          var box = sizes.getBoundingClientRect();
          if(box.top < 0 || box.bottom > window.innerHeight){
            sizes.scrollIntoView({behavior:'smooth', block:'center'});
          }
          var first = sizes.querySelector('button:not(:disabled)');
          if(first) first.focus({preventScroll:true});
        }
        return;
      }
      add();
    });
    bindOne(fitH, 'input', fitRun);
    bindOne(fitW, 'input', fitRun);
    bindOne(fitFits, 'click', fitPress);
    bindOne(fitPick, 'click', fitChoose);
    bindZones();
    bindBuybar();
  }

  /* WHICH COLOUR A URL IS. ?c= wins when it names one this page offers, because
     it is the only thing on the URL a shopper can have chosen deliberately -- a
     shared link, a campaign link, or the page's own address after a swatch. The
     PATH decides when there is no ?c=: on an ordinary arrival Shopify served
     ONE product and that product's own colourway is the one to open on, and
     after a swap the path is the sibling's, which is what makes this readable
     again on the way back through the history.

     The pattern used to be [a-z]+, which stops at the first hyphen or digit.
     ice_grey, dark_red and dark_grey are three of the ten colourway keys set on
     this store, and every one of them matched as "ice" or "dark", equalled no
     id, and silently fell back to the first swatch. */
  function colourFromURL(){
    var m = location.search.match(/[?&]c=([A-Za-z0-9_-]+)/);
    var want = m && decodeURIComponent(m[1]);
    var pick = -1;
    if(want) PRODUCT.colours.forEach(function(c,i){ if(c.id === want) pick = i; });
    if(pick > -1) return pick;
    var seg = location.pathname.match(/\/products\/([^\/?#]+)/);
    if(seg) pick = indexOfHandle(decodeURIComponent(seg[1]));
    if(pick < 0) pick = indexOfHandle(BLOB.handle);
    return pick;
  }

  /* ---- boot and re-mount -----------------------------------------
     mountSection() lives in the bundle's first IIFE and cannot see this one, so
     this listens for its own shopify:section:load exactly as the collection and
     the cart do. Without it the first edit a merchant makes in the theme editor
     leaves every handle pointing at a detached node, silently -- and TWO
     sections feed this controller, main-product and product-buybar, so the
     guard is on either of their anchors rather than on one of them. */
  function mountProduct(){
    if(!d.getElementById('pdFrames')) return;
    grab();
    if(!frames || !colours || !sizes || !addBtn) return;
    readBlob();
    PRODUCT = buildProduct();
    failed = null;
    size = null;
    col = 0;
    var pick = colourFromURL();
    if(pick >= 0) col = pick;
    fitRead();
    bind();
    pickColour(col);
    /* A ?variant= link asked for one size, and the blob publishes whichever one
       Shopify resolved. Applied AFTER pickColour, which clears the size on
       purpose, and only when that size is actually buyable -- a stale link to a
       variant that has since sold out should land on the page in its ordinary
       state, not on a chosen size with a dead button. */
    if(BLOB.size && SIZES.indexOf(BLOB.size) > -1){
      var c0 = PRODUCT.colours[col];
      if(!sizeless(c0) && c0.stock[BLOB.size] > 0){
        size = BLOB.size;
        buildSizes(); syncAdd(); syncTable();
      }
    }
  }
  mountProduct();
  /* BACK ACROSS A COLOUR SWAP. pickColour() pushes the sibling's own URL, and
     that entry is same-document -- so Back changes the address bar and
     navigates nothing. Without this the page kept showing the colour the
     shopper had just left, under a URL naming the one they went back to, and a
     link copied there shared a page nobody was looking at.

     It re-picks through pickColour itself, so the gallery, the price, the
     stock line, the pressed swatch and the size chips all follow, and it passes
     no second argument, so it pushes nothing back onto the history. Guarded on
     the mount rather than the template: layout/theme.liquid loads one bundle
     for every page in the shop and this listener is on all of them. */
  window.addEventListener('popstate', function(){
    if(!d.getElementById('pdFrames') || !frames) return;
    var back = colourFromURL();
    if(back < 0 || back === col) return;
    pickColour(back);
  });
  d.addEventListener('shopify:section:load', function(e){
    var box = e.target;
    if(!box || !box.querySelector) return;
    if(!box.querySelector('#pdFrames') && !box.querySelector('#buybar')) return;
    try{ mountProduct(); }
    catch(err){ if(CFG.designMode && window.console && console.error) console.error('[OG] product mount failed', err); }
  });
})();

/* ============================================================
   CONTACT FORM
   Its own IIFE at the end of the bundle, like the collection, cart and product
   controllers, because mountSection() lives in the first one and cannot see
   this. It registers its own shopify:section:load and RE-POINTS handles rather
   than re-capturing into new variables -- a section swap replaces the subtree
   and anything captured at parse time dies with it.

   The reference runs its own copy of this. The validation, the messages and the
   busy handling are identical; only the submit differs, because that side has
   no endpoint to post to.
   ============================================================ */
(function(){
  var d = document;
  if(!d.getElementById('ctForm') || !window.OG) return;

  var f, msg, send, sendEn, sendAr;
  var busy = false;
  var EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var setBi = OG.setBi;

  function grab(){
    f    = d.getElementById('ctForm');
    msg  = d.getElementById('ctMsg');
    send = d.getElementById('ctSend');
    if(send){ sendEn = send.getAttribute('data-en'); sendAr = send.getAttribute('data-ar'); }
  }
  function say(en, ar, ok){
    if(!msg) return;
    msg.hidden = false;
    msg.classList.toggle('ok', !!ok);
    setBi(msg, en, ar);
  }
  function clearBad(){
    [].forEach.call(f.querySelectorAll('.cf-shell.bad'), function(s){ s.classList.remove('bad'); });
    [].forEach.call(f.querySelectorAll('[aria-invalid]'), function(i){ i.removeAttribute('aria-invalid'); });
  }
  /* ONE error at a time, the first offending field, focus moved to it. With
     four fields a summary list is longer than the form. Two channels for one
     message -- the ring on the field and the sentence in #ctMsg -- and never
     two copies of the text. */
  function bad(input, en, ar){
    clearBad();
    var shell = input.closest('.cf-shell');
    if(shell) shell.classList.add('bad');
    input.setAttribute('aria-invalid', 'true');
    say(en, ar, false);
    input.focus();
    return false;
  }
  function valid(){
    var name = d.getElementById('ctName'), mail = d.getElementById('ctEmail'),
        body = d.getElementById('ctBody');
    if(!name || !mail || !body) return false;
    if(!name.value.trim())
      return bad(name, 'Add your name so we know who we are answering.',
                       'اكتب اسمك عشان نعرف بنرد على مين.');
    if(!EMAIL.test(mail.value.trim()))
      return bad(mail, 'That does not look like an email address.',
                       'ده مش شكل بريد إلكتروني.');
    if(!body.value.trim())
      return bad(body, 'Tell us what you need and we will answer it.',
                       'قول لنا محتاج إيه وهنرد عليك.');
    clearBad();
    return true;
  }
  function done(){
    busy = false;
    if(!send) return;
    send.disabled = false;
    setBi(send, sendEn, sendAr);
  }

  function onSubmit(e){
    e.preventDefault();
    if(busy || !valid()) return;
    /* URLSearchParams and not FormData: Shopify's contact endpoint wants
       urlencoded. Without it the whole post is silently ignored. */
    if(!window.URLSearchParams){ f.submit(); return; }
    busy = true;
    if(send){ send.disabled = true; setBi(send, 'Sending', 'جاري الإرسال'); }

    fetch(f.action, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type':'application/x-www-form-urlencoded', 'Accept':'text/html'},
      body: new URLSearchParams(new FormData(f)).toString()
    }).then(function(r){
      /* THE OUTCOME IS THE REDIRECT AND THE SENTENCE IS THE PAGE, and those
         are two different reads. Shopify answers a sent message by redirecting
         to this page with form.posted_successfully? true, and a refused one by
         re-rendering it with form.errors set -- so whichever it was,
         main-contact.liquid has already written the merchant's own wording for
         it into #ctMsg, in both languages, off the section's settings. Reading
         it is what keeps this path from carrying a second copy of every
         sentence that then drifts out of step: edit "Reply when it sends" in
         the editor and this is where the edit shows up.

         WHICH sentence is settled by the page; WHETHER it sent is settled by
         the url. A refusal has been answered 200 and could be answered 422,
         so the status cannot decide it -- but a response that carries no
         #ctMsg at all is the server or the throttler rather than anything the
         shopper typed, and the literals below are the last resort for exactly
         that: a page this bundle did not render. */
      return r.text().then(function(html){
        var sent = !!(r.url && r.url.indexOf('contact_posted=true') > -1);
        var back = new DOMParser().parseFromString(html, 'text/html').getElementById('ctMsg');
        var en = back && back.getAttribute('data-en');
        if(sent) f.reset();
        if(en){
          say(en, back.getAttribute('data-ar') || en, sent);
        } else if(sent){
          say('Sent. We reply by email within one working day.',
              'تم الإرسال. بنرد بالبريد خلال يوم عمل.', true);
        } else if(!r.ok){
          say('That did not go through. Try again in a moment, or message us on WhatsApp.',
              'الرسالة ما وصلتش. جرب تاني بعد شوية، أو راسلنا على واتساب.', false);
        } else {
          say('That was refused. Check the form and send again — what you typed is still here.',
              'الإرسال اترفض. راجع البيانات وابعت تاني — اللي كتبته لسه موجود.', false);
        }
      });
    }).catch(function(){
      /* A rejected promise IS the connection, so this arm keeps the sentence
         the refusal above may not use. */
      say('That did not go through. Try again in a moment, or message us on WhatsApp.',
          'الرسالة ما وصلتش. جرب تاني بعد شوية، أو راسلنا على واتساب.', false);
    }).then(done, done);   /* restored on BOTH arms, or a failed send leaves a dead button */
  }

  function mountContact(){
    grab();
    if(!f) return;
    f.addEventListener('submit', onSubmit);
  }
  mountContact();
  d.addEventListener('shopify:section:load', function(e){
    if(e.target && e.target.querySelector && e.target.querySelector('#ctForm')) mountContact();
  });
})();
